import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Skull, ChevronDown, DollarSign, Clock, AlertTriangle, Eye, Flame, Zap, Lock, Shield } from 'lucide-react';
import { CINEMATIC_KEYFRAMES, ScanlineOverlay, NoiseOverlay, GasParticles, ToxicDrips, SewerFrame, GlitchTitle, StatusBar } from './CinematicStyles';
import { useDemo } from '../../context/DemoContext';
import { toast } from 'sonner';
import OnChainReceipt, { fireOnChainToast } from '../OnChainReceipt';

// Floor 10 (The Abyss) custom assets
import abyssBg from '../../assets/games/descent/abyss_bg.png';
import gremlinSoulImg from '../../assets/games/descent/gremlin_soul.png';
import victoryBgImg from '../../assets/games/descent/victory_portal.png';
import gremlinSkullImg from '../../assets/games/gremlin_skull.png';
// Floor intro backgrounds
import floor1Bg from '../../assets/games/descent/floor1/drain_bg.png';
import floor3Bg from '../../assets/games/descent/floors/floor3_laser.png';
import floor1Grate from '../../assets/games/descent/floor1/grate.png';
import floor2Bg from '../../assets/games/descent/floor2/pipes_bg.png';
import floor4Bg from '../../assets/games/descent/floors/floor4_rats.png';
import floor4Rat from '../../assets/games/descent/floor4/rat.webp';
import floor4Crown from '../../assets/games/descent/floor4/crown.webp';
import floor5Bg from '../../assets/games/descent/floors/floor5_flood.png';
import floor5LeakBg from '../../assets/games/descent/floor5/leak_bg.png';
import floor5Lantern from '../../assets/games/descent/floor5/lantern.png';
import floor6Bg from '../../assets/games/descent/floors/floor6_blackout.png';
import floor6Flame from '../../assets/games/descent/floor6/flame.webp';
import floor7Bg from '../../assets/games/descent/floors/floor7_furnace.png';
import floor7Lamp from '../../assets/games/descent/floor7/lamp.webp';
import floor7CyanDust from '../../assets/games/descent/floor7/cyan_dust.webp';
import floor8Bg from '../../assets/games/descent/floors/floor8_reactor.png';
import floor9Bg from '../../assets/games/descent/floors/floor9_warden.png';
import floor9GhostFlame from '../../assets/games/descent/floor9/ghost_flame.webp';
import floor9IronMask from '../../assets/games/descent/floor9/iron_mask.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_WALLET = 'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4';

const ENTRY_FEE = 1000000;
const MULTIPLIERS = [1.2, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0, 7.0, 10.0, 500.0];

const FLOORS = [
  { name: 'THE DRAIN', desc: 'Seal the toxic leaks before they flood', color: '#39ff14', icon: '1' },
  { name: 'PIPE MAZE', desc: 'Memorize and repeat the valve sequence', color: '#00ccff', icon: '2' },
  { name: 'LASER GRID', desc: 'Dodge the scanning lasers', color: '#ff0040', icon: '3' },
  { name: "RAT KING'S DEN", desc: 'Click rats. Avoid the red eyes.', color: '#f5a623', icon: '4' },
  { name: 'THE LEAK', desc: 'Patch the cracks. Mind your lantern. The gas does not forgive.', color: '#ff8030', icon: '5' },
  { name: 'BLACKOUT TUNNELS', desc: 'Find symbols in the dark', color: '#666', icon: '6' },
  { name: 'THE FURNACE', desc: 'Tap the rhythm of the pipes', color: '#ff4400', icon: '7' },
  { name: 'TOXIC CORE', desc: 'Reversed controls. Stay sharp.', color: '#b915cc', icon: '8' },
  { name: 'THE WARDEN', desc: 'Move only when it looks away', color: '#cc0000', icon: '9' },
  { name: 'THE ABYSS', desc: 'Escape the maze before time runs out', color: '#000', icon: 'X' },
];

// Floor intro screen config — background images, accent colors, lore quotes
const FLOOR_INTROS = {
  1: { bg: floor1Bg, color: '#39ff14', lore: '"The toxins drip endlessly. Seal them, or drown in the filth."' },
  2: { bg: floor2Bg, color: '#00ccff', lore: '"The pipes scream. Only those who remember the sequence — and the lie — survive."' },
  3: { bg: floor3Bg, color: '#ff0040', lore: '"One wrong step and the grid cuts through flesh and bone."' },
  4: { bg: floor4Bg, color: '#f5a623', lore: '"They worship something ancient down here. Do not meet its gaze."' },
  5: { bg: floor5Bg, color: '#39ff14', lore: '"The pipes weep gas. Your flame is your only friend, and your only enemy."' },
  6: { bg: floor6Bg, color: '#333333', lore: '"In absolute darkness, trust nothing. Not even the light."' },
  7: { bg: floor7Bg, color: '#ff4400', lore: '"The furnace remembers rhythm. Miss a beat and it consumes you."' },
  8: { bg: floor8Bg, color: '#b915cc', lore: '"Reality inverts at the core. Your instincts will betray you."' },
  9: { bg: floor9Bg, color: '#cc0000', lore: '"The Warden never sleeps. It only blinks."' },
};

// Per-floor tutorials shown BEFORE the floor intro. Lore prologue + rules + controls.
const FLOOR_TUTORIALS = {
  1: {
    floorName: 'The Drain',
    latin: 'Lex Putrida',
    prologue: 'The first threshold. An iron grate boils with toxic sludge. The Chancellor watches to see if your hands move faster than the filth that drowns the unworthy.',
    rules: [
      'Six ports ring the grate. Leaks burst at random — a fill-meter rings each one.',
      'Tap a leak before its meter fills, or the floor overflows.',
      'Three overflows and the floor consumes you. Twelve seals to pass.',
    ],
    controls: 'Tap / Click each leak before it overflows',
  },
  2: {
    floorName: 'Pipe Maze',
    latin: 'Memoria Ferrea',
    prologue: 'Iron bones groan above you. The valves cry in sequence — but the Chancellor\'s steam carries false notes. Your iron memory must distinguish what is from what was whispered.',
    rules: [
      'Six brass valves ignite in a sequence. ONE valve in each sequence is a LIE — marked by a crimson tint.',
      'Repeat the sequence by tapping each valve in order, SKIPPING the lie.',
      'Wrong tap or wrong order = a steam strike. Three strikes and the boiler ruptures.',
    ],
    controls: 'Watch · spot the lie · repeat the truth in order',
  },
  3: {
    floorName: 'Iron Lattice',
    latin: 'Cultrum Lucis',
    prologue: 'An ornate cathedral lattice — eight columns, five rows of judging cells. The grid cuts, but only along its lines. A lit row or column will be lanced. Step out, or be cut.',
    rules: [
      'Move with WASD or arrows; click any cell to leap to it.',
      'When a row OR column ignites in orange, a laser will lance it. Be elsewhere.',
      'Survive 75 seconds. Late game brings two simultaneous strikes.',
    ],
    controls: 'WASD / Arrows · click to leap · escape the lit line',
  },
  4: {
    floorName: "Rat King's Den",
    latin: 'Rex Murium',
    prologue: 'The rats gather here, crowned and many-eyed. Most are mere vermin. One is the king. Do not meet its gaze.',
    rules: [
      'Rats scurry across the floor — tap them to score.',
      'The Rat King has glowing red eyes. DO NOT tap it.',
      'One touch of the king ends your descent.',
    ],
    controls: 'Tap rats · avoid red eyes',
  },
  5: {
    floorName: 'The Leak',
    latin: 'Flamma Fragilis',
    prologue: 'A slow chamber. Gas seeps from a thousand cracks. Your lantern keeps the void at bay — but a flame near gas is a bargain with death.',
    rules: [
      'Cracks appear on the walls — tap each one to patch it before the gas fills the room.',
      'Keep the lantern alive. Tap it when it flickers to keep it lit.',
      'Too much gas + an open flame = you detonate.',
    ],
    controls: 'Patch cracks · tend the lantern',
  },
  6: {
    floorName: 'Blackout Tunnels',
    latin: 'Tenebrae Verae',
    prologue: 'The lights are dead. Whispered symbols hide in the dark — one is true, the others are lies told by the tunnel itself.',
    rules: [
      'Three symbols briefly flicker into view in the darkness.',
      'Remember which symbol is the TRUE one — it will reappear among decoys.',
      'Choose wrong and the dark swallows you.',
    ],
    controls: 'Watch carefully · tap the TRUE symbol',
  },
  7: {
    floorName: 'The Furnace',
    latin: 'Ritus Ignis',
    prologue: 'The furnace sings in rhythm. Old and slow, but unforgiving. Strike with its beat, or burn against it.',
    rules: [
      'A metronome pulses with the furnace rhythm.',
      'Tap ON each beat — not before, not after.',
      'Three missed beats and the furnace takes its tribute.',
    ],
    controls: 'Tap on the beat',
  },
  8: {
    floorName: 'Toxic Core',
    latin: 'Inversio Animae',
    prologue: 'A ring of sigils orbits a mirror altar. One glows as the True Mark — strike it before the rotation claims it. But beware: the mirror turns. Reality inverts. Your instincts become the Chancellor\'s jest.',
    rules: [
      'Strike the GLOWING sigil before it rotates away.',
      'Every few seconds the ring REVERSES and the sigils mirror left-right.',
      'Wrong sigil costs a life. Three lives. Ten Marks to pass.',
    ],
    controls: 'Click or tap the glowing sigil',
  },
  9: {
    floorName: 'The Warden',
    latin: 'Custos Sempiternus',
    prologue: 'A monumental iron mask hangs above the cathedral. Its crimson iris is the only law. Hold to advance your soul-wisp upward toward the altar; release the instant the eye opens. Move under the gaze, and the Warden ends you.',
    rules: [
      'HOLD mouse or SPACE to advance the wisp upward.',
      'When the iris is RED — release. Any movement = caught.',
      'When the iris is dark — move freely. Reach the altar at the top.',
    ],
    controls: 'Hold to ascend · release the instant the eye opens',
  },
  10: {
    floorName: 'The Abyss',
    latin: 'Vorago Ultima',
    prologue: 'No more floors below. Only the dark, only the clock. The Chancellor\'s gaze turns elsewhere — this last trial is between you and the Abyss itself. Concentric rings pulse inward; each broken by a single narrow gap. Find the gap, or be consumed.',
    rules: [
      'Move your cursor to set the wisp\'s orbit angle around the center.',
      'Align with the GAP in each ring before it crosses your orbit. Outside the gap = caught.',
      'Rings spawn faster as the floor draws on. Inversion may flip your control. One life. 75 seconds.',
    ],
    controls: 'Cursor / touch / arrow keys to rotate · find the gap',
  },
};

// Floor Tutorial — lore prologue + rules + controls, shown BEFORE the cinematic intro.
// Colors match the floor. Players can toggle off future tutorials (persists in localStorage).
function FloorTutorial({ floorNum, onBegin, onToggleSkip }) {
  const tut = FLOOR_TUTORIALS[floorNum];
  const intro = FLOOR_INTROS[floorNum];
  // Fallback colors for floor 10 (not in FLOOR_INTROS)
  const color = (intro && intro.color) || (FLOORS[floorNum - 1] && FLOORS[floorNum - 1].color) || '#dcc878';
  const [dontShow, setDontShow] = useState(false);
  const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  const roman = romanNumerals[floorNum - 1] || String(floorNum);
  if (!tut) { onBegin(); return null; }

  const glowColor = (a) => {
    const m = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return `rgba(255,255,255,${a})`;
    return `rgba(${parseInt(m[1],16)},${parseInt(m[2],16)},${parseInt(m[3],16)},${a})`;
  };

  const handleBegin = () => {
    if (dontShow) onToggleSkip(true);
    onBegin();
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center p-4 z-50"
      style={{
        background: 'radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.96) 100%)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}>
      {/* Subtle aura */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 50% 50%, ${glowColor(0.10)} 0%, transparent 55%)` }} />

      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg"
        style={{
          background: 'linear-gradient(180deg, rgba(16,10,18,0.95) 0%, rgba(6,3,10,0.98) 100%)',
          border: `1px solid ${glowColor(0.55)}`,
          borderRadius: 6,
          boxShadow: `0 0 48px ${glowColor(0.35)}, 0 0 100px ${glowColor(0.15)}, inset 0 0 40px rgba(0,0,0,0.6)`,
          padding: '32px 28px 22px',
        }}
        data-testid={`floor-tutorial-${floorNum}`}
      >
        {/* Corner gilded brackets */}
        {['top-2 left-2 border-t border-l','top-2 right-2 border-t border-r','bottom-2 left-2 border-b border-l','bottom-2 right-2 border-b border-r'].map((cls, i) => (
          <span key={i} className={`absolute w-4 h-4 ${cls}`} style={{ borderColor: glowColor(0.65) }} />
        ))}

        {/* Floor ID strip */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full mb-3"
            style={{ background: glowColor(0.08), border: `1px solid ${glowColor(0.35)}` }}>
            <span className="text-[10px] font-mono uppercase tracking-[0.32em]" style={{ color: glowColor(0.9) }}>
              Floor {roman} · of · X
            </span>
          </div>
          <h2 className="font-black mb-1" style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 'clamp(1.6rem, 3.5vw, 2.3rem)',
            letterSpacing: '0.04em',
            color: '#f0e6cc',
            textShadow: `0 0 18px ${glowColor(0.55)}`,
          }}>
            {tut.floorName}
          </h2>
          <p className="italic" style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: '0.88rem',
            letterSpacing: '0.18em',
            color: glowColor(0.85),
          }}>
            {tut.latin}
          </p>
        </div>

        {/* Divider */}
        <div className="w-full mb-4" style={{ height: 1, background: `linear-gradient(90deg, transparent, ${glowColor(0.45)}, transparent)` }} />

        {/* Prologue */}
        <p className="mb-5 italic" style={{
          fontFamily: '"EB Garamond", Georgia, serif',
          fontSize: '1rem',
          lineHeight: 1.55,
          color: 'rgba(230,220,200,0.82)',
          textAlign: 'center',
        }}>
          “{tut.prologue}”
        </p>

        {/* Rules */}
        <div className="mb-4">
          <h3 className="font-mono uppercase mb-2" style={{
            fontSize: '0.66rem', letterSpacing: '0.28em',
            color: glowColor(0.95),
          }}>
            ◈ The Rules
          </h3>
          <ol className="space-y-2">
            {tut.rules.map((r, i) => (
              <li key={i} className="flex gap-3 items-start text-[0.88rem]" style={{ color: 'rgba(230,220,200,0.82)', lineHeight: 1.5 }}>
                <span className="inline-flex flex-shrink-0 items-center justify-center font-bold"
                  style={{
                    width: 22, height: 22, borderRadius: 2,
                    background: glowColor(0.14),
                    border: `1px solid ${glowColor(0.5)}`,
                    color: glowColor(1),
                    fontFamily: "'Cinzel', serif",
                    fontSize: '0.72rem',
                    marginTop: 1,
                  }}>
                  {['I','II','III','IV'][i] || String(i + 1)}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Controls pill */}
        <div className="mb-5 flex items-center justify-center gap-2 px-3 py-2 rounded-sm"
          style={{ background: glowColor(0.06), border: `1px solid ${glowColor(0.25)}` }}>
          <span className="text-[9px] font-mono uppercase tracking-[0.28em]" style={{ color: glowColor(0.7) }}>Controls</span>
          <span className="text-[11px] font-semibold" style={{ color: glowColor(1) }}>{tut.controls}</span>
        </div>

        {/* Don't show again */}
        <label className="flex items-center gap-2 cursor-pointer select-none mb-4 justify-center"
          data-testid="tutorial-dont-show-toggle">
          <span className="relative inline-flex items-center">
            <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)}
              className="sr-only" />
            <span className="inline-block w-4 h-4 border"
              style={{
                borderColor: glowColor(dontShow ? 1 : 0.5),
                background: dontShow ? glowColor(0.85) : 'transparent',
                transition: 'all 0.2s',
              }} />
            {dontShow && (
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ color: '#000', fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</span>
            )}
          </span>
          <span className="text-[11px] tracking-[0.16em] uppercase" style={{ color: 'rgba(230,220,200,0.65)' }}>
            Don't show tutorials again
          </span>
        </label>

        {/* Begin button */}
        <button
          onClick={handleBegin}
          data-testid="floor-tutorial-begin-button"
          className="w-full py-3 font-bold text-sm uppercase tracking-[0.32em] transition-all hover:brightness-125"
          style={{
            fontFamily: "'Cinzel', serif",
            color: '#fff',
            background: `linear-gradient(135deg, ${glowColor(0.32)} 0%, ${glowColor(0.14)} 100%)`,
            border: `1px solid ${glowColor(0.85)}`,
            borderRadius: 4,
            boxShadow: `0 0 22px ${glowColor(0.4)}, inset 0 0 18px rgba(0,0,0,0.4)`,
            textShadow: `0 0 10px ${glowColor(0.85)}, 0 1px 2px rgba(0,0,0,0.9)`,
          }}
        >
          Begin the Descent
        </button>
      </motion.div>
    </div>
  );
}

// Reusable Floor Intro Transition Screen
function FloorIntro({ floorNum, onReady }) {
  const floor = FLOORS[floorNum - 1];
  const intro = FLOOR_INTROS[floorNum];
  if (!floor || !intro) { onReady(); return null; }

  const { bg, color, lore } = intro;
  const glowColor = (a) => {
    const m = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (!m) return `rgba(255,255,255,${a})`;
    return `rgba(${parseInt(m[1],16)},${parseInt(m[2],16)},${parseInt(m[3],16)},${a})`;
  };

  // Roman numeral for the floor number — feels ancient
  const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  const roman = romanNumerals[floorNum - 1] || String(floorNum);

  const isFloor5 = floorNum === 5;
  const f5GradientStyle = isFloor5
    ? {
        backgroundImage: 'linear-gradient(95deg, #66ccff 0%, #0099ff 22%, #ffaa30 55%, #ff7720 78%, #66ccff 100%)',
        backgroundSize: '200% 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: 'transparent',
        animation: 'fi-shimmer 4s linear infinite, fi-glow-5 4s ease-in-out infinite',
        filter: 'drop-shadow(0 0 28px rgba(0,150,255,0.45)) drop-shadow(0 0 60px rgba(255,140,40,0.35))',
      }
    : null;

  return (
    <div className="relative h-full w-full overflow-hidden" style={{ background: '#020004' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Cinzel:wght@400;700;900&display=swap');
        @keyframes fi-glow-${floorNum} {
          0%, 100% { text-shadow: 0 0 50px ${glowColor(0.5)}, 0 0 100px ${glowColor(0.25)}, 0 0 160px ${glowColor(0.1)}, 0 4px 30px rgba(0,0,0,0.9); }
          50% { text-shadow: 0 0 70px ${glowColor(0.7)}, 0 0 130px ${glowColor(0.4)}, 0 0 200px ${glowColor(0.18)}, 0 4px 30px rgba(0,0,0,0.9); }
        }
        ${floorNum === 5 ? `
          @keyframes fi-glow-5 {
            0%, 100% { filter: drop-shadow(0 0 28px rgba(0,150,255,0.45)) drop-shadow(0 0 60px rgba(255,140,40,0.35)); }
            50% { filter: drop-shadow(0 0 36px rgba(0,180,255,0.6)) drop-shadow(0 0 80px rgba(255,150,50,0.5)); }
          }
        ` : ''}
        @keyframes fi-glitch { 0%, 92%, 100% { transform: translate(0,0); clip-path: inset(0 0 0 0); } 93% { transform: translate(-3px, 1px); clip-path: inset(20% 0 60% 0); } 95% { transform: translate(2px, -1px); clip-path: inset(50% 0 20% 0); } 97% { transform: translate(-1px, 2px); clip-path: inset(10% 0 70% 0); } }
        @keyframes fi-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes fi-rotate-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes fi-rotate-rev { 0% { transform: rotate(360deg); } 100% { transform: rotate(0deg); } }
        @keyframes fi-pulse-ring { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.04); } }
        @keyframes fi-ember-flicker { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes fi-camera-push { 0% { transform: scale(1.18); } 100% { transform: scale(1.02); } }
        @keyframes fi-tablet-rise { 0% { transform: translateY(40px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes fi-roman-stamp { 0% { transform: scale(2.2); opacity: 0; filter: blur(20px); } 30% { opacity: 1; } 60% { transform: scale(0.95); filter: blur(0px); } 100% { transform: scale(1); opacity: 0.18; filter: blur(0px); } }
      `}</style>

      {/* Background — cinematic camera push-in */}
      <motion.div
        initial={{ scale: 1.18, opacity: 0 }}
        animate={{ scale: 1.02, opacity: [0, 0.6, 0.6, 0.32] }}
        transition={{ duration: 4.5, times: [0, 0.25, 0.6, 1], ease: 'easeOut' }}
        className="absolute inset-0 z-0"
      >
        {bg ? (
          <img src={bg} alt="" className="w-full h-full object-cover" />
        ) : (
          // Procedural fallback — pure CSS deep-tint background for floors w/o BG image.
          <div className="w-full h-full" style={{
            background: `radial-gradient(ellipse at 50% 45%, ${glowColor(0.22)} 0%, rgba(8,0,4,0.85) 45%, #020001 100%)`,
          }} />
        )}
      </motion.div>

      {/* Color-graded fog wash from top — gives the BG the floor's tint */}
      <motion.div className="absolute inset-0 z-1 pointer-events-none"
        initial={{ opacity: 0 }} animate={{ opacity: 0.32 }} transition={{ delay: 0.4, duration: 2 }}
        style={{ background: `linear-gradient(180deg, ${glowColor(0.18)} 0%, transparent 35%, transparent 65%, ${glowColor(0.28)} 100%)`, mixBlendMode: 'overlay' }} />

      {/* Vignette */}
      <div className="absolute inset-0 z-2 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 12%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.95) 100%)' }} />

      {/* Colored fog rising from below */}
      <motion.div className="absolute inset-x-0 bottom-0 z-3 pointer-events-none"
        initial={{ height: '0%', opacity: 0 }}
        animate={{ height: '55%', opacity: 0.5 }}
        transition={{ delay: 0.5, duration: 3.5, ease: 'easeOut' }}
        style={{ background: `linear-gradient(to top, ${glowColor(0.3)} 0%, ${glowColor(0.08)} 40%, transparent 100%)` }} />

      {/* God rays from off-screen top */}
      <motion.div className="absolute inset-0 z-4 pointer-events-none"
        initial={{ opacity: 0 }} animate={{ opacity: 0.35 }} transition={{ delay: 0.8, duration: 2 }}
        style={{ background: `radial-gradient(ellipse 50vmax 80vmax at 50% -10%, ${glowColor(0.18)} 0%, ${glowColor(0.06)} 30%, transparent 60%)`, mixBlendMode: 'screen' }} />

      {/* Rising embers — varied sizes, two layers */}
      {Array.from({ length: 18 }).map((_, i) => (
        <motion.div key={`em${i}`} className="absolute rounded-full z-8 pointer-events-none"
          initial={{ left: `${4 + Math.random() * 92}%`, top: `${75 + Math.random() * 25}%`, opacity: 0 }}
          animate={{ top: `${-8 - Math.random() * 12}%`, opacity: [0, 0.7, 0] }}
          transition={{ delay: 0.3 + i * 0.18, duration: 4 + Math.random() * 3, ease: 'easeOut' }}
          style={{
            width: `${1.5 + Math.random() * 3}px`, height: `${1.5 + Math.random() * 3}px`,
            background: i % 3 === 0 ? '#fff5d8' : color,
            boxShadow: `0 0 8px ${glowColor(0.6)}, 0 0 16px ${glowColor(0.3)}`,
            filter: 'blur(0.4px)',
          }} />
      ))}

      {/* GIANT GHOST ROMAN NUMERAL — stamps in behind the title like a watermark */}
      <div className="absolute inset-0 z-9 flex items-center justify-center pointer-events-none">
        <span style={{
          fontFamily: "'Cinzel', serif",
          fontWeight: 900,
          fontSize: 'clamp(280px, 50vmin, 620px)',
          color: glowColor(0.18),
          textShadow: `0 0 60px ${glowColor(0.35)}, 0 0 120px ${glowColor(0.15)}`,
          animation: 'fi-roman-stamp 2s ease-out forwards',
          letterSpacing: '-0.05em',
          lineHeight: 1,
        }}>{roman}</span>
      </div>

      {/* SIGIL RING — large rotating decorative ring behind title (lore-themed) */}
      <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ delay: 0.6, duration: 1.8, ease: 'easeOut' }}
          style={{ width: 'clamp(360px, 64vmin, 720px)', height: 'clamp(360px, 64vmin, 720px)' }}
        >
          <svg viewBox="0 0 600 600" className="w-full h-full" style={{ animation: 'fi-pulse-ring 5s ease-in-out infinite' }}>
            {/* outer ring — slow rotate */}
            <g style={{ transformOrigin: '300px 300px', animation: 'fi-rotate-slow 60s linear infinite' }}>
              <circle cx="300" cy="300" r="290" fill="none" stroke={color} strokeWidth="1" opacity="0.55" />
              <circle cx="300" cy="300" r="278" fill="none" stroke={color} strokeWidth="0.4" opacity="0.35" strokeDasharray="2 6" />
              {/* 12 cardinal sigil ticks */}
              {Array.from({ length: 12 }).map((_, i) => (
                <g key={i} transform={`rotate(${i * 30} 300 300)`}>
                  <line x1="300" y1="10" x2="300" y2="22" stroke={color} strokeWidth="1.4" opacity="0.7" />
                  <polygon points="300,28 304,32 300,36 296,32" fill={color} opacity={i % 3 === 0 ? '0.85' : '0.35'} />
                </g>
              ))}
            </g>
            {/* inner ring — counter-rotate */}
            <g style={{ transformOrigin: '300px 300px', animation: 'fi-rotate-rev 90s linear infinite' }}>
              <circle cx="300" cy="300" r="240" fill="none" stroke={color} strokeWidth="0.6" opacity="0.5" />
              <circle cx="300" cy="300" r="225" fill="none" stroke={color} strokeWidth="0.3" opacity="0.3" strokeDasharray="1 8" />
              {/* 24 minor ticks */}
              {Array.from({ length: 24 }).map((_, i) => (
                <line key={`mt${i}`} x1="300" y1="60" x2="300" y2="68"
                  transform={`rotate(${i * 15} 300 300)`}
                  stroke={color} strokeWidth="0.6" opacity="0.4" />
              ))}
            </g>
            {/* Static cross-hairlines */}
            <line x1="60" y1="300" x2="540" y2="300" stroke={color} strokeWidth="0.4" opacity="0.18" />
            <line x1="300" y1="60" x2="300" y2="540" stroke={color} strokeWidth="0.4" opacity="0.18" />
            {/* Ornate inner sigil — 8-pointed star */}
            <g style={{ transformOrigin: '300px 300px', animation: 'fi-rotate-slow 120s linear infinite' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <line key={`star${i}`} x1="300" y1="170" x2="300" y2="430"
                  transform={`rotate(${i * 22.5} 300 300)`}
                  stroke={color} strokeWidth="0.3" opacity="0.18" />
              ))}
              <circle cx="300" cy="300" r="120" fill="none" stroke={color} strokeWidth="0.5" opacity="0.35" />
              <circle cx="300" cy="300" r="80" fill="none" stroke={color} strokeWidth="0.4" opacity="0.4" strokeDasharray="3 2" />
            </g>
          </svg>
        </motion.div>
      </div>

      {/* TITLE CONTENT */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none px-6">
        {/* Top ornament: floor of 10 with diamond */}
        <motion.div initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: [0, 0.7, 0.7, 0.45], y: 0 }}
          transition={{ delay: 0.4, duration: 2.4, times: [0, 0.2, 0.7, 1] }}
          className="flex items-center gap-3.5 mb-3">
          <span style={{ color: glowColor(0.6), fontSize: '10px' }}>◆</span>
          <span className="text-[10px] md:text-xs tracking-[0.6em] font-mono uppercase font-black"
            style={{ color: glowColor(0.85), textShadow: `0 0 8px ${glowColor(0.6)}` }}>
            Floor {roman} of X
          </span>
          <span style={{ color: glowColor(0.6), fontSize: '10px' }}>◆</span>
        </motion.div>

        {/* "THE DESCENT" overhead label — like a chapter heading */}
        <motion.p
          initial={{ opacity: 0, letterSpacing: '0.8em' }}
          animate={{ opacity: 0.4, letterSpacing: '0.55em' }}
          transition={{ delay: 0.6, duration: 1.6 }}
          className="text-[8px] md:text-[10px] font-mono uppercase mb-4"
          style={{ color: 'rgba(255,255,255,0.45)' }}>
          A chapter of the descent
        </motion.p>

        {/* Title — Cinzel Decorative with glitch + glow + per-floor accent */}
        <div className="relative">
          {/* Glitch shadow layer */}
          <motion.h2 initial={{ opacity: 0 }} animate={{ opacity: 0.14 }} transition={{ delay: 1.2 }}
            className="absolute inset-0 text-5xl md:text-7xl lg:text-8xl font-black text-center select-none whitespace-nowrap"
            aria-hidden style={{ fontFamily: "'Cinzel Decorative', serif", color, animation: 'fi-glitch 5s steps(1) infinite', filter: 'blur(2px)' }}>
            {floor.name}
          </motion.h2>
          {/* Main title */}
          <motion.h2 initial={{ letterSpacing: '0.7em', scale: 0.85, opacity: 0, filter: 'blur(8px)' }}
            animate={{ letterSpacing: '0.12em', scale: 1, opacity: 1, filter: 'blur(0px)' }}
            transition={{ delay: 0.85, duration: 2.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-5xl md:text-7xl lg:text-8xl font-black text-center relative whitespace-nowrap"
            style={isFloor5
              ? { fontFamily: "'Cinzel Decorative', serif", ...f5GradientStyle }
              : { fontFamily: "'Cinzel Decorative', serif", color, animation: `fi-glow-${floorNum} 4s ease-in-out infinite` }}>
            {floor.name}
          </motion.h2>
        </div>

        {/* Ornamental shimmer divider with center crest */}
        <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 'min(440px, 78vw)', opacity: 1 }}
          transition={{ delay: 1.9, duration: 1.1 }}
          className="relative h-[2px] mt-6 flex items-center justify-center"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${glowColor(0.1)} 15%, ${glowColor(0.7)} 50%, ${glowColor(0.1)} 85%, transparent 100%)`,
            backgroundSize: '200% 100%', animation: 'fi-shimmer 3s linear infinite',
          }}>
          {/* Center diamond crest */}
          <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 2.4, duration: 0.6 }}
            className="absolute" style={{
              width: '14px', height: '14px',
              background: color,
              boxShadow: `0 0 12px ${glowColor(0.9)}, 0 0 24px ${glowColor(0.5)}`,
              transform: 'rotate(45deg)',
            }} />
        </motion.div>

        {/* Description — engraved style */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.85 }}
          transition={{ delay: 2.1, duration: 1 }}
          className="text-xs md:text-sm font-mono mt-5 tracking-[0.2em] uppercase font-bold text-center max-w-xl"
          style={{ color: glowColor(0.95), textShadow: `0 0 6px ${glowColor(0.4)}` }}>
          {floor.desc}
        </motion.p>

        {/* LORE TABLET — ancient inscribed slab */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 1, ease: 'easeOut' }}
          className="relative mt-7 px-7 py-3.5 max-w-md"
          style={{
            background: `linear-gradient(180deg, rgba(20,15,10,0.65) 0%, rgba(10,8,5,0.65) 100%)`,
            border: `1px solid ${glowColor(0.45)}`,
            boxShadow: `0 0 20px ${glowColor(0.18)} inset, 0 4px 18px rgba(0,0,0,0.6)`,
            backdropFilter: 'blur(2px)',
          }}>
          {/* Corner sigils */}
          {[
            ['top-0 left-0', '0deg'],
            ['top-0 right-0', '90deg'],
            ['bottom-0 right-0', '180deg'],
            ['bottom-0 left-0', '270deg'],
          ].map(([pos, rot]) => (
            <div key={pos} className={`absolute ${pos} w-2.5 h-2.5 m-1`} style={{
              transform: `rotate(${rot})`,
              borderTop: `1px solid ${glowColor(0.85)}`,
              borderLeft: `1px solid ${glowColor(0.85)}`,
            }} />
          ))}
          <p className="text-[11px] md:text-xs font-serif italic text-center" style={{
            color: 'rgba(255,240,200,0.78)',
            letterSpacing: '0.04em',
            lineHeight: 1.55,
            textShadow: `0 0 4px ${glowColor(0.3)}`,
          }}>
            {lore}
          </p>
        </motion.div>

        {/* Loading dots + "ENTERING" label */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}
          className="flex flex-col items-center gap-2.5 mt-6">
          <span className="text-[8px] tracking-[0.55em] font-mono uppercase font-bold" style={{ color: glowColor(0.65) }}>
            Preparing the descent
          </span>
          <div className="flex gap-2.5 items-center">
            {[0, 1, 2].map(i => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 1, 0.2], scale: [0, 1.3, 0.8] }}
                transition={{ delay: 3.2 + i * 0.25, duration: 0.5, ease: 'easeOut' }}
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: color, boxShadow: `0 0 10px ${glowColor(0.7)}` }} />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Edge ornaments — left and right vertical chains of diamonds */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} transition={{ delay: 1.5, duration: 1 }}
        className="absolute top-1/2 left-6 -translate-y-1/2 z-15 pointer-events-none flex flex-col gap-3">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={`l${i}`} style={{
            width: '6px', height: '6px',
            background: color, transform: 'rotate(45deg)',
            boxShadow: `0 0 8px ${glowColor(0.6)}`,
            opacity: i === 2 ? 1 : 0.4,
          }} />
        ))}
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} transition={{ delay: 1.5, duration: 1 }}
        className="absolute top-1/2 right-6 -translate-y-1/2 z-15 pointer-events-none flex flex-col gap-3">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={`r${i}`} style={{
            width: '6px', height: '6px',
            background: color, transform: 'rotate(45deg)',
            boxShadow: `0 0 8px ${glowColor(0.6)}`,
            opacity: i === 2 ? 1 : 0.4,
          }} />
        ))}
      </motion.div>

      {/* Scanlines — subtle CRT feel */}
      <div className="absolute inset-0 z-25 pointer-events-none opacity-[0.05]"
        style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 4px)' }} />

      {/* Film grain */}
      <div className="absolute inset-0 z-26 pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>")`,
        backgroundSize: '200px 200px',
        opacity: 0.06,
        mixBlendMode: 'overlay',
      }} />

      {/* Fade to black → trigger game */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 4.4, duration: 0.7, ease: 'easeIn' }}
        onAnimationComplete={onReady}
        className="absolute inset-0 z-30 pointer-events-none"
        style={{ background: '#000' }} />
    </div>
  );
}

// ════════════════════════════════════════
// FLOOR MINI-GAMES
// ════════════════════════════════════════

// Floor 1: The Drain — "LEX PUTRIDA"
// Lore: "The first threshold. Toxic pipes burst and flood the floor. The
//        Chancellor watches to see if you can seal them before the filth takes you."
//
// A single ornate iron grate at the center of the chamber. Six fixed leak ports
// arranged on its face. Leaks burst at random — each fills a circular sludge
// meter; when full, it overflows. Tap a leak before it overflows to seal it.
// 12 seals to pass. 3 overflows = wipe. Easy floor-1 onramp: short, dopamine-
// rich, single input, instant readability.
function Floor1({ onComplete, onFail }) {
  const TOTAL_SEALS  = 12;
  const MAX_OVERFLOWS = 3;
  const PORT_COUNT    = 6;

  // ─── State ──────────────────────────────────────────────────────────────
  const [seals, setSeals]         = useState(0);
  const [overflows, setOverflows] = useState(0);
  const [leaks, setLeaks]         = useState([]);          // active leaks
  const [phase, setPhase]         = useState('opening');   // 'opening' | 'play' | 'win' | 'wipe'
  const [, force]                 = useState(0);

  const sealsRef     = useRef(0);
  const overflowsRef = useRef(0);
  const leaksRef     = useRef([]);
  const leakIdRef    = useRef(0);
  const lastSpawnRef = useRef(0);
  const failedRef    = useRef(false);
  const completedRef = useRef(false);
  const sealEffectsRef = useRef([]); // transient gold splash rings
  const overflowFlashRef = useRef(0);
  const rafRef = useRef(null);

  // Sealed effects are short visual blooms; we use a ref + force-render
  const addSealEffect = useCallback((portIdx) => {
    sealEffectsRef.current.push({ id: Date.now() + Math.random(), portIdx, spawnedAt: performance.now() });
  }, []);

  // ─── Cinematic opening (1.2s) then play ─────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      if (failedRef.current || completedRef.current) return;
      setPhase('play');
      lastSpawnRef.current = performance.now();
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  // ─── Cleanup ────────────────────────────────────────────────────────────
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // ─── Game loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'play') return;

    const FILL_MS = 2400;        // base time to overflow from spawn — very forgiving
    const FILL_MS_LATE = 1900;   // tightens as more seals accumulate

    const loop = () => {
      const now = performance.now();

      // Difficulty: spawn cadence + fill time
      const progressFrac = Math.min(1, sealsRef.current / TOTAL_SEALS);
      const spawnGap = Math.round(1500 - progressFrac * 600); // 1500ms → 900ms
      const fillMs   = Math.round(FILL_MS - progressFrac * (FILL_MS - FILL_MS_LATE));

      // Max concurrent leaks
      const maxConcurrent = sealsRef.current < 4 ? 1 : sealsRef.current < 8 ? 2 : 3;

      if (
        now - lastSpawnRef.current >= spawnGap &&
        leaksRef.current.length < maxConcurrent &&
        !failedRef.current &&
        !completedRef.current
      ) {
        // Spawn a leak at an unused port
        const usedPorts = new Set(leaksRef.current.map(l => l.portIdx));
        const free = [];
        for (let i = 0; i < PORT_COUNT; i++) if (!usedPorts.has(i)) free.push(i);
        if (free.length > 0) {
          const portIdx = free[Math.floor(Math.random() * free.length)];
          leaksRef.current.push({
            id: leakIdRef.current++,
            portIdx,
            spawnedAt: now,
            fillMs,
            overflowed: false,
            sealed: false,
          });
          lastSpawnRef.current = now;
        }
      }

      // Update leaks → mark overflowed when full
      leaksRef.current = leaksRef.current.filter(l => {
        if (l.sealed) return now - l.sealedAt < 400; // keep briefly for splash
        const age = now - l.spawnedAt;
        if (!l.overflowed && age >= l.fillMs) {
          l.overflowed = true;
          overflowsRef.current += 1;
          setOverflows(overflowsRef.current);
          overflowFlashRef.current = now;
          if (overflowsRef.current >= MAX_OVERFLOWS && !failedRef.current) {
            failedRef.current = true;
            setPhase('wipe');
            setTimeout(onFail, 1100);
          }
        }
        if (l.overflowed) return now - l.spawnedAt < l.fillMs + 600;
        return true;
      });

      // Prune old seal-effect rings
      sealEffectsRef.current = sealEffectsRef.current.filter(e => now - e.spawnedAt < 700);

      // Force re-render
      force(v => (v + 1) & 0xffff);

      if (!failedRef.current && !completedRef.current) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, onFail]);

  // ─── Tap handler ────────────────────────────────────────────────────────
  const handleLeakTap = (leakId) => {
    if (phase !== 'play') return;
    const leak = leaksRef.current.find(l => l.id === leakId && !l.sealed && !l.overflowed);
    if (!leak) return;
    leak.sealed = true;
    leak.sealedAt = performance.now();
    addSealEffect(leak.portIdx);
    sealsRef.current += 1;
    setSeals(sealsRef.current);
    if (sealsRef.current >= TOTAL_SEALS && !completedRef.current) {
      completedRef.current = true;
      setPhase('win');
      setTimeout(onComplete, 1300);
    }
  };

  // ─── Port positions on the grate (clock 12, 2, 4, 6, 8, 10) ─────────────
  // Radius 19 places vents cleanly inside the grate spoke field (spokes run 4-25.6).
  const portPositions = Array.from({ length: PORT_COUNT }, (_, i) => {
    const angle = (i / PORT_COUNT) * Math.PI * 2 - Math.PI / 2;
    return {
      idx: i,
      x: 50 + 19 * Math.cos(angle),
      y: 50 + 19 * Math.sin(angle),
    };
  });

  const now = performance.now();
  const overflowShake = overflowFlashRef.current && (now - overflowFlashRef.current < 350);

  return (
    <div
      className="relative h-full w-full overflow-hidden select-none"
      data-testid="floor-1"
      style={{
        background: '#070302', touchAction: 'none',
        animation: phase === 'wipe'
          ? 'f1-wipe-shake 0.16s steps(3) 5'
          : overflowShake
            ? 'f1-overflow-shake 0.18s steps(3) 2'
            : 'none',
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Cinzel+Decorative:wght@700;900&display=swap');
        @keyframes f1-grate-breathe {
          0%,100% { transform: scale(1);    filter: drop-shadow(0 0 14px rgba(57,255,20,0.18)); }
          50%     { transform: scale(1.015); filter: drop-shadow(0 0 24px rgba(57,255,20,0.32)); }
        }
        @keyframes f1-spoke-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes f1-leak-burst {
          0%   { opacity: 0; transform: scale(0.3); }
          70%  { opacity: 1; transform: scale(1.18); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes f1-leak-pulse {
          0%,100% { filter: drop-shadow(0 0 6px rgba(57,255,20,0.65)) drop-shadow(0 0 16px rgba(57,255,20,0.35)); }
          50%     { filter: drop-shadow(0 0 10px rgba(120,255,80,0.85)) drop-shadow(0 0 24px rgba(57,255,20,0.55)); }
        }
        @keyframes f1-seal-bloom {
          0%   { opacity: 1; transform: scale(0.5); }
          100% { opacity: 0; transform: scale(2.6); }
        }
        @keyframes f1-seal-splash {
          0%   { opacity: 0; }
          25%  { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(2.2); }
        }
        @keyframes f1-overflow-flash {
          0%, 100% { opacity: 0; }
          25%      { opacity: 0.55; }
        }
        @keyframes f1-overflow-shake {
          0%,100% { transform: translate(0,0); }
          25%     { transform: translate(-2px,1px); }
          50%     { transform: translate(2px,-1px); }
          75%     { transform: translate(-1px,2px); }
        }
        @keyframes f1-wipe-shake {
          0%,100% { transform: translate(0,0); }
          25%     { transform: translate(-5px,2px); }
          50%     { transform: translate(5px,-2px); }
          75%     { transform: translate(-3px,3px); }
        }
        @keyframes f1-drip {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.45; }
          85%  { opacity: 0.25; }
          100% { transform: translateY(0%); opacity: 0; }
        }
        @keyframes f1-opening-bloom {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.2); }
          60%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%,-50%) scale(6); }
        }
        @keyframes f1-win-radiance {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.4); }
          100% { opacity: 1; transform: translate(-50%,-50%) scale(3); }
        }
        @keyframes f1-win-text {
          0%   { opacity: 0; transform: translate(-50%,-50%) translateY(20px); letter-spacing: 0.7em; filter: blur(6px); }
          25%  { opacity: 1; transform: translate(-50%,-50%) translateY(0);    letter-spacing: 0.32em; filter: blur(0); }
          75%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%,-50%) translateY(-12px); letter-spacing: 0.5em; filter: blur(3px); }
        }
      `}</style>

      {/* === SEWER STONE BACKGROUND === */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, rgba(20,45,18,0.4) 0%, rgba(8,18,8,0.85) 35%, rgba(3,8,3,0.96) 70%, #020403 100%)',
      }} />

      {/* Subtle moss/grime texture via overlapping radial gradients */}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        backgroundImage: `
          radial-gradient(circle at 18% 22%, rgba(57,180,30,0.12) 0%, transparent 28%),
          radial-gradient(circle at 82% 75%, rgba(40,120,20,0.10) 0%, transparent 30%),
          radial-gradient(circle at 50% 50%, rgba(80,200,40,0.05) 0%, transparent 60%)
        `,
      }} />

      {/* Top → bottom shaft tint */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 25%, transparent 75%, rgba(15,40,12,0.55) 100%)',
      }} />

      {/* Vertical grime drips */}
      <div className="absolute inset-x-0 top-0 h-full pointer-events-none overflow-hidden">
        {Array.from({ length: 12 }).map((_, i) => {
          const x = 4 + (i * 8.2);
          const dur = 8 + ((i * 3) % 7);
          const delay = -((i * 0.55) % dur);
          return (
            <div key={`f1-drip-${i}`} className="absolute" style={{
              left: `${x}%`, top: 0, width: 1, height: '40vh',
              background: 'linear-gradient(180deg, rgba(57,140,30,0.28) 0%, rgba(80,180,40,0.4) 60%, transparent 100%)',
              animation: `f1-drip ${dur}s linear ${delay}s infinite`,
              transformOrigin: 'top',
            }} />
          );
        })}
      </div>

      {/* === SVG ARENA === */}
      <svg className="absolute left-1/2 top-1/2 pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: 'min(92vmin, 100%)',
          height: 'min(92vmin, 100%)',
          transform: 'translate(-50%,-50%)',
          overflow: 'visible',
        }}>
        <defs>
          <radialGradient id="f1-grate-iron" cx="50%" cy="40%" r="65%">
            <stop offset="0%"  stopColor="#4a4238" />
            <stop offset="40%" stopColor="#2a2620" />
            <stop offset="80%" stopColor="#14110d" />
            <stop offset="100%" stopColor="#080604" />
          </radialGradient>
          <radialGradient id="f1-grate-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#1a3a14" />
            <stop offset="50%" stopColor="#0a1808" />
            <stop offset="100%" stopColor="#040804" />
          </radialGradient>
          <radialGradient id="f1-sludge" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#a8ff6a" />
            <stop offset="35%" stopColor="#39c020" />
            <stop offset="80%" stopColor="#1a5a0c" />
            <stop offset="100%" stopColor="#062004" />
          </radialGradient>
        </defs>

        {/* === GRATE FRAME === */}
        <g style={{ animation: 'f1-grate-breathe 4s ease-in-out infinite', transformOrigin: '50px 50px' }}>
          {/* Outermost iron ring */}
          <circle cx="50" cy="50" r="32" fill="url(#f1-grate-iron)" stroke="#0a0806" strokeWidth="0.4" />
          {/* Rivets around outer ring */}
          {Array.from({ length: 18 }).map((_, i) => {
            const a = (i / 18) * Math.PI * 2;
            const rx = 50 + 30.5 * Math.cos(a);
            const ry = 50 + 30.5 * Math.sin(a);
            return <circle key={`rivet-${i}`} cx={rx} cy={ry} r="0.55"
              fill="#0a0806" stroke="#4a4238" strokeWidth="0.15" />;
          })}
          {/* Inner stepped ring */}
          <circle cx="50" cy="50" r="27" fill="none" stroke="#0a0806" strokeWidth="0.5" />
          <circle cx="50" cy="50" r="26.4" fill="none" stroke="#2a2620" strokeWidth="0.25" />
          {/* Dark grate interior */}
          <circle cx="50" cy="50" r="26" fill="url(#f1-grate-core)" />

          {/* Radial spokes (16 bars) — the actual grate */}
          <g style={{ transformOrigin: '50px 50px' }}>
            {Array.from({ length: 16 }).map((_, i) => {
              const a = (i / 16) * Math.PI * 2;
              const x1 = 50 + 4 * Math.cos(a);
              const y1 = 50 + 4 * Math.sin(a);
              const x2 = 50 + 25.6 * Math.cos(a);
              const y2 = 50 + 25.6 * Math.sin(a);
              return <line key={`spoke-${i}`} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#0a0806" strokeWidth="0.9" strokeLinecap="round" />;
            })}
          </g>
          {/* Two concentric grate rings */}
          <circle cx="50" cy="50" r="16" fill="none" stroke="#0a0806" strokeWidth="0.45" />
          <circle cx="50" cy="50" r="10" fill="none" stroke="#0a0806" strokeWidth="0.4" />

          {/* === PORT VENTS (always visible — leaks burst from these) === */}
          {portPositions.map(p => (
            <g key={`port-vent-${p.idx}`}>
              {/* Dark recessed vent hole — ornate brass rim */}
              <circle cx={p.x} cy={p.y} r="4.3"
                fill="#040402"
                stroke="#3a3026"
                strokeWidth="0.5" />
              {/* Inner shadow ring */}
              <circle cx={p.x} cy={p.y} r="3.8"
                fill="none" stroke="#000" strokeWidth="0.35" opacity="0.85" />
              {/* Brass highlight rim (top arc) */}
              <path
                d={`M ${(p.x - 3.7).toFixed(2)} ${(p.y - 0.4).toFixed(2)} A 3.7 3.7 0 0 1 ${(p.x + 3.7).toFixed(2)} ${(p.y - 0.4).toFixed(2)}`}
                fill="none" stroke="#5a4838" strokeWidth="0.35" opacity="0.6" />
              {/* Four tiny rivets around vent rim */}
              {[45, 135, 225, 315].map((deg, i) => {
                const a = deg * Math.PI / 180;
                const rx = p.x + 4.0 * Math.cos(a);
                const ry = p.y + 4.0 * Math.sin(a);
                return <circle key={`vrv-${p.idx}-${i}`} cx={rx} cy={ry} r="0.25"
                  fill="#1a1410" stroke="#4a3a2c" strokeWidth="0.1" />;
              })}
            </g>
          ))}

          {/* Center cap */}
          <circle cx="50" cy="50" r="3.8" fill="url(#f1-grate-iron)" stroke="#0a0806" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="1.8" fill="#0a0806" />
          <circle cx="50" cy="50" r="0.5" fill="#39ff14" opacity="0.5" />

          {/* Engraved sigil ring */}
          <g fill="rgba(120,220,80,0.3)" fontFamily="'Cinzel Decorative', serif" fontSize="1.6" textAnchor="middle">
            {[30, 90, 150, 210, 270, 330].map(deg => {
              const rad = deg * Math.PI / 180;
              const x = 50 + 28.5 * Math.cos(rad);
              const y = 50 + 28.5 * Math.sin(rad) + 0.5;
              return <text key={`sig-${deg}`} x={x} y={y} transform={`rotate(${deg + 90} ${x} ${y})`}>✦</text>;
            })}
          </g>
        </g>

        {/* === LEAKS === */}
        {leaksRef.current.map(leak => {
          const port = portPositions[leak.portIdx];
          if (leak.sealed) return null;
          const age = performance.now() - leak.spawnedAt;
          const fill = Math.min(1, age / leak.fillMs);
          const radius = 3.4;       // sits cleanly inside the 3.8 vent inner shadow
          const fillCircumference = 2 * Math.PI * (radius + 1.3);
          const isCritical = fill > 0.7;
          return (
            <g key={`leak-${leak.id}`} style={{
              transformOrigin: `${port.x}px ${port.y}px`,
              animation: 'f1-leak-burst 0.35s ease-out, f1-leak-pulse 1s ease-in-out infinite',
            }}>
              {/* Outer fill-meter ring (depleting) */}
              <circle cx={port.x} cy={port.y} r={radius + 1.3}
                fill="none"
                stroke={isCritical ? '#ff6040' : '#39ff14'}
                strokeWidth="0.5"
                strokeDasharray={`${fillCircumference * fill} ${fillCircumference}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${port.x} ${port.y})`}
                style={{ filter: isCritical ? 'drop-shadow(0 0 2px #ff6040)' : 'drop-shadow(0 0 1.5px #39ff14)' }} />
              {/* Inner faint base ring */}
              <circle cx={port.x} cy={port.y} r={radius + 1.3}
                fill="none" stroke="rgba(57,255,20,0.12)" strokeWidth="0.3" />
              {/* Sludge bubble */}
              <circle cx={port.x} cy={port.y} r={radius}
                fill="url(#f1-sludge)"
                opacity="0.96" />
              {/* Highlight glint */}
              <circle cx={port.x - 0.85} cy={port.y - 1.05} r="0.8"
                fill="rgba(220,255,180,0.6)" />
              {/* Splash droplet around the vent rim (looks like sludge spilling over) */}
              <circle cx={port.x + 2.6} cy={port.y + 2.2} r="0.4"
                fill="rgba(120,220,80,0.5)" />
              <circle cx={port.x - 2.4} cy={port.y + 2.5} r="0.3"
                fill="rgba(80,180,40,0.45)" />
            </g>
          );
        })}

        {/* === SEAL EFFECTS === */}
        {sealEffectsRef.current.map(e => {
          const port = portPositions[e.portIdx];
          const age = performance.now() - e.spawnedAt;
          if (age > 700) return null;
          return (
            <g key={`seal-${e.id}`} style={{ transformOrigin: `${port.x}px ${port.y}px` }}>
              <circle cx={port.x} cy={port.y} r="3.5"
                fill="none" stroke="#ffd070" strokeWidth="0.65"
                style={{ animation: 'f1-seal-bloom 0.6s ease-out forwards' }} />
              <circle cx={port.x} cy={port.y} r="2.5"
                fill="rgba(255,220,140,0.45)"
                style={{ animation: 'f1-seal-splash 0.5s ease-out forwards' }} />
            </g>
          );
        })}

        {/* === Overflow markers (red flashes at overflowed leaks) === */}
        {leaksRef.current.filter(l => l.overflowed && !l.sealed).map(leak => {
          const port = portPositions[leak.portIdx];
          return (
            <g key={`of-${leak.id}`}>
              <circle cx={port.x} cy={port.y} r="5"
                fill="rgba(255,40,40,0.55)"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255,0,40,0.95))' }} />
              <circle cx={port.x} cy={port.y} r="3.2"
                fill="rgba(255,0,40,0.85)" />
            </g>
          );
        })}
      </svg>

      {/* === HTML tap zones for leaks (reliable hit-testing) === */}
      {leaksRef.current.filter(l => !l.sealed && !l.overflowed).map(leak => {
        const port = portPositions[leak.portIdx];
        return (
          <button key={`tap-${leak.id}`}
            onClick={() => handleLeakTap(leak.id)}
            data-testid={`f1-leak-${leak.id}`}
            aria-label="Seal leak"
            style={{
              position: 'absolute',
              left: `calc(50% + (${port.x - 50}) * min(92vmin, 100%) / 100)`,
              top:  `calc(50% + (${port.y - 50}) * min(92vmin, 100%) / 100)`,
              transform: 'translate(-50%,-50%)',
              width: 'min(11vmin, 76px)', height: 'min(11vmin, 76px)',
              borderRadius: '50%',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              zIndex: 30,
            }} />
        );
      })}

      {/* === OPENING BLOOM === */}
      {phase === 'opening' && (
        <div className="absolute left-1/2 top-1/2 pointer-events-none rounded-full" style={{
          width: '10vmin', height: '10vmin',
          background: 'radial-gradient(circle, rgba(120,255,80,0.55) 0%, rgba(57,200,20,0.22) 40%, transparent 75%)',
          animation: 'f1-opening-bloom 1.2s ease-out forwards',
        }} />
      )}

      {/* === HUD === */}
      <div className="absolute top-0 left-0 right-0 z-40 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.78) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[9px] tracking-[0.42em] font-mono uppercase" style={{ color: 'rgba(120,255,80,0.45)' }}>
              Lex Putrida
            </span>
            <p className="font-mono mt-1 text-[14px] font-black" style={{
              color: '#a8ff6a',
              textShadow: '0 0 8px rgba(120,255,80,0.45)',
            }}>{seals}<span className="text-white/15 font-normal text-[11px]">/{TOTAL_SEALS}</span><span className="ml-2 text-[9px] font-normal text-white/35">sealed</span></p>
          </div>

          {/* Overflow indicators (3 brass tokens) */}
          <div className="flex flex-col items-end gap-1">
            <span className="text-[7px] font-mono tracking-[0.3em] uppercase" style={{ color: 'rgba(255,180,120,0.4)' }}>Overflows</span>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: MAX_OVERFLOWS }).map((_, i) => {
                const lost = i < overflows;
                return (
                  <div key={`of-${i}`} className="w-2.5 h-2.5 rounded-full transition-all duration-300" style={{
                    background: lost ? '#5a1a08' : '#a8ff6a',
                    border: `1px solid ${lost ? 'rgba(120,40,20,0.6)' : 'rgba(120,255,80,0.7)'}`,
                    boxShadow: lost ? 'none' : '0 0 6px rgba(120,255,80,0.6)',
                    opacity: lost ? 0.4 : 1,
                  }} />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* === BOTTOM PROMPT === */}
      <div className="absolute bottom-3 left-0 right-0 z-40 text-center">
        <span className="text-[8px] font-mono tracking-[0.32em] uppercase" style={{ color: 'rgba(120,255,80,0.32)' }}>
          Tap each leak before it overflows
        </span>
      </div>

      {/* === Overflow screen flash === */}
      {overflowShake && (
        <div className="absolute inset-0 z-30 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(255,0,40,0.15) 75%, rgba(255,0,40,0.32) 100%)',
          animation: 'f1-overflow-flash 0.35s ease-out forwards',
        }} />
      )}

      {/* === Win bloom + text === */}
      {phase === 'win' && (
        <>
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-40 rounded-full" style={{
            width: '14vmin', height: '14vmin',
            background: 'radial-gradient(circle, rgba(150,255,180,0.55) 0%, rgba(80,220,140,0.18) 50%, transparent 80%)',
            animation: 'f1-win-radiance 1.2s ease-out forwards',
          }} />
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-50" style={{
            transform: 'translate(-50%,-50%)',
            color: '#d8ffd0',
            fontFamily: "'Cinzel Decorative', serif",
            fontSize: 'clamp(28px, 5vmin, 56px)',
            fontWeight: 900,
            letterSpacing: '0.32em',
            textShadow: '0 0 16px rgba(150,255,180,0.85), 0 0 36px rgba(80,220,140,0.55)',
            whiteSpace: 'nowrap',
            animation: 'f1-win-text 1.3s ease-in-out forwards',
          }}>DRAIN SEALED</div>
        </>
      )}

      {/* === Late-game vignette === */}
      {overflows >= 2 && phase === 'play' && (
        <div className="absolute inset-0 z-5 pointer-events-none transition-opacity duration-500" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(255,0,40,0.1) 75%, rgba(255,0,40,0.22) 100%)',
          opacity: 1,
        }} />
      )}
    </div>
  );
}


// Floor 2: Pipe Maze — "THE FORGED LIE"
// Lore: "The valves cry in sequence — but the Chancellor's steam carries false
//        notes. Your iron memory must distinguish what is from what was whispered."
//
// Six ornate brass valves orbit a central boiler. A sequence ignites them in
// order — but one valve in each sequence is a LIE: a faint crimson tint under
// the brass, a half-beat-late steam puff. The player must replay the sequence
// SKIPPING the false valve. Round 1: 4 steps · 1 lie. Round 3: 6 steps · 1 lie.
// Round 5: 7 steps · 2 lies. Wrong order or tapping a lie = a steam-strike.
// Three strikes and the boiler claims you. Five rounds to pass.
function Floor2({ onComplete, onFail }) {
  const VALVE_COUNT      = 6;
  const ORBIT_RADIUS_PCT = 30;     // % of vmin from center
  const TOTAL_ROUNDS     = 5;
  const MAX_STRIKES      = 3;

  // ─── Round config ───────────────────────────────────────────────────────
  const roundConfig = useCallback((r) => {
    if (r === 1) return { steps: 4, lies: 1, playbackMs: 720 };
    if (r === 2) return { steps: 5, lies: 1, playbackMs: 660 };
    if (r === 3) return { steps: 6, lies: 1, playbackMs: 600 };
    if (r === 4) return { steps: 6, lies: 2, playbackMs: 560 };
    return         { steps: 7, lies: 2, playbackMs: 520 };
  }, []);

  // ─── State ──────────────────────────────────────────────────────────────
  const [round, setRound]         = useState(1);
  const [phase, setPhase]         = useState('intro');     // 'intro' | 'playback' | 'input' | 'roundwin' | 'wipe'
  const [sequence, setSequence]   = useState([]);          // [{valveIdx, lie:bool}, ...]
  const [playbackIdx, setPlaybackIdx] = useState(-1);
  const [inputIdx, setInputIdx]   = useState(0);           // pointer into truthSequence
  const [strikes, setStrikes]     = useState(0);
  const [activeValve, setActiveValve] = useState(null);
  const [shakeValve, setShakeValve]   = useState(null);
  const [pressure, setPressure]   = useState(0);
  const [showHint, setShowHint]   = useState(false);

  const failedRef    = useRef(false);
  const completedRef = useRef(false);
  const seqRef       = useRef([]);
  const inputIdxRef  = useRef(0);
  const strikesRef   = useRef(0);
  const roundRef     = useRef(1);
  const playbackTimers = useRef([]);

  // ─── Sequence generator ─────────────────────────────────────────────────
  const generateSequence = useCallback((r) => {
    const cfg = roundConfig(r);
    const seq = [];
    const lieIndices = new Set();
    while (lieIndices.size < cfg.lies) {
      lieIndices.add(Math.floor(Math.random() * cfg.steps));
    }
    let lastIdx = -1;
    for (let i = 0; i < cfg.steps; i++) {
      let v = Math.floor(Math.random() * VALVE_COUNT);
      // Avoid immediate same-valve repeats — looks broken visually
      if (v === lastIdx) v = (v + 1 + Math.floor(Math.random() * (VALVE_COUNT - 1))) % VALVE_COUNT;
      lastIdx = v;
      seq.push({ valveIdx: v, lie: lieIndices.has(i) });
    }
    return seq;
  }, [roundConfig]);

  const truthSequence = (seq) => seq.filter(s => !s.lie);

  // ─── Start a round ──────────────────────────────────────────────────────
  const startRound = useCallback((r) => {
    if (failedRef.current || completedRef.current) return;
    const seq = generateSequence(r);
    seqRef.current = seq;
    setSequence(seq);
    inputIdxRef.current = 0;
    setInputIdx(0);
    setPlaybackIdx(-1);
    setActiveValve(null);
    setShakeValve(null);
    setPhase('playback');
    setShowHint(r === 1); // hint only on the first round

    const cfg = roundConfig(r);
    // Clear any previous playback timers
    playbackTimers.current.forEach(clearTimeout);
    playbackTimers.current = [];

    // Schedule each valve ignition
    const stepGap = cfg.playbackMs;
    seq.forEach((step, i) => {
      const onAt = 600 + i * stepGap;       // 600ms initial breath
      const offAt = onAt + Math.round(stepGap * 0.55);
      playbackTimers.current.push(setTimeout(() => {
        if (failedRef.current || completedRef.current) return;
        setPlaybackIdx(i);
        setActiveValve({ idx: step.valveIdx, lie: step.lie });
      }, onAt));
      playbackTimers.current.push(setTimeout(() => {
        if (failedRef.current || completedRef.current) return;
        setActiveValve(null);
      }, offAt));
    });

    // Transition to input phase after playback finishes
    const totalMs = 600 + seq.length * stepGap + 350;
    playbackTimers.current.push(setTimeout(() => {
      if (failedRef.current || completedRef.current) return;
      setPlaybackIdx(-1);
      setPhase('input');
    }, totalMs));
  }, [generateSequence, roundConfig]);

  // ─── Initial start ──────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => startRound(1), 800);
    return () => {
      clearTimeout(t);
      playbackTimers.current.forEach(clearTimeout);
    };
  }, [startRound]);

  // ─── Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => () => {
    playbackTimers.current.forEach(clearTimeout);
  }, []);

  // ─── Pressure meter — slow ambient build during input phase ─────────────
  useEffect(() => {
    if (phase !== 'input') {
      setPressure(0);
      return;
    }
    let raf;
    let start = performance.now();
    const loop = () => {
      const elapsed = (performance.now() - start) / 1000;
      // Gentle ambient build: ~15% per 10s. Strikes spike pressure separately.
      setPressure(Math.min(100, elapsed * 1.5));
      if (phase === 'input' && !failedRef.current && !completedRef.current) {
        raf = requestAnimationFrame(loop);
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // ─── Tap handler ────────────────────────────────────────────────────────
  const handleValveTap = (valveIdx) => {
    if (phase !== 'input') return;
    if (failedRef.current || completedRef.current) return;

    const truth = truthSequence(seqRef.current);
    const expected = truth[inputIdxRef.current];
    const correct = expected && expected.valveIdx === valveIdx;

    // Flash visual feedback regardless
    setShakeValve({ idx: valveIdx, success: correct, ts: Date.now() });
    setTimeout(() => setShakeValve(null), 450);

    if (correct) {
      inputIdxRef.current += 1;
      setInputIdx(inputIdxRef.current);
      // Sequence complete?
      if (inputIdxRef.current >= truth.length) {
        setPhase('roundwin');
        const nextRound = roundRef.current + 1;
        if (nextRound > TOTAL_ROUNDS) {
          completedRef.current = true;
          setTimeout(onComplete, 1100);
        } else {
          setTimeout(() => {
            roundRef.current = nextRound;
            setRound(nextRound);
            startRound(nextRound);
          }, 1200);
        }
      }
    } else {
      // Wrong tap — either a lie, wrong order, or out-of-sequence
      strikesRef.current += 1;
      setStrikes(strikesRef.current);
      setPressure(p => Math.min(100, p + 18));
      if (strikesRef.current >= MAX_STRIKES) {
        failedRef.current = true;
        setPhase('wipe');
        setTimeout(onFail, 1100);
      }
    }
  };

  // ─── Valve positions on the hexagonal ring ──────────────────────────────
  const valvePositions = Array.from({ length: VALVE_COUNT }, (_, i) => {
    const angle = (i / VALVE_COUNT) * Math.PI * 2 - Math.PI / 2;
    return {
      idx: i,
      x: 50 + ORBIT_RADIUS_PCT * Math.cos(angle),
      y: 50 + ORBIT_RADIUS_PCT * Math.sin(angle),
      pipeAngle: (angle * 180) / Math.PI,
    };
  });

  const cfg = roundConfig(round);
  const truthLen = sequence.filter(s => !s.lie).length;

  return (
    <div className="relative h-full w-full overflow-hidden select-none"
      data-testid="floor-2"
      style={{
        background: '#0a0604', touchAction: 'none',
        animation: phase === 'wipe' ? 'f2-rupture-shake 0.16s steps(3) 5' : 'none',
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Cinzel+Decorative:wght@700;900&display=swap');
        @keyframes f2-boiler-breathe {
          0%,100% { transform: scale(1);    filter: drop-shadow(0 0 12px rgba(255,150,40,0.45)); }
          50%     { transform: scale(1.025); filter: drop-shadow(0 0 22px rgba(255,180,80,0.65)); }
        }
        @keyframes f2-steam-rise {
          0%   { opacity: 0; transform: translate(-50%, 0) scale(0.5); }
          25%  { opacity: 0.7; }
          100% { opacity: 0; transform: translate(-50%, -160%) scale(1.6); }
        }
        @keyframes f2-pipe-flow {
          0%   { stroke-dashoffset: 100; opacity: 0.55; }
          50%  { opacity: 0.85; }
          100% { stroke-dashoffset: 0;   opacity: 0.55; }
        }
        @keyframes f2-valve-ignite-true {
          0%,100% { filter: drop-shadow(0 0 8px rgba(255,180,80,0.55)); transform: scale(1); }
          50%     { filter: drop-shadow(0 0 22px rgba(255,210,120,0.95)) drop-shadow(0 0 44px rgba(255,160,40,0.65)); transform: scale(1.06); }
        }
        @keyframes f2-valve-ignite-lie {
          0%,100% { filter: drop-shadow(0 0 8px rgba(220,80,60,0.45)); transform: scale(1); }
          50%     { filter: drop-shadow(0 0 20px rgba(255,80,80,0.85)) drop-shadow(0 0 40px rgba(180,40,40,0.5)); transform: scale(1.04); }
        }
        @keyframes f2-valve-success {
          0%,100% { filter: drop-shadow(0 0 8px rgba(120,255,160,0.6)); }
          50%     { filter: drop-shadow(0 0 26px rgba(150,255,180,0.95)) drop-shadow(0 0 50px rgba(80,220,140,0.55)); }
        }
        @keyframes f2-valve-fail {
          0%,100% { transform: translate(0,0); }
          25%     { transform: translate(-3px,1px); }
          50%     { transform: translate(3px,-1px); }
          75%     { transform: translate(-2px,2px); }
        }
        @keyframes f2-rupture-shake {
          0%,100% { transform: translate(0,0); }
          25%     { transform: translate(-5px,2px); }
          50%     { transform: translate(5px,-2px); }
          75%     { transform: translate(-3px,3px); }
        }
        @keyframes f2-gauge-needle { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(3deg); } }
        @keyframes f2-grime-drip {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.55; }
          85%  { opacity: 0.3; }
          100% { transform: translateY(0%); opacity: 0; }
        }
        @keyframes f2-roundwin-bloom {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.4); }
          40%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%,-50%) scale(2.6); }
        }
        @keyframes f2-roundwin-text {
          0%   { opacity: 0; transform: translate(-50%,-50%) translateY(20px); letter-spacing: 0.7em; filter: blur(6px); }
          25%  { opacity: 1; transform: translate(-50%,-50%) translateY(0);    letter-spacing: 0.32em; filter: blur(0); }
          75%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%,-50%) translateY(-12px); letter-spacing: 0.5em; filter: blur(3px); }
        }
        @keyframes f2-strike-flash {
          0%,100% { opacity: 0; }
          25%     { opacity: 0.4; }
        }
      `}</style>

      {/* === BACKGROUND === */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, rgba(70,30,12,0.55) 0%, rgba(30,12,5,0.85) 40%, rgba(10,4,2,0.96) 75%, #050201 100%)',
      }} />

      {/* Iron-plate wall texture — procedural rivet grid */}
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
        backgroundImage: `
          radial-gradient(circle at 20% 20%, rgba(120,70,40,0.18) 0%, transparent 28%),
          radial-gradient(circle at 80% 70%, rgba(120,70,40,0.14) 0%, transparent 28%),
          radial-gradient(circle at 50% 50%, rgba(180,100,60,0.08) 0%, transparent 55%)
        `,
      }} />

      {/* Subtle vertical "tube" gradient — feels like a deep iron shaft */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 25%, transparent 75%, rgba(60,15,5,0.55) 100%)',
      }} />

      {/* Grime drips */}
      <div className="absolute inset-x-0 top-0 h-full pointer-events-none overflow-hidden">
        {Array.from({ length: 10 }).map((_, i) => {
          const x = 5 + (i * 9.5);
          const dur = 9 + ((i * 3) % 8);
          const delay = -((i * 0.7) % dur);
          return (
            <div key={`drip-${i}`} className="absolute" style={{
              left: `${x}%`, top: 0, width: 1, height: '40vh',
              background: 'linear-gradient(180deg, rgba(80,40,20,0.35) 0%, rgba(120,70,40,0.55) 60%, transparent 100%)',
              animation: `f2-grime-drip ${dur}s linear ${delay}s infinite`,
              transformOrigin: 'top',
            }} />
          );
        })}
      </div>

      {/* === SVG ARENA === */}
      <svg className="absolute left-1/2 top-1/2 pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: 'min(95vmin, 100%)',
          height: 'min(95vmin, 100%)',
          transform: 'translate(-50%,-50%)',
          overflow: 'visible',
        }}>
        <defs>
          <radialGradient id="f2-boiler-grad" cx="50%" cy="40%" r="60%">
            <stop offset="0%"  stopColor="#5a3a26" />
            <stop offset="35%" stopColor="#3a2014" />
            <stop offset="80%" stopColor="#1c0e08" />
            <stop offset="100%" stopColor="#0a0402" />
          </radialGradient>
          <radialGradient id="f2-boiler-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#ffd070" />
            <stop offset="40%" stopColor="#ff7030" />
            <stop offset="80%" stopColor="#8a2010" />
            <stop offset="100%" stopColor="#2a0805" />
          </radialGradient>
          <radialGradient id="f2-valve-brass" cx="40%" cy="35%" r="65%">
            <stop offset="0%"  stopColor="#c89868" />
            <stop offset="35%" stopColor="#8a5828" />
            <stop offset="70%" stopColor="#4a2c12" />
            <stop offset="100%" stopColor="#1a0c04" />
          </radialGradient>
          <radialGradient id="f2-valve-lie" cx="40%" cy="35%" r="65%">
            <stop offset="0%"  stopColor="#c87060" />
            <stop offset="35%" stopColor="#8a3828" />
            <stop offset="70%" stopColor="#4a1810" />
            <stop offset="100%" stopColor="#1a0604" />
          </radialGradient>
          <linearGradient id="f2-pipe-iron" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5a3826" />
            <stop offset="50%" stopColor="#2c1808" />
            <stop offset="100%" stopColor="#1a0c04" />
          </linearGradient>
        </defs>

        {/* === Pipes connecting boiler to each valve === */}
        {valvePositions.map(v => (
          <g key={`pipe-${v.idx}`}>
            {/* Outer pipe casing */}
            <line x1="50" y1="50" x2={v.x} y2={v.y}
              stroke="url(#f2-pipe-iron)" strokeWidth="3.2" strokeLinecap="round" />
            {/* Inner channel */}
            <line x1="50" y1="50" x2={v.x} y2={v.y}
              stroke="rgba(20,8,4,0.7)" strokeWidth="1.4" strokeLinecap="round" />
            {/* Steam-flow animation when this valve is currently active in playback */}
            {activeValve && activeValve.idx === v.idx && phase === 'playback' && (
              <line x1="50" y1="50" x2={v.x} y2={v.y}
                stroke={activeValve.lie ? 'rgba(255,90,80,0.85)' : 'rgba(255,210,120,0.95)'}
                strokeWidth="1.3" strokeLinecap="round"
                strokeDasharray="3 6"
                style={{ animation: 'f2-pipe-flow 0.6s linear infinite' }} />
            )}
          </g>
        ))}

        {/* === Central Boiler === */}
        <g style={{ animation: 'f2-boiler-breathe 3.6s ease-in-out infinite', transformOrigin: '50px 50px' }}>
          {/* Outer riveted ring */}
          <circle cx="50" cy="50" r="11.5" fill="url(#f2-boiler-grad)" />
          {/* Rivets */}
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            const rx = 50 + Math.cos(a) * 10.2;
            const ry = 50 + Math.sin(a) * 10.2;
            return <circle key={`r-${i}`} cx={rx} cy={ry} r="0.55" fill="#1a0c04" stroke="#5a3826" strokeWidth="0.15" />;
          })}
          {/* Furnace mouth */}
          <circle cx="50" cy="50" r="6.5" fill="url(#f2-boiler-core)" opacity="0.92" />
          <circle cx="50" cy="50" r="3.2" fill="#fff0c0" opacity="0.65" />
          <circle cx="50" cy="50" r="1.4" fill="#ffffff" opacity="0.9" />
          {/* Engraved sigil ring around boiler mouth */}
          <g fill="rgba(255,210,140,0.45)" fontFamily="'Cinzel Decorative', serif" fontSize="1.8" textAnchor="middle">
            {[0, 60, 120, 180, 240, 300].map(deg => {
              const rad = deg * Math.PI / 180;
              const x = 50 + 8.7 * Math.cos(rad);
              const y = 50 + 8.7 * Math.sin(rad) + 0.6;
              return <text key={`sig-${deg}`} x={x} y={y} transform={`rotate(${deg + 90} ${x} ${y})`}>✦</text>;
            })}
          </g>
        </g>

        {/* === Valves === */}
        {valvePositions.map(v => {
          const isActive   = activeValve && activeValve.idx === v.idx;
          const isLieFlash = isActive && activeValve.lie;
          const shake      = shakeValve && shakeValve.idx === v.idx;
          const shakeOk    = shake && shakeValve.success;
          const shakeBad   = shake && !shakeValve.success;
          return (
            <g key={`valve-${v.idx}`} style={{
              transformOrigin: `${v.x}px ${v.y}px`,
              animation: shakeBad
                ? 'f2-valve-fail 0.35s ease-in-out'
                : shakeOk
                  ? 'f2-valve-success 0.45s ease-in-out'
                  : isLieFlash
                    ? 'f2-valve-ignite-lie 0.55s ease-in-out infinite'
                    : isActive
                      ? 'f2-valve-ignite-true 0.55s ease-in-out infinite'
                      : 'none',
            }}>
              {/* Outer brass rim */}
              <circle cx={v.x} cy={v.y} r="6"
                fill={isLieFlash ? 'url(#f2-valve-lie)' : 'url(#f2-valve-brass)'}
                stroke={isActive ? (isLieFlash ? '#ff7060' : '#ffd070') : '#3a2014'}
                strokeWidth="0.45" />
              {/* Rivets on the rim */}
              {Array.from({ length: 8 }).map((_, i) => {
                const a = (i / 8) * Math.PI * 2;
                const rx = v.x + Math.cos(a) * 5.1;
                const ry = v.y + Math.sin(a) * 5.1;
                return <circle key={`vr-${v.idx}-${i}`} cx={rx} cy={ry} r="0.32" fill="#1a0c04" />;
              })}
              {/* Center pressure dial face */}
              <circle cx={v.x} cy={v.y} r="3.7"
                fill={isLieFlash ? '#2a0a06' : '#1a0c04'}
                stroke={isActive ? (isLieFlash ? '#ff8060' : '#ffc070') : '#5a3826'}
                strokeWidth="0.3" />
              {/* Dial marks */}
              {[-60, -30, 0, 30, 60].map(deg => {
                const a = (deg + 270) * Math.PI / 180;
                const rx1 = v.x + 2.8 * Math.cos(a);
                const ry1 = v.y + 2.8 * Math.sin(a);
                const rx2 = v.x + 3.4 * Math.cos(a);
                const ry2 = v.y + 3.4 * Math.sin(a);
                return <line key={`vmd-${v.idx}-${deg}`} x1={rx1} y1={ry1} x2={rx2} y2={ry2}
                  stroke="rgba(200,140,80,0.55)" strokeWidth="0.18" />;
              })}
              {/* Dial needle */}
              <line x1={v.x} y1={v.y}
                x2={v.x + 2.2 * Math.cos((isActive ? 30 : -45) * Math.PI / 180)}
                y2={v.y + 2.2 * Math.sin((isActive ? 30 : -45) * Math.PI / 180)}
                stroke={isLieFlash ? '#ff6050' : isActive ? '#ffe090' : '#a05028'}
                strokeWidth="0.45" strokeLinecap="round"
                style={{ transition: 'all 0.18s ease-out' }} />
              <circle cx={v.x} cy={v.y} r="0.45" fill={isLieFlash ? '#ff6050' : isActive ? '#ffe090' : '#5a3826'} />
              {/* Vent slots (radial slits on outer rim) */}
              {Array.from({ length: 4 }).map((_, i) => {
                const a = (i / 4) * Math.PI * 2 + Math.PI / 8;
                const rx = v.x + Math.cos(a) * 5.8;
                const ry = v.y + Math.sin(a) * 5.8;
                return <rect key={`vent-${v.idx}-${i}`}
                  x={rx - 0.18} y={ry - 0.55} width="0.36" height="1.1"
                  rx="0.18"
                  fill="#000"
                  transform={`rotate(${(a * 180) / Math.PI} ${rx} ${ry})`}
                  opacity="0.65" />;
              })}
            </g>
          );
        })}
      </svg>

      {/* === Valve tap zones (HTML for reliable hit-testing on mobile) === */}
      {valvePositions.map(v => (
        <button key={`tap-${v.idx}`}
          onClick={() => handleValveTap(v.idx)}
          disabled={phase !== 'input'}
          data-testid={`f2-valve-${v.idx}`}
          aria-label={`Valve ${v.idx + 1}`}
          style={{
            position: 'absolute',
            left: `calc(50% + (${v.x - 50}) * min(95vmin, 100%) / 100)`,
            top:  `calc(50% + (${v.y - 50}) * min(95vmin, 100%) / 100)`,
            transform: 'translate(-50%,-50%)',
            width: 'min(12vmin, 88px)', height: 'min(12vmin, 88px)',
            borderRadius: '50%',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: phase === 'input' ? 'pointer' : 'default',
            zIndex: 20,
          }} />
      ))}

      {/* === Steam puffs above active valve === */}
      {activeValve && phase === 'playback' && (() => {
        const v = valvePositions[activeValve.idx];
        return (
          <div className="absolute pointer-events-none z-15" style={{
            left: `calc(50% + (${v.x - 50}) * min(95vmin, 100%) / 100)`,
            top:  `calc(50% + (${v.y - 50}) * min(95vmin, 100%) / 100)`,
            width: 24, height: 24, transform: 'translate(-50%, -120%)',
          }}>
            {[0, 1, 2].map(i => (
              <div key={`steam-${i}`} className="absolute left-1/2 rounded-full" style={{
                width: 12 + i * 3, height: 12 + i * 3,
                background: activeValve.lie
                  ? 'radial-gradient(circle, rgba(255,120,100,0.45) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(240,210,170,0.5) 0%, transparent 70%)',
                animation: `f2-steam-rise ${0.9 + i * 0.15}s ease-out ${i * 0.08}s infinite`,
                filter: 'blur(2px)',
              }} />
            ))}
          </div>
        );
      })()}

      {/* === HUD (top bar) === */}
      <div className="absolute top-0 left-0 right-0 z-40 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.78) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[9px] tracking-[0.42em] font-mono uppercase" style={{ color: 'rgba(255,180,120,0.5)' }}>
              Memoria Ferrea · Round {round}/{TOTAL_ROUNDS}
            </span>
            <p className="text-[8px] tracking-[0.22em] font-mono mt-1 uppercase" style={{
              color: phase === 'playback' ? '#ffd070' : phase === 'input' ? '#ff9050' : 'rgba(255,255,255,0.32)',
            }}>
              {phase === 'playback' && '◐ The valves cry — and one valve lies'}
              {phase === 'input' && `◉ Repeat the truth (${inputIdx}/${truthLen})`}
              {phase === 'roundwin' && '✓ Truth honoured'}
              {phase === 'wipe' && '✗ The boiler ruptures'}
              {phase === 'intro' && '◯ Listen…'}
            </p>
          </div>

          {/* Strike indicators */}
          <div className="flex items-center gap-1.5">
            {Array.from({ length: MAX_STRIKES }).map((_, i) => {
              const lost = i < strikes;
              return (
                <div key={`strike-${i}`} className="w-2.5 h-2.5 rounded-full transition-all duration-300" style={{
                  background: lost ? '#5a1a08' : '#ffaa50',
                  border: `1px solid ${lost ? 'rgba(120,40,20,0.6)' : 'rgba(255,180,90,0.85)'}`,
                  boxShadow: lost ? 'none' : '0 0 6px rgba(255,180,80,0.7)',
                  opacity: lost ? 0.4 : 1,
                }} />
              );
            })}
          </div>

          {/* Pressure gauge */}
          <div className="text-right">
            <span className="text-[7px] font-mono tracking-[0.25em] uppercase" style={{ color: 'rgba(255,180,120,0.4)' }}>Pressure</span>
            <div className="w-20 h-1.5 mt-1 rounded-sm overflow-hidden" style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,180,120,0.18)',
            }}>
              <div className="h-full transition-all duration-200" style={{
                width: `${pressure}%`,
                background: pressure > 70
                  ? 'linear-gradient(90deg, #ffaa50 0%, #ff5040 100%)'
                  : 'linear-gradient(90deg, #ffaa50 0%, #ffd080 100%)',
                boxShadow: pressure > 70 ? '0 0 6px rgba(255,80,60,0.7)' : '0 0 4px rgba(255,180,80,0.5)',
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* === Bottom prompt === */}
      <div className="absolute bottom-3 left-0 right-0 z-40 text-center">
        <span className="text-[8px] font-mono tracking-[0.32em] uppercase" style={{ color: 'rgba(255,180,120,0.32)' }}>
          {showHint
            ? 'A faint crimson tint marks the lie — skip it'
            : 'Watch the sequence · skip the lie · repeat the rest in order'}
        </span>
      </div>

      {/* === Strike screen flash === */}
      {shakeValve && !shakeValve.success && (
        <div className="absolute inset-0 z-30 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(255,0,40,0.18) 80%, rgba(255,0,40,0.4) 100%)',
          animation: 'f2-strike-flash 0.4s ease-out forwards',
        }} />
      )}

      {/* === Round-win bloom + text === */}
      {phase === 'roundwin' && (
        <>
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-40 rounded-full" style={{
            width: '12vmin', height: '12vmin',
            background: 'radial-gradient(circle, rgba(150,255,180,0.55) 0%, rgba(80,220,140,0.18) 50%, transparent 80%)',
            animation: 'f2-roundwin-bloom 1.1s ease-out forwards',
          }} />
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-50" style={{
            transform: 'translate(-50%,-50%)',
            color: '#d8ffd0',
            fontFamily: "'Cinzel Decorative', serif",
            fontSize: 'clamp(28px, 5vmin, 56px)',
            fontWeight: 900,
            letterSpacing: '0.32em',
            textShadow: '0 0 16px rgba(150,255,180,0.85), 0 0 36px rgba(80,220,140,0.55)',
            whiteSpace: 'nowrap',
            animation: 'f2-roundwin-text 1.1s ease-in-out forwards',
          }}>
            {round >= TOTAL_ROUNDS ? 'IRON KEPT' : `ROUND ${round}`}
          </div>
        </>
      )}

      {/* === Late-pressure danger vignette === */}
      {pressure > 70 && phase === 'input' && (
        <div className="absolute inset-0 z-5 pointer-events-none transition-opacity duration-500" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(255,80,40,0.08) 75%, rgba(255,80,40,0.18) 100%)',
          opacity: 1,
        }} />
      )}
    </div>
  );
}


// Floor 3: Laser Grid — "THE GRID CUTS"
// Lore: "The chamber is an ornate cathedral lattice. Each cell judges its
//        occupant. The grid cuts — but only along its lines."
//
// The full screen is an 8×5 stone lattice. The wisp occupies one cell at a
// time. Every ~1.4s a row OR a column ignites in orange warning, then a
// crimson laser lances through it. Move out before it fires — or be cut.
// 75 seconds survival. Late game: 2 simultaneous warnings (row + column).
// Input: WASD / Arrow keys to step, click any cell to teleport.
function Floor3({ onComplete, onFail }) {
  const GRID_COLS = 8;
  const GRID_ROWS = 5;
  const FLOOR_DURATION = 75;

  // ─── State ──────────────────────────────────────────────────────────────
  const [wispCol, setWispCol]     = useState(0);
  const [wispRow, setWispRow]     = useState(2);
  const [timeLeft, setTimeLeft]   = useState(FLOOR_DURATION);
  const [phase, setPhase]         = useState('opening'); // 'opening' | 'play' | 'death' | 'win'
  const [cellsJudged, setCellsJudged] = useState(0);
  const [, force]                 = useState(0);

  // ─── Refs (tight loop) ──────────────────────────────────────────────────
  const wispRef        = useRef({ col: 0, row: 2 });
  const warningsRef    = useRef([]);
  const strikesRef     = useRef([]);
  const warningIdRef   = useRef(0);
  const lastSpawnRef   = useRef(0);
  const startedAtRef   = useRef(0);
  const failedRef      = useRef(false);
  const completedRef   = useRef(false);
  const rafRef         = useRef(null);
  const judgedCountRef = useRef(0);

  // ─── Cinematic opening (1.2s) ───────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      if (failedRef.current || completedRef.current) return;
      setPhase('play');
      startedAtRef.current = performance.now();
      lastSpawnRef.current = performance.now() + 800; // first warning ~800ms in
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  // ─── Difficulty curve ───────────────────────────────────────────────────
  const computeDifficulty = useCallback((elapsedSec) => {
    const t = Math.min(1, elapsedSec / FLOOR_DURATION);
    return {
      spawnIntervalMs: Math.round(1400 - t * 600),  // 1400 → 800
      telegraphMs:     Math.round(700  - t * 300),  // 700  → 400
      maxConcurrent:   elapsedSec < 20 ? 1 : 2,
    };
  }, []);

  // ─── Spawn warning ──────────────────────────────────────────────────────
  const spawnWarning = useCallback((now, telegraphMs) => {
    // Try to avoid spawning a warning that conflicts with an already-firing strike
    const activeWarnings = warningsRef.current;
    const isRow = Math.random() < 0.5;
    const max = isRow ? GRID_ROWS : GRID_COLS;
    // Try to pick an index not already warned
    let candidate = Math.floor(Math.random() * max);
    for (let i = 0; i < max; i++) {
      const exists = activeWarnings.some(w => w.type === (isRow ? 'row' : 'col') && w.index === candidate);
      if (!exists) break;
      candidate = (candidate + 1) % max;
    }
    warningsRef.current.push({
      id: warningIdRef.current++,
      type: isRow ? 'row' : 'col',
      index: candidate,
      warnedAt: now,
      fireAt: now + telegraphMs,
      firedAt: null,
    });
  }, []);

  // ─── Game loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'play') return;

    const STRIKE_VISIBLE_MS = 280;
    const loop = () => {
      const now = performance.now();
      const elapsedSec = (now - startedAtRef.current) / 1000;
      const diff = computeDifficulty(elapsedSec);

      // Spawn new warnings
      if (now - lastSpawnRef.current >= diff.spawnIntervalMs && warningsRef.current.length < diff.maxConcurrent) {
        spawnWarning(now, diff.telegraphMs);
        lastSpawnRef.current = now;
      }

      // Resolve warnings → strikes
      warningsRef.current = warningsRef.current.filter(w => {
        if (!w.firedAt && now >= w.fireAt) {
          w.firedAt = now;
          // Check collision with wisp
          const hit =
            (w.type === 'row' && wispRef.current.row === w.index) ||
            (w.type === 'col' && wispRef.current.col === w.index);
          strikesRef.current.push({
            id: w.id,
            type: w.type,
            index: w.index,
            firedAt: now,
            hit,
          });
          if (hit && !failedRef.current) {
            failedRef.current = true;
            setPhase('death');
            setTimeout(onFail, 1100);
            return false;
          } else {
            judgedCountRef.current += 1;
            setCellsJudged(judgedCountRef.current);
          }
          return false; // warning consumed
        }
        return true; // still in telegraph window
      });

      // Prune old strikes after their visible window
      strikesRef.current = strikesRef.current.filter(s => now - s.firedAt < STRIKE_VISIBLE_MS);

      force(v => (v + 1) & 0xffff);

      if (!failedRef.current && !completedRef.current) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, computeDifficulty, spawnWarning, onFail]);

  // ─── Countdown ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'play') return;
    const t = setInterval(() => {
      setTimeLeft(s => {
        if (s <= 1) {
          clearInterval(t);
          if (!failedRef.current && !completedRef.current) {
            completedRef.current = true;
            setPhase('win');
            setTimeout(onComplete, 1300);
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [phase, onComplete]);

  // ─── Input ──────────────────────────────────────────────────────────────
  const moveWisp = useCallback((dCol, dRow) => {
    if (failedRef.current || completedRef.current || phase !== 'play') return;
    const nextCol = Math.max(0, Math.min(GRID_COLS - 1, wispRef.current.col + dCol));
    const nextRow = Math.max(0, Math.min(GRID_ROWS - 1, wispRef.current.row + dRow));
    wispRef.current = { col: nextCol, row: nextRow };
    setWispCol(nextCol);
    setWispRow(nextRow);
  }, [phase]);

  const teleportWisp = (col, row) => {
    if (failedRef.current || completedRef.current || phase !== 'play') return;
    wispRef.current = { col, row };
    setWispCol(col);
    setWispRow(row);
  };

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      if (k === 'ArrowUp' || k === 'w' || k === 'W')    { moveWisp(0, -1); e.preventDefault(); }
      else if (k === 'ArrowDown' || k === 's' || k === 'S')  { moveWisp(0, 1);  e.preventDefault(); }
      else if (k === 'ArrowLeft' || k === 'a' || k === 'A')  { moveWisp(-1, 0); e.preventDefault(); }
      else if (k === 'ArrowRight' || k === 'd' || k === 'D') { moveWisp(1, 0);  e.preventDefault(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moveWisp]);

  // ─── Render helpers ─────────────────────────────────────────────────────
  const isWarned = (col, row) => warningsRef.current.some(w =>
    (w.type === 'row' && w.index === row) || (w.type === 'col' && w.index === col)
  );

  return (
    <div
      className="relative h-full w-full overflow-hidden select-none"
      data-testid="floor-3"
      style={{
        background: '#040201', touchAction: 'none',
        animation: phase === 'death' ? 'f3-death-shake 0.16s steps(3) 5' : 'none',
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Cinzel+Decorative:wght@700;900&display=swap');
        @keyframes f3-tile-idle { 0%,100% { opacity: 0.85; } 50% { opacity: 1; } }
        @keyframes f3-tile-warn {
          0%   { background: rgba(40,15,8,0.55); }
          15%  { background: rgba(180,60,20,0.55); box-shadow: inset 0 0 24px rgba(255,120,40,0.65); }
          100% { background: rgba(220,90,30,0.62); box-shadow: inset 0 0 30px rgba(255,140,60,0.85); }
        }
        @keyframes f3-warn-sigil {
          0%,100% { opacity: 0.3; transform: scale(0.9); }
          50%     { opacity: 0.95; transform: scale(1.1); }
        }
        @keyframes f3-strike-flash {
          0%   { opacity: 0; transform: scale(0.6); }
          12%  { opacity: 1; transform: scale(1.15); }
          100% { opacity: 0; transform: scale(1); }
        }
        @keyframes f3-wisp-pulse {
          0%,100% { box-shadow: 0 0 14px #ffd070, 0 0 28px rgba(255,180,80,0.7), 0 0 60px rgba(255,140,40,0.35); }
          50%     { box-shadow: 0 0 22px #fff0c0, 0 0 42px rgba(255,220,140,0.95), 0 0 90px rgba(255,170,80,0.55); }
        }
        @keyframes f3-opening-bloom {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.2); }
          60%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%,-50%) scale(8); }
        }
        @keyframes f3-death-shake {
          0%,100% { transform: translate(0,0); }
          25%     { transform: translate(-5px,2px); }
          50%     { transform: translate(5px,-2px); }
          75%     { transform: translate(-3px,3px); }
        }
        @keyframes f3-win-radiance {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.4); }
          100% { opacity: 1; transform: translate(-50%,-50%) scale(3); }
        }
        @keyframes f3-win-text {
          0%   { opacity: 0; transform: translate(-50%,-50%) translateY(20px); letter-spacing: 0.7em; filter: blur(6px); }
          25%  { opacity: 1; transform: translate(-50%,-50%) translateY(0);    letter-spacing: 0.32em; filter: blur(0); }
          75%  { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%,-50%) translateY(-12px); letter-spacing: 0.5em; filter: blur(3px); }
        }
        @keyframes f3-grid-pulse {
          0%,100% { opacity: 0.55; }
          50%     { opacity: 0.85; }
        }
      `}</style>

      {/* === DEEP CHAMBER BACKDROP === */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, rgba(50,8,8,0.45) 0%, rgba(18,4,4,0.85) 35%, rgba(6,2,2,0.96) 70%, #020101 100%)',
      }} />

      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 50%, transparent 35%, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0.85) 100%)',
      }} />

      {/* === CATHEDRAL LATTICE GRID === */}
      <div className="absolute inset-0 z-10 p-3 sm:p-6"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
          gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
          gap: 'min(0.6vmin, 6px)',
        }}>
        {Array.from({ length: GRID_ROWS }).flatMap((_, row) =>
          Array.from({ length: GRID_COLS }).map((_, col) => {
            const isWisp = wispCol === col && wispRow === row;
            const warned = isWarned(col, row);
            return (
              <button key={`cell-${col}-${row}`}
                onClick={() => teleportWisp(col, row)}
                data-testid={`f3-cell-${col}-${row}`}
                aria-label={`Cell ${col + 1}-${row + 1}`}
                style={{
                  position: 'relative',
                  padding: 0,
                  background: warned
                    ? 'linear-gradient(135deg, rgba(220,90,30,0.55) 0%, rgba(180,40,15,0.45) 100%)'
                    : 'linear-gradient(135deg, rgba(34,18,12,0.85) 0%, rgba(20,10,6,0.95) 100%)',
                  border: warned
                    ? '1px solid rgba(255,140,60,0.85)'
                    : isWisp
                      ? '1.5px solid rgba(255,210,120,0.85)'
                      : '1px solid rgba(80,40,20,0.45)',
                  borderRadius: 3,
                  boxShadow: warned
                    ? 'inset 0 0 28px rgba(255,120,40,0.65), 0 0 12px rgba(255,80,30,0.45)'
                    : isWisp
                      ? 'inset 0 0 20px rgba(255,210,120,0.35), 0 0 18px rgba(255,180,80,0.45)'
                      : 'inset 0 0 12px rgba(0,0,0,0.55)',
                  cursor: phase === 'play' ? 'pointer' : 'default',
                  transition: 'border-color 0.12s, box-shadow 0.18s',
                  overflow: 'hidden',
                }}>
                {/* Corner rivets */}
                {!warned && (
                  <>
                    <span style={{ position: 'absolute', top: 3, left: 3, width: 3, height: 3, borderRadius: '50%', background: '#0a0604', border: '0.5px solid #3a2014' }} />
                    <span style={{ position: 'absolute', top: 3, right: 3, width: 3, height: 3, borderRadius: '50%', background: '#0a0604', border: '0.5px solid #3a2014' }} />
                    <span style={{ position: 'absolute', bottom: 3, left: 3, width: 3, height: 3, borderRadius: '50%', background: '#0a0604', border: '0.5px solid #3a2014' }} />
                    <span style={{ position: 'absolute', bottom: 3, right: 3, width: 3, height: 3, borderRadius: '50%', background: '#0a0604', border: '0.5px solid #3a2014' }} />
                  </>
                )}

                {/* Etched cross — center decoration */}
                {!warned && !isWisp && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(120,60,30,0.18)',
                    fontFamily: "'Cinzel Decorative', serif",
                    fontSize: 'clamp(10px, 2.2vmin, 22px)',
                    pointerEvents: 'none',
                  }}>✦</div>
                )}

                {/* Warning sigil — when this cell's row/col is warned */}
                {warned && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#ffeac0',
                    fontFamily: "'Cinzel Decorative', serif",
                    fontSize: 'clamp(11px, 2.6vmin, 26px)',
                    fontWeight: 900,
                    animation: 'f3-warn-sigil 0.65s ease-in-out infinite',
                    textShadow: '0 0 8px #ff8030, 0 0 16px rgba(255,100,30,0.6)',
                    pointerEvents: 'none',
                  }}>☩</div>
                )}

                {/* Wisp inside its cell */}
                {isWisp && phase !== 'death' && (
                  <div style={{
                    position: 'absolute', left: '50%', top: '50%',
                    transform: 'translate(-50%,-50%)',
                    width: 'clamp(14px, 3vmin, 30px)',
                    height: 'clamp(14px, 3vmin, 30px)',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, #fff8e0 0%, #ffd070 35%, #ff8030 70%, transparent 100%)',
                    animation: 'f3-wisp-pulse 1.1s ease-in-out infinite',
                    pointerEvents: 'none',
                  }} />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* === STRIKE LINES (full-row/full-column crimson lances) === */}
      <div className="absolute inset-0 z-20 pointer-events-none p-3 sm:p-6">
        <div style={{
          width: '100%', height: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
          gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
          gap: 'min(0.6vmin, 6px)',
        }}>
          {/* Empty grid cells just to establish the same layout */}
          {Array.from({ length: GRID_COLS * GRID_ROWS }).map((_, i) => (
            <div key={`spacer-${i}`} />
          ))}
        </div>
        {strikesRef.current.map(s => {
          const isRow = s.type === 'row';
          // Compute row/column position as % of container (accounting for padding consistency)
          const rowH = 100 / GRID_ROWS;
          const colW = 100 / GRID_COLS;
          return (
            <div key={`strike-${s.id}`}
              style={{
                position: 'absolute',
                left: isRow ? '1.5%' : `${s.index * colW + colW * 0.05}%`,
                top:  isRow ? `${s.index * rowH + rowH * 0.05}%` : '1.5%',
                width:  isRow ? '97%' : `${colW * 0.9}%`,
                height: isRow ? `${rowH * 0.9}%` : '97%',
                background: 'linear-gradient(' + (isRow ? '90deg' : '180deg') + ', rgba(255,40,60,0.05) 0%, rgba(255,80,80,0.95) 15%, rgba(255,100,100,0.98) 50%, rgba(255,80,80,0.95) 85%, rgba(255,40,60,0.05) 100%)',
                boxShadow: '0 0 28px rgba(255,40,60,0.75), 0 0 60px rgba(255,40,60,0.4), inset 0 0 40px rgba(255,200,200,0.4)',
                mixBlendMode: 'screen',
                animation: 'f3-strike-flash 0.28s ease-out forwards',
                filter: 'blur(1px)',
                borderRadius: 2,
              }} />
          );
        })}
      </div>

      {/* === OPENING BLOOM === */}
      {phase === 'opening' && (
        <div className="absolute left-1/2 top-1/2 z-30 pointer-events-none rounded-full" style={{
          width: '12vmin', height: '12vmin',
          background: 'radial-gradient(circle, rgba(255,140,60,0.6) 0%, rgba(220,40,40,0.22) 40%, transparent 75%)',
          animation: 'f3-opening-bloom 1.2s ease-out forwards',
        }} />
      )}

      {/* === HUD === */}
      <div className="absolute top-0 left-0 right-0 z-50 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.78) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[9px] tracking-[0.42em] font-mono uppercase" style={{ color: 'rgba(255,140,80,0.5)' }}>
              Iron Lattice
            </span>
            <p className="font-mono mt-1 text-[12px]" style={{ color: '#ffaa70' }}>
              <span className="font-black text-[14px]">{cellsJudged}</span>
              <span className="text-white/30 text-[10px]"> verdicts survived</span>
            </p>
          </div>

          <div className="text-right">
            <span className="text-[8px] font-mono tracking-[0.3em] uppercase" style={{ color: timeLeft <= 12 ? '#ff5060' : 'rgba(255,140,80,0.4)' }}>
              {timeLeft <= 12 ? 'final verdict' : 'the clock'}
            </span>
            <div className="font-mono text-2xl font-black mt-0.5" style={{
              color: timeLeft <= 12 ? '#ff5060' : '#ffaa70',
              textShadow: `0 0 12px ${timeLeft <= 12 ? 'rgba(255,80,96,0.7)' : 'rgba(255,140,80,0.4)'}`,
            }}>{timeLeft}</div>
          </div>
        </div>
      </div>

      {/* === BOTTOM PROMPT === */}
      <div className="absolute bottom-3 left-0 right-0 z-50 text-center">
        <span className="text-[8px] font-mono tracking-[0.32em] uppercase" style={{ color: 'rgba(255,140,80,0.35)' }}>
          WASD / Arrows to move · click any cell to leap · the lit row or column will be cut
        </span>
      </div>

      {/* === WIN BLOOM + TEXT === */}
      {phase === 'win' && (
        <>
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-40 rounded-full" style={{
            width: '14vmin', height: '14vmin',
            background: 'radial-gradient(circle, rgba(150,255,180,0.55) 0%, rgba(80,220,140,0.18) 50%, transparent 80%)',
            animation: 'f3-win-radiance 1.2s ease-out forwards',
          }} />
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-50" style={{
            transform: 'translate(-50%,-50%)',
            color: '#d8ffd0',
            fontFamily: "'Cinzel Decorative', serif",
            fontSize: 'clamp(28px, 5vmin, 56px)',
            fontWeight: 900,
            letterSpacing: '0.32em',
            textShadow: '0 0 16px rgba(150,255,180,0.85), 0 0 36px rgba(80,220,140,0.55)',
            whiteSpace: 'nowrap',
            animation: 'f3-win-text 1.3s ease-in-out forwards',
          }}>LATTICE PASSED</div>
        </>
      )}

      {/* === DEATH SCREEN-FLASH === */}
      {phase === 'death' && (
        <div className="absolute inset-0 z-40 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(255,0,40,0.55) 0%, rgba(180,0,30,0.35) 40%, transparent 80%)',
          animation: 'f3-strike-flash 0.5s ease-out forwards',
          mixBlendMode: 'screen',
        }} />
      )}
    </div>
  );
}


// Floor 4: Rat King's Den — AAA dark nest whack-a-mole with combos, decoys, danger
function Floor4({ onComplete, onFail }) {
  const [score, setScore] = useState(0);
  const [rats, setRats] = useState([]);
  const [combo, setCombo] = useState(0);
  const [timeLeft, setTimeLeft] = useState(18);
  const [flash, setFlash] = useState(null);
  const [hitFx, setHitFx] = useState(null);
  const needed = 18;
  const failedRef = useRef(false);
  const scoreRef = useRef(0);
  const spawnTimerRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(t => { if (t <= 1) { clearInterval(timer); return 0; } return t - 1; });
    }, 1000);
    const spawnRat = () => {
      const roll = Math.random();
      let type = 'normal';
      if (roll < 0.2) type = 'red';
      else if (roll < 0.28) type = 'golden';
      else if (roll < 0.38) type = 'decoy';
      const id = Date.now() + Math.random();
      const rat = { id, type, x: 5 + Math.random() * 82, y: 12 + Math.random() * 75, alive: true };
      const lifespan = Math.max(650, 1300 - scoreRef.current * 30);
      setRats(prev => [...prev, rat]);
      setTimeout(() => setRats(prev => prev.filter(r => r.id !== id)), lifespan);
      const nextDelay = Math.max(280, 650 - scoreRef.current * 16);
      spawnTimerRef.current = setTimeout(spawnRat, nextDelay);
    };
    spawnTimerRef.current = setTimeout(spawnRat, 500);
    return () => { clearInterval(timer); clearTimeout(spawnTimerRef.current); };
  }, []);

  useEffect(() => {
    if (scoreRef.current >= needed && !failedRef.current) { failedRef.current = true; onComplete(); }
    if (timeLeft === 0 && scoreRef.current < needed && !failedRef.current) { failedRef.current = true; onFail(); }
  }, [timeLeft, score, onComplete, onFail]);

  const tap = (rat) => {
    if (!rat.alive) return;
    if (rat.type === 'red') { onFail(); return; }
    if (rat.type === 'decoy') {
      setCombo(0); setFlash('bad');
      setTimeout(() => setFlash(null), 300);
      setRats(prev => prev.map(r => r.id === rat.id ? { ...r, alive: false } : r));
      return;
    }
    const pts = rat.type === 'golden' ? 3 : 1;
    scoreRef.current += pts;
    setScore(scoreRef.current);
    setCombo(c => c + 1);
    setHitFx({ x: rat.x, y: rat.y, pts, id: Date.now() });
    setTimeout(() => setHitFx(null), 600);
    setFlash('good');
    setTimeout(() => setFlash(null), 200);
    setRats(prev => prev.map(r => r.id === rat.id ? { ...r, alive: false } : r));
  };

  const progress = score / needed;

  return (
    <div className="relative h-full w-full overflow-hidden" data-testid="floor-4"
      style={{ background: '#080400' }}>
      <style>{`
        @keyframes f4-emerge { 0% { transform: scale(0) rotate(-15deg); opacity: 0; } 40% { transform: scale(1.15) rotate(3deg); opacity: 1; } 100% { transform: scale(1) rotate(0deg); opacity: 1; } }
        @keyframes f4-eye-pulse { 0%, 100% { opacity: 0.7; box-shadow: 0 0 6px currentColor; } 50% { opacity: 1; box-shadow: 0 0 14px currentColor, 0 0 28px currentColor; } }
        @keyframes f4-score-pop { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-40px) scale(1.3); opacity: 0; } }
        @keyframes f4-scurry { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-2px); } 75% { transform: translateX(2px); } }
        @keyframes f4-crown-breathe { 0%, 100% { opacity: 0.14; transform: translate(-50%, -50%) scale(1); } 50% { opacity: 0.22; transform: translate(-50%, -50%) scale(1.04); } }
        @keyframes f4-scurry-right {
          0% { transform: translateX(0) scaleX(-1); }
          45% { transform: translateX(60vw) scaleX(-1); }
          50% { transform: translateX(60vw) scaleX(-1); opacity: 0; }
          51% { transform: translateX(0) scaleX(-1); opacity: 0; }
          55% { opacity: 0.22; }
          100% { transform: translateX(120vw) scaleX(-1); }
        }
        @keyframes f4-scurry-left {
          0% { transform: translateX(0) scaleX(1); }
          45% { transform: translateX(-60vw) scaleX(1); }
          50% { transform: translateX(-60vw) scaleX(1); opacity: 0; }
          51% { transform: translateX(0) scaleX(1); opacity: 0; }
          55% { opacity: 0.22; }
          100% { transform: translateX(-120vw) scaleX(1); }
        }
      `}</style>

      {/* Nest atmosphere */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 70%, rgba(40,25,0,0.25) 0%, rgba(8,4,0,0.9) 60%, #050200 100%)' }} />

      {/* Organic nest texture */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        background: 'repeating-conic-gradient(rgba(245,166,35,0.3) 0% 1%, transparent 1% 10%) 50% 50% / 60px 60px',
      }} />

      {/* Rat King's Crown — centered ghostly key prop, faded behind action */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
        style={{
          width: '46%', maxWidth: '480px', aspectRatio: '1/1',
          backgroundImage: `url(${floor4Crown})`,
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
          opacity: 0.18,
          filter: 'sepia(0.4) hue-rotate(-10deg) saturate(1.2) brightness(0.9) drop-shadow(0 0 30px rgba(245,166,35,0.25))',
          mixBlendMode: 'screen',
          animation: 'f4-crown-breathe 6s ease-in-out infinite',
        }} />

      {/* Ambient rat silhouettes — scurrying in the background */}
      {Array.from({ length: 6 }).map((_, i) => {
        const fromLeft = i % 2 === 0;
        const dur = 6 + (i * 1.3) % 4;
        const delay = -((i * 1.7) % 6);
        const top = 18 + (i * 13) % 65;
        const scale = 0.35 + (i % 3) * 0.12;
        const flipX = fromLeft ? -1 : 1;
        return (
          <img key={`ratsil-${i}`} src={floor4Rat} alt=""
            className="absolute pointer-events-none z-[1] select-none"
            style={{
              top: `${top}%`,
              left: fromLeft ? '-12%' : 'auto',
              right: fromLeft ? 'auto' : '-12%',
              width: `${60 * scale}px`,
              opacity: 0.22,
              filter: 'brightness(0.55) contrast(1.1) drop-shadow(0 0 6px rgba(245,166,35,0.18))',
              mixBlendMode: 'screen',
              transform: `scaleX(${flipX})`,
              animation: `${fromLeft ? 'f4-scurry-right' : 'f4-scurry-left'} ${dur}s linear ${delay}s infinite`,
            }} />
        );
      })}

      {/* Faint dust motes (kept, reduced) */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={`mote-${i}`} className="absolute w-[2px] h-[2px] rounded-full pointer-events-none z-[2]"
          style={{ left: `${20 + i * 28}%`, top: `${22 + (i % 2) * 30}%`,
            background: 'rgba(245,166,35,0.12)', boxShadow: '0 0 4px rgba(245,166,35,0.08)',
            animation: `f4-scurry ${2 + i * 0.5}s ease-in-out infinite ${i * 0.3}s`,
          }} />
      ))}

      {/* Screen flash */}
      {flash && (
        <div className="absolute inset-0 z-50 pointer-events-none transition-opacity duration-150"
          style={{ background: flash === 'good' ? 'rgba(245,166,35,0.06)' : 'rgba(255,0,0,0.12)' }} />
      )}

      {/* HUD */}
      <div className="absolute top-0 left-0 right-0 z-40 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[9px] tracking-[0.4em] font-mono uppercase" style={{ color: '#f5a62340' }}>Rat King's Den</span>
            <div className="flex items-center gap-3 mt-1">
              <span className="font-mono text-base font-black" style={{ color: '#f5a623', textShadow: '0 0 8px rgba(245,166,35,0.3)' }}>
                {score}<span className="text-white/15 font-normal">/{needed}</span>
              </span>
              {combo > 2 && (
                <motion.span
                  key={combo}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-xs font-mono font-black tracking-wide"
                  style={{ color: '#ffcc00', textShadow: '0 0 8px rgba(255,204,0,0.5)' }}>
                  x{combo}
                </motion.span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Progress bar */}
            <div className="w-20 h-1.5 rounded-sm overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(245,166,35,0.08)' }}>
              <div className="h-full rounded-sm transition-all duration-300" style={{
                width: `${progress * 100}%`,
                background: 'linear-gradient(90deg, #8b4513, #f5a623, #ffcc00)',
                boxShadow: '0 0 6px rgba(245,166,35,0.4)',
              }} />
            </div>
            <span className="font-mono text-lg font-black" style={{
              color: timeLeft <= 5 ? '#ff4040' : '#f5a623',
              textShadow: `0 0 8px rgba(${timeLeft <= 5 ? '255,0,0' : '245,166,35'},0.4)`,
            }}>{timeLeft}</span>
          </div>
        </div>
      </div>

      {/* Score pop-up effect */}
      {hitFx && (
        <div className="absolute z-45 pointer-events-none"
          style={{ left: `${hitFx.x}%`, top: `${hitFx.y - 5}%`,
            animation: 'f4-score-pop 0.6s ease-out forwards' }}>
          <span className="font-mono text-sm font-black" style={{
            color: hitFx.pts >= 3 ? '#ffcc00' : '#f5a623',
            textShadow: `0 0 8px ${hitFx.pts >= 3 ? 'rgba(255,204,0,0.6)' : 'rgba(245,166,35,0.4)'}`,
          }}>+{hitFx.pts}</span>
        </div>
      )}

      {/* Rat targets */}
      {rats.filter(r => r.alive).map(r => {
        const isRed = r.type === 'red';
        const isGold = r.type === 'golden';
        const isDecoy = r.type === 'decoy';
        const baseColor = isRed ? '#ff0000' : isGold ? '#ffcc00' : isDecoy ? '#666655' : '#f5a623';
        const size = isGold ? 64 : isRed ? 56 : 52;

        return (
          <motion.button key={r.id}
            onClick={() => tap(r)}
            initial={{ scale: 0, rotate: -15 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', damping: 15, stiffness: 400 }}
            className="absolute z-20 cursor-pointer"
            style={{ left: `calc(${r.x}% - ${size/2}px)`, top: `calc(${r.y}% - ${size/2}px)`,
              width: `${size}px`, height: `${size}px` }}>

            {/* Outer danger glow for red rats */}
            {isRed && (
              <div className="absolute inset-[-8px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(255,0,0,0.12) 0%, transparent 70%)',
                  animation: 'f4-eye-pulse 0.8s ease-in-out infinite', color: '#ff0000' }} />
            )}

            {/* Gold aura */}
            {isGold && (
              <div className="absolute inset-[-6px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(255,200,0,0.15) 0%, transparent 70%)',
                  boxShadow: '0 0 20px rgba(255,200,0,0.15)' }} />
            )}

            {/* Body — dark creature silhouette */}
            <div className="w-full h-full rounded-full flex items-center justify-center relative"
              style={{
                background: isRed
                  ? 'radial-gradient(circle at 45% 40%, rgba(60,0,0,0.9) 0%, rgba(20,0,0,0.95) 100%)'
                  : isGold
                  ? 'radial-gradient(circle at 45% 40%, rgba(50,40,0,0.8) 0%, rgba(15,12,0,0.95) 100%)'
                  : isDecoy
                  ? 'radial-gradient(circle at 45% 40%, rgba(30,30,25,0.8) 0%, rgba(10,10,8,0.95) 100%)'
                  : 'radial-gradient(circle at 45% 40%, rgba(45,30,0,0.8) 0%, rgba(12,8,0,0.95) 100%)',
                border: `2px solid ${baseColor}${isRed ? '40' : isGold ? '35' : '18'}`,
                boxShadow: isRed
                  ? '0 0 20px rgba(255,0,0,0.3), inset 0 0 10px rgba(255,0,0,0.1)'
                  : isGold
                  ? '0 0 20px rgba(255,200,0,0.25), inset 0 0 10px rgba(255,200,0,0.08)'
                  : `0 0 8px ${baseColor}15, inset 0 2px 6px rgba(0,0,0,0.3)`,
                animation: !isRed ? 'f4-scurry 0.3s ease-in-out infinite' : undefined,
              }}>

              {/* Eyes */}
              <div className="flex gap-2 relative" style={{ marginTop: '-4px' }}>
                <div className="w-2 h-2 rounded-full" style={{
                  background: baseColor,
                  boxShadow: `0 0 ${isRed ? 10 : isGold ? 8 : 4}px ${baseColor}`,
                  animation: isRed ? 'f4-eye-pulse 0.5s ease-in-out infinite' : undefined,
                }} />
                <div className="w-2 h-2 rounded-full" style={{
                  background: baseColor,
                  boxShadow: `0 0 ${isRed ? 10 : isGold ? 8 : 4}px ${baseColor}`,
                  animation: isRed ? 'f4-eye-pulse 0.5s ease-in-out infinite 0.1s' : undefined,
                }} />
              </div>

              {/* Golden crown indicator */}
              {isGold && (
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-[8px]"
                  style={{ color: '#ffcc00', textShadow: '0 0 6px rgba(255,204,0,0.6)' }}>
                  ★
                </div>
              )}
            </div>
          </motion.button>
        );
      })}

      {/* Bottom legend */}
      <div className="absolute bottom-3 left-0 right-0 z-40 flex justify-center gap-5">
        <span className="text-[7px] font-mono flex items-center gap-1.5" style={{ color: '#f5a62340' }}>
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#f5a623', boxShadow: '0 0 3px #f5a623' }} />1PT</span>
        <span className="text-[7px] font-mono flex items-center gap-1.5" style={{ color: '#ffcc0050' }}>
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#ffcc00', boxShadow: '0 0 3px #ffcc00' }} />3PT</span>
        <span className="text-[7px] font-mono flex items-center gap-1.5" style={{ color: '#ff000050' }}>
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#ff0000', boxShadow: '0 0 3px #ff0000' }} />DEATH</span>
        <span className="text-[7px] font-mono flex items-center gap-1.5" style={{ color: '#66665540' }}>
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#666655' }} />DECOY</span>
      </div>
    </div>
  );
}
// Floor 5: THE LEAK — fullscreen AAA redesign (Phase A + B)
// Skill check floor: simultaneous crack patching + lantern flame management + rising water
// Win: seal NEEDED_PATCHES cracks. Fail: water reaches lantern OR lantern dies OR lantern ignites in gas.
function Floor5({ onComplete, onFail }) {
  const NEEDED_PATCHES = 8;
  const FLOOR_DURATION = 70; // seconds soft cap

  // Refs that game loop reads (don't trigger renders)
  const failedRef = useRef(false);
  const completedRef = useRef(false);
  const cracksRef = useRef([]); // [{ id, x, y, severity, sealed, hissAge, drag: null|{progress} }]
  const ventsRef = useRef([]); // [{ id, x, y, on, cycle }]
  const cloudsRef = useRef([]); // [{ id, x, y, age, radius }]
  const lanternRef = useRef({
    x: 50, y: 78, // % of screen
    flame: 100,   // 0-100
    igniting: 0,  // 0-100; if hits 100 -> fail
  });
  const waterRef = useRef(0);
  const sealedRef = useRef(0);
  const dragRef = useRef(null); // { crackId, lastX, lastY, totalPath }
  const elapsedRef = useRef(0);
  const fanCooldownRef = useRef(0);
  const mouseRef = useRef({ x: 50, y: 78 });

  // Render state (mirrors refs at ~60fps tick)
  const [, forceTick] = useState(0);
  const containerRef = useRef(null);

  const tickLoop = useCallback(() => {
    if (failedRef.current || completedRef.current) return;

    const dt = 1 / 60;
    elapsedRef.current += dt;

    // === DIFFICULTY CURVE ===
    const t = elapsedRef.current;
    const phase = t < 18 ? 1 : t < 35 ? 2 : t < 55 ? 3 : 4;
    const maxConcurrent = phase >= 4 ? 4 : phase + 1; // 2,3,4,4
    const crackSpawnRate = 0.14 + phase * 0.04; // % chance/sec
    const cloudSpawnRate = phase >= 2 ? 0.06 + (phase - 2) * 0.04 : 0;
    const waterRiseBase = 0.35 + phase * 0.12; // pct/sec passive
    const waterFromBurst = 7;
    const waterFromSeal = -1.2;

    // === SPAWN CRACKS ===
    const activeCracks = cracksRef.current.filter(c => !c.sealed && !c.burst);
    if (activeCracks.length < maxConcurrent && Math.random() < crackSpawnRate * dt * 60) {
      const id = `c_${Math.random().toString(36).slice(2, 9)}`;
      const angle = Math.random() * 360;
      const severity = phase >= 3 && Math.random() < 0.3 ? 2 : 1; // big cracks need 2x the drag
      cracksRef.current.push({
        id,
        x: 8 + Math.random() * 84,
        y: 8 + Math.random() * 60, // not in lantern HUD area
        angle,
        severity,
        progress: 0, // 0-100 (sealing progress)
        hissAge: 0,
        timeToBurst: severity === 2 ? 11 : 8,
        burstAt: t + (severity === 2 ? 11 : 8),
        sealed: false,
        burst: false,
        active: true,
      });
    }

    // === SPAWN GAS CLOUDS ===
    if (cloudsRef.current.length < 3 && Math.random() < cloudSpawnRate * dt * 60) {
      cloudsRef.current.push({
        id: `g_${Math.random().toString(36).slice(2, 9)}`,
        x: 10 + Math.random() * 80,
        y: 25 + Math.random() * 50,
        age: 0,
        ttl: 8 + Math.random() * 6,
        radius: 14 + Math.random() * 6,
      });
    }

    // === UPDATE CRACKS — burst logic ===
    cracksRef.current.forEach(c => {
      if (c.sealed || c.burst) return;
      c.hissAge += dt;
      if (t >= c.burstAt) {
        c.burst = true;
        c.active = false;
        waterRef.current = Math.min(100, waterRef.current + waterFromBurst);
      }
    });

    // === UPDATE CLOUDS ===
    cloudsRef.current.forEach(g => { g.age += dt; });
    cloudsRef.current = cloudsRef.current.filter(g => g.age < g.ttl);

    // === LANTERN ===
    const lan = lanternRef.current;
    // Drain flame slowly always
    lan.flame -= dt * 4;
    // Drain faster if many cracks
    lan.flame -= activeCracks.length * dt * 0.6;
    // Check if lantern is inside any cloud (the lantern is what ignites gas, not the cursor)
    const lx = lan.x, ly = lan.y;
    let inCloud = false;
    cloudsRef.current.forEach(g => {
      const dx = lx - g.x, dy = ly - g.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      const fade = 1 - g.age / g.ttl;
      if (d < g.radius * fade) inCloud = true;
    });
    if (inCloud && lan.flame > 5) {
      lan.igniting = Math.min(100, lan.igniting + dt * 38);
    } else {
      lan.igniting = Math.max(0, lan.igniting - dt * 25);
    }
    if (lan.igniting >= 100) {
      failedRef.current = true;
      setTimeout(() => onFail({ reason: 'ignition' }), 600);
    }
    if (lan.flame <= 0) {
      failedRef.current = true;
      setTimeout(() => onFail({ reason: 'darkness' }), 600);
    }
    fanCooldownRef.current = Math.max(0, fanCooldownRef.current - dt);

    // === WATER ===
    waterRef.current = Math.min(100, waterRef.current + waterRiseBase * dt + activeCracks.length * 0.6 * dt);
    // Lantern drowned?
    if (waterRef.current >= 75) {
      failedRef.current = true;
      setTimeout(() => onFail({ reason: 'drowned' }), 600);
    }

    forceTick(n => (n + 1) % 1000);
  }, [onFail]);

  // Game loop — 60fps via rAF
  useEffect(() => {
    let rafId;
    let lastTime = performance.now();
    const loop = (now) => {
      const delta = now - lastTime;
      if (delta >= 16) {
        lastTime = now;
        tickLoop();
      }
      if (!failedRef.current && !completedRef.current) {
        rafId = requestAnimationFrame(loop);
      }
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [tickLoop]);

  // Mouse tracking → represents the player's "lantern position" for cloud detection
  useEffect(() => {
    const handle = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left) / rect.width * 100;
      const y = ((e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top) / rect.height * 100;
      mouseRef.current = { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
      lanternRef.current.x = mouseRef.current.x;
      lanternRef.current.y = Math.min(85, mouseRef.current.y + 3); // lantern hangs slightly below cursor
      // Drag handler — accumulate path length as sealing progress
      if (dragRef.current) {
        const d = dragRef.current;
        const dx = mouseRef.current.x - d.lastX;
        const dy = mouseRef.current.y - d.lastY;
        const moved = Math.sqrt(dx * dx + dy * dy);
        d.totalPath += moved;
        d.lastX = mouseRef.current.x;
        d.lastY = mouseRef.current.y;
        const crack = cracksRef.current.find(c => c.id === d.crackId);
        if (crack && !crack.burst && !crack.sealed) {
          const required = crack.severity === 2 ? 32 : 18;
          crack.progress = Math.min(100, (d.totalPath / required) * 100);
          if (crack.progress >= 100) {
            crack.sealed = true;
            crack.active = false;
            sealedRef.current += 1;
            waterRef.current = Math.max(0, waterRef.current - 1.2);
            dragRef.current = null;
            if (sealedRef.current >= NEEDED_PATCHES && !completedRef.current) {
              completedRef.current = true;
              setTimeout(() => onComplete(), 600);
            }
          }
        }
      }
    };
    const up = () => {
      if (dragRef.current) {
        const crack = cracksRef.current.find(c => c.id === dragRef.current.crackId);
        if (crack && !crack.sealed) crack.progress *= 0.5; // partial penalty for releasing early
        dragRef.current = null;
      }
    };
    window.addEventListener('mousemove', handle);
    window.addEventListener('touchmove', handle);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', handle);
      window.removeEventListener('touchmove', handle);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, [onComplete]);

  // Fan the flame — Spacebar
  useEffect(() => {
    const handle = (e) => {
      if (e.code === 'Space' && fanCooldownRef.current <= 0) {
        e.preventDefault();
        lanternRef.current.flame = Math.min(100, lanternRef.current.flame + 18);
        fanCooldownRef.current = 0.6;
      }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, []);

  const startDrag = (crack) => {
    if (crack.burst || crack.sealed) return;
    dragRef.current = {
      crackId: crack.id,
      lastX: mouseRef.current.x,
      lastY: mouseRef.current.y,
      totalPath: (crack.progress / 100) * (crack.severity === 2 ? 32 : 18),
    };
  };

  // ─── DERIVED RENDER VALUES ───
  const lan = lanternRef.current;
  const water = waterRef.current;
  const cracks = cracksRef.current;
  const clouds = cloudsRef.current;
  const sealed = sealedRef.current;
  const flame = lan.flame;
  const igniting = lan.igniting;
  const danger = water > 50 || flame < 30 || igniting > 40;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[60] overflow-hidden select-none"
      data-testid="floor-5"
      style={{
        background: '#020005',
        cursor: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'><defs><filter id='s' x='-50%25' y='-50%25' width='200%25' height='200%25'><feDropShadow dx='0' dy='1' stdDeviation='1.2' flood-color='black' flood-opacity='0.85'/></filter></defs><g filter='url(%23s)' fill='%23ffeebb' stroke='%23663300' stroke-width='1.2' stroke-linejoin='round'><path d='M11 3 L11 16 L8 13 L5.5 14.2 L11.6 23 L13 24 L20 24 L21.5 22 L21.5 18 L20 14.5 L18.5 14.5 L18.5 11.5 L17 11.5 L17 11 L15.5 11 L15.5 9 L14 9 L14 4 Z'/></g></svg>") 14 4, pointer`,
      }}
    >
      <style>{`
        @keyframes f5-leak-hiss { 0%,100% { opacity: 0.35; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.4); } }
        @keyframes f5-water-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes f5-flame-flicker { 0%,100% { transform: scale(1) rotate(-2deg); opacity: 0.95; } 25% { transform: scale(1.05) rotate(1deg); opacity: 1; } 50% { transform: scale(0.95) rotate(2deg); opacity: 0.85; } 75% { transform: scale(1.02) rotate(-1deg); opacity: 0.92; } }
        @keyframes f5-gas-drift { 0% { transform: translate(0,0) scale(1); opacity: 0; } 20% { opacity: 0.8; } 80% { opacity: 0.8; } 100% { transform: translate(20px,-30px) scale(1.4); opacity: 0; } }
        @keyframes f5-spark { 0% { transform: scale(0); opacity: 1; } 100% { transform: scale(3); opacity: 0; } }
        @keyframes f5-warning-pulse { 0%,100% { opacity: 0.15; } 50% { opacity: 0.45; } }
        @keyframes f5-pendulum { 0%,100% { transform: rotate(-4.5deg); } 50% { transform: rotate(4.5deg); } }
        @keyframes f5-pendulum-fast { 0%,100% { transform: rotate(-7deg); } 50% { transform: rotate(7deg); } }
        @keyframes f5-dust-rise { 0% { transform: translate(0, 0); opacity: 0; } 8% { opacity: 0.7; } 90% { opacity: 0.5; } 100% { transform: translate(2vmin, -110vh); opacity: 0; } }
        @keyframes f5-drip-fall { 0% { transform: translateY(0); opacity: 0; } 10% { opacity: 1; } 100% { transform: translateY(110vh); opacity: 0.4; } }
        @keyframes f5-grain { 0%,100% { transform: translate(0,0); } 25% { transform: translate(-2%, 1%); } 50% { transform: translate(1%, -1%); } 75% { transform: translate(-1%, -2%); } }
        @keyframes f5-hud-pulse { 0%,100% { box-shadow: 0 0 14px rgba(255,170,40,0.18) inset, 0 0 0 1px rgba(255,170,40,0.4); } 50% { box-shadow: 0 0 22px rgba(255,170,40,0.32) inset, 0 0 0 1px rgba(255,210,60,0.7); } }
        @keyframes f5-glow-pulse { 0%,100% { filter: drop-shadow(0 0 8px rgba(255,170,40,0.5)); } 50% { filter: drop-shadow(0 0 16px rgba(255,210,80,0.8)); } }
      `}</style>

      {/* Sewer corridor BG */}
      <div className="absolute inset-0" style={{
        backgroundImage: `url(${floor5LeakBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'brightness(0.62) saturate(1.25) contrast(1.1)',
      }} />

      {/* Atmospheric color grade — teal-orange cinematic LUT */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(15,40,60,0.35) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(40,15,5,0.35) 100%)',
          mixBlendMode: 'soft-light',
        }} />

      {/* God rays from off-screen top-right */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 60vmax 80vmax at 85% -15%, rgba(180,220,255,0.10) 0%, rgba(120,200,255,0.04) 30%, transparent 60%)',
        mixBlendMode: 'screen',
      }} />

      {/* Distant deep-sewer crimson glow (suggests the descent continues below) */}
      <div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{
        height: '40%',
        background: 'radial-gradient(ellipse at 50% 100%, rgba(255,80,30,0.08) 0%, rgba(120,30,0,0.04) 40%, transparent 70%)',
        mixBlendMode: 'screen',
      }} />

      {/* Bottom vignette + corridor depth */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 100%, rgba(0,40,80,0.18) 0%, rgba(0,8,12,0.55) 70%, rgba(0,4,8,0.85) 100%)',
      }} />

      {/* Floating dust motes — drift slowly upward through light shafts */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 22 }).map((_, i) => {
          const seed = i * 53.7;
          const left = (seed * 1.3) % 100;
          const dur = 14 + (seed % 8);
          const delay = (seed * 0.3) % 14;
          const size = 0.18 + ((seed * 0.07) % 0.22);
          return (
            <div key={`dust_${i}`} style={{
              position: 'absolute',
              left: `${left}%`,
              bottom: '-5vmin',
              width: `${size}vmin`, height: `${size}vmin`,
              borderRadius: '50%',
              background: 'rgba(255,220,180,0.55)',
              boxShadow: '0 0 6px rgba(255,200,150,0.4)',
              animation: `f5-dust-rise ${dur}s linear ${delay}s infinite`,
              filter: 'blur(0.5px)',
            }} />
          );
        })}
      </div>

      {/* Drips from the ceiling — slow water droplets */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => {
          const seed = i * 71.3;
          const left = 8 + ((seed * 1.7) % 84);
          const dur = 4 + ((seed * 0.4) % 3);
          const delay = (seed * 0.6) % 8;
          return (
            <div key={`drip_${i}`} style={{
              position: 'absolute',
              left: `${left}%`,
              top: '-2vmin',
              width: '0.25vmin',
              height: '1.4vmin',
              background: 'linear-gradient(180deg, rgba(180,220,240,0) 0%, rgba(140,200,230,0.7) 70%, rgba(180,230,255,0.95) 100%)',
              borderRadius: '50% 50% 60% 60%',
              boxShadow: '0 0 4px rgba(140,200,255,0.4)',
              animation: `f5-drip-fall ${dur}s ease-in ${delay}s infinite`,
            }} />
          );
        })}
      </div>

      {/* Pipe joints overlay (decorative) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-25" viewBox="0 0 100 100" preserveAspectRatio="none">
        <line x1="0" y1="22" x2="100" y2="22" stroke="#1a3a4a" strokeWidth="0.4" />
        <line x1="0" y1="48" x2="100" y2="48" stroke="#1a3a4a" strokeWidth="0.4" />
        <line x1="20" y1="0" x2="20" y2="70" stroke="#1a3a4a" strokeWidth="0.3" />
        <line x1="55" y1="0" x2="55" y2="70" stroke="#1a3a4a" strokeWidth="0.3" />
        <line x1="80" y1="0" x2="80" y2="70" stroke="#1a3a4a" strokeWidth="0.3" />
      </svg>

      {/* Gas clouds */}
      {clouds.map(g => {
        const fade = 1 - g.age / g.ttl;
        return (
          <div key={g.id} className="absolute pointer-events-none rounded-full"
            style={{
              left: `${g.x}%`, top: `${g.y}%`,
              width: `${g.radius * 2}vmin`, height: `${g.radius * 2}vmin`,
              transform: 'translate(-50%, -50%)',
              background: `radial-gradient(circle, rgba(57,255,20,${0.28 * fade}) 0%, rgba(57,255,20,${0.12 * fade}) 40%, transparent 75%)`,
              filter: 'blur(6px)',
              animation: 'f5-warning-pulse 1.6s ease-in-out infinite',
            }}
          />
        );
      })}

      {/* Cracks */}
      {cracks.map(c => {
        if (c.sealed || c.burst) return null;
        const required = c.severity === 2 ? 80 : 45;
        const len = c.severity === 2 ? 11 : 7; // vmin
        const isDragging = dragRef.current?.crackId === c.id;
        return (
          <div key={c.id} className="absolute pointer-events-none"
            style={{ left: `${c.x}%`, top: `${c.y}%`, transform: `translate(-50%, -50%) rotate(${c.angle}deg)` }}>
            {/* Hiss particles */}
            <div className="absolute" style={{
              left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              width: `${len * 2.2}vmin`, height: `${len * 2.2}vmin`,
              background: `radial-gradient(circle, rgba(57,255,20,0.35) 0%, transparent 60%)`,
              animation: 'f5-leak-hiss 1.1s ease-in-out infinite',
              filter: 'blur(3px)',
            }} />
            {/* Crack body */}
            <div onMouseDown={() => startDrag(c)} onTouchStart={() => startDrag(c)}
              className="relative pointer-events-auto"
              style={{
                width: `${len}vmin`, height: c.severity === 2 ? '0.7vmin' : '0.55vmin',
                background: c.severity === 2
                  ? 'linear-gradient(90deg, transparent, #ff3030 40%, #ff6630 60%, transparent)'
                  : 'linear-gradient(90deg, transparent, #ffaa00 40%, #ff6600 60%, transparent)',
                boxShadow: c.severity === 2
                  ? '0 0 8px rgba(255,40,0,0.7), 0 0 18px rgba(255,80,0,0.4)'
                  : '0 0 6px rgba(255,170,0,0.6), 0 0 14px rgba(255,140,0,0.3)',
                cursor: dragRef.current?.crackId === c.id ? 'grabbing' : 'grab',
              }}>
              {/* Hit-area expander — invisible, easier to click */}
              <div className="absolute"
                style={{
                  left: `-${len * 0.25}vmin`, top: '-1.2vmin',
                  width: `${len * 1.5}vmin`, height: '2.5vmin',
                  pointerEvents: 'auto',
                  cursor: dragRef.current?.crackId === c.id ? 'grabbing' : 'grab',
                }}
                onMouseDown={() => startDrag(c)} onTouchStart={() => startDrag(c)}
              />
              {/* Sealing progress bar */}
              <div className="absolute -bottom-2 left-0 h-[3px] rounded-sm transition-none"
                style={{
                  width: `${c.progress}%`,
                  background: 'linear-gradient(90deg, #39ff14, #aaff66)',
                  boxShadow: '0 0 6px rgba(57,255,20,0.7)',
                }} />
              {isDragging && (
                <div className="absolute -inset-2 rounded pointer-events-none" style={{
                  border: '1px solid rgba(57,255,20,0.6)', boxShadow: '0 0 12px rgba(57,255,20,0.4)',
                }} />
              )}
            </div>
            {/* Severity indicator */}
            {c.severity === 2 && (
              <div className="absolute" style={{
                left: '50%', top: `-${len * 0.4}vmin`, transform: 'translateX(-50%) rotate(0deg)',
                fontSize: '8px', letterSpacing: '0.2em', fontFamily: "'Courier New', monospace",
                color: '#ff3030', textShadow: '0 0 6px rgba(255,40,0,0.6)',
              }}>MAJOR</div>
            )}
          </div>
        );
      })}

      {/* Burst flash (recent ones) */}
      {cracks.filter(c => c.burst && elapsedRef.current - (c.burstAt + 0) < 0.6).map(c => (
        <div key={`burst_${c.id}`} className="absolute pointer-events-none rounded-full"
          style={{
            left: `${c.x}%`, top: `${c.y}%`, width: '8vmin', height: '8vmin',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(255,40,0,0.7) 0%, transparent 60%)',
            animation: 'f5-spark 0.6s ease-out forwards',
          }} />
      ))}

      {/* Rising water layer */}
      <div className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{
          height: `${water}%`,
          background: 'linear-gradient(to top, rgba(0,30,60,0.92) 0%, rgba(0,80,160,0.55) 50%, rgba(0,140,255,0.18) 100%)',
          transition: 'height 0.5s cubic-bezier(0.4,0,0.2,1)',
        }}>
        <div className="absolute inset-x-0 -top-1 h-1.5"
          style={{
            background: 'linear-gradient(90deg, transparent, #00ccff 30%, #66ddff 50%, #00ccff 70%, transparent)',
            boxShadow: '0 0 12px rgba(0,200,255,0.7)',
            animation: 'f5-water-bob 2s ease-in-out infinite',
          }} />
      </div>

      {/* Radial DIM mask — darkens everything OUTSIDE the lantern's reach */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle ${24 + flame * 0.18}vmin at ${lan.x}% ${lan.y}%, transparent 0%, transparent 28%, rgba(0,0,5,0.65) 70%, rgba(0,0,5,0.92) 100%)`,
          mixBlendMode: 'multiply',
          transition: 'background 0.18s linear',
        }} />

      {/* Lantern — hangs BELOW cursor, so the hand cursor stays visible above */}
      <div className="absolute pointer-events-none" style={{
        left: `${lan.x}%`, top: `${lan.y}%`,
        transform: 'translate(-50%, 8%)', // lantern body hangs below the cursor/hand
        zIndex: 30,
      }}>
        {/* Swing wrapper — pendulum rotation around the chain anchor (above the lantern) */}
        <div style={{
          transformOrigin: '50% -120%', // virtual chain anchor sits above the lantern, so it swings naturally beneath
          animation: danger ? 'f5-pendulum-fast 1.3s ease-in-out infinite' : 'f5-pendulum 2.4s ease-in-out infinite',
        }}>
          {/* Tight warm aura right behind the glass */}
          <div className="absolute" style={{
            left: '50%', top: '52%', transform: 'translate(-50%, -50%)',
            width: '6.5vmin', height: '6.5vmin',
            background: igniting > 40
              ? `radial-gradient(circle, rgba(120,255,80,${0.75 + igniting * 0.003}) 0%, rgba(57,255,20,0.32) 35%, transparent 70%)`
              : `radial-gradient(circle, rgba(255,220,140,${0.95 * (flame / 100)}) 0%, rgba(255,150,40,${0.5 * (flame / 100)}) 35%, transparent 70%)`,
            filter: 'blur(2px)',
            animation: 'f5-flame-flicker 0.45s ease-in-out infinite',
            pointerEvents: 'none',
          }} />
          {/* Lantern image */}
          <img src={floor5Lantern} alt="" draggable="false"
            style={{
              position: 'relative',
              display: 'block',
              width: '8vmin',
              height: 'auto',
              filter: igniting > 40
                ? `drop-shadow(0 0 16px rgba(57,255,20,${0.55 + igniting * 0.004})) drop-shadow(0 0 36px rgba(57,255,20,0.4)) brightness(${0.9 + igniting * 0.003})`
                : `drop-shadow(0 0 16px rgba(255,170,60,${0.5 * (flame / 100) + 0.12})) drop-shadow(0 0 36px rgba(255,120,30,${0.32 * (flame / 100)})) brightness(${0.9 + (flame / 100) * 0.25})`,
            }}
          />
        </div>
      </div>

      {/* HUD — top */}
      <div className="absolute top-0 left-0 right-0 z-40 px-6 pt-5 pb-4 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.97) 0%, rgba(0,5,10,0.7) 60%, transparent 100%)' }}>
        <div className="flex items-start justify-between">
          {/* Left: floor + objective — orange/gold */}
          <div className="px-6 py-4 rounded-sm" style={{
            background: 'linear-gradient(180deg, rgba(35,18,4,0.97), rgba(22,10,3,0.97))',
            border: '2px solid rgba(255,180,50,0.7)',
            boxShadow: '0 0 28px rgba(255,140,30,0.25) inset, 0 0 36px rgba(255,140,30,0.2), 0 4px 18px rgba(0,0,0,0.6)',
            animation: 'f5-hud-pulse 3.5s ease-in-out infinite',
          }}>
            <span className="text-[12px] tracking-[0.5em] font-mono uppercase block font-black" style={{ color: '#ffd980', textShadow: '0 0 10px rgba(255,200,80,0.9)' }}>The Leak</span>
            <div className="flex items-baseline gap-3 mt-2">
              <span className="font-black leading-none" style={{
                color: '#fff5d8',
                textShadow: '0 0 22px rgba(255,200,80,1), 0 0 44px rgba(255,150,40,0.7), 0 2px 6px rgba(0,0,0,0.95)',
                fontFamily: "'Cinzel', serif",
                fontSize: '52px',
              }}>
                {sealed}<span className="font-black text-3xl" style={{ color: '#aa6633' }}>/{NEEDED_PATCHES}</span>
              </span>
              <span className="text-[12px] font-mono tracking-[0.4em] font-black" style={{ color: '#ffbb44', textShadow: '0 0 6px rgba(255,150,40,0.7)' }}>SEALED</span>
            </div>
            {/* Sealed progress strip */}
            <div className="mt-2.5 h-1.5 rounded-sm overflow-hidden" style={{ background: 'rgba(0,0,0,0.85)', border: '1px solid rgba(255,170,40,0.5)', width: '210px' }}>
              <div className="h-full rounded-sm transition-all duration-300" style={{
                width: `${(sealed / NEEDED_PATCHES) * 100}%`,
                background: 'linear-gradient(90deg, #ff6600, #ffaa30, #ffeebb)',
                boxShadow: '0 0 10px rgba(255,170,40,0.9)',
              }} />
            </div>
          </div>
          {/* Right: water level — blue */}
          <div className="px-6 py-4 rounded-sm text-right" style={{
            background: 'linear-gradient(180deg, rgba(2,18,38,0.97), rgba(2,12,30,0.97))',
            border: `2px solid ${water > 60 ? 'rgba(0,210,255,1)' : 'rgba(0,180,255,0.65)'}`,
            boxShadow: water > 60
              ? '0 0 32px rgba(0,200,255,0.4) inset, 0 0 44px rgba(0,200,255,0.3), 0 4px 18px rgba(0,0,0,0.6)'
              : '0 0 22px rgba(0,150,255,0.2) inset, 0 4px 18px rgba(0,0,0,0.6)',
          }}>
            <span className="text-[12px] font-mono tracking-[0.5em] uppercase font-black block" style={{
              color: water > 50 ? '#aaeeff' : '#bbddee',
              textShadow: '0 0 10px rgba(80,200,255,0.7)',
            }}>FLOOD LEVEL</span>
            <div className="w-60 h-4 mt-2.5 rounded-sm overflow-hidden ml-auto relative" style={{
              background: 'rgba(0,0,0,0.85)',
              border: `1px solid ${water > 60 ? 'rgba(0,210,255,0.95)' : 'rgba(0,170,255,0.55)'}`,
            }}>
              <div className="h-full rounded-sm transition-all duration-500 relative" style={{
                width: `${water}%`,
                background: water > 60
                  ? 'linear-gradient(90deg, #0066bb, #0099dd, #00ccff, #88ddff)'
                  : 'linear-gradient(90deg, #003366, #0077cc, #00aaee)',
                boxShadow: water > 60 ? '0 0 14px rgba(0,200,255,1)' : '0 0 8px rgba(0,150,255,0.5)',
              }}>
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                  backgroundSize: '60% 100%',
                  animation: 'fi-shimmer 2.4s linear infinite',
                }} />
              </div>
            </div>
            <div className="font-black mt-2" style={{
              color: water > 60 ? '#aaeeff' : '#bbeeff',
              fontSize: '20px',
              fontFamily: "'Cinzel', serif",
              textShadow: water > 60 ? '0 0 14px rgba(0,200,255,1)' : '0 0 8px rgba(80,200,255,0.6)',
            }}>
              {Math.round(water)}<span className="text-sm ml-0.5">%</span>
            </div>
            {water >= 65 && (
              <span className="text-[11px] font-mono tracking-[0.35em] mt-2 inline-block font-black" style={{
                color: '#ff7070',
                animation: 'f5-warning-pulse 0.6s ease-in-out infinite',
                textShadow: '0 0 14px rgba(255,40,40,1)',
              }}>
                ⚠ LANTERN AT RISK
              </span>
            )}
          </div>
        </div>
      </div>

      {/* HUD — lantern bottom-left */}
      <div className="absolute bottom-5 left-5 z-40 pointer-events-none px-6 py-4 rounded-sm" style={{
        background: 'linear-gradient(180deg, rgba(30,15,3,0.97), rgba(18,8,2,0.97))',
        border: `2px solid ${flame < 25 ? 'rgba(255,80,50,1)' : 'rgba(255,200,60,0.7)'}`,
        boxShadow: flame < 25
          ? '0 0 32px rgba(255,40,40,0.4) inset, 0 0 44px rgba(255,40,40,0.3), 0 4px 18px rgba(0,0,0,0.6)'
          : '0 0 26px rgba(255,170,40,0.22) inset, 0 0 32px rgba(255,140,30,0.18), 0 4px 18px rgba(0,0,0,0.6)',
        minWidth: '270px',
      }}>
        <span className="text-[13px] font-mono tracking-[0.5em] uppercase block font-black" style={{
          color: flame < 25 ? '#ff9090' : '#ffdd80',
          textShadow: flame < 25 ? '0 0 12px rgba(255,40,40,0.9)' : '0 0 10px rgba(255,200,80,0.7)',
        }}>Lantern</span>
        <div className="flex items-center gap-3 mt-2.5">
          <div className="w-52 h-4 rounded-sm overflow-hidden relative" style={{ background: 'rgba(0,0,0,0.85)', border: `1px solid ${flame < 25 ? 'rgba(255,40,40,0.8)' : 'rgba(255,180,40,0.65)'}` }}>
            <div className="h-full rounded-sm transition-all duration-200 relative" style={{
              width: `${flame}%`,
              background: flame < 25
                ? 'linear-gradient(90deg, #ff2020, #ff5030, #ffaa00)'
                : 'linear-gradient(90deg, #ff5500, #ff9933, #ffcc66, #ffeebb)',
              boxShadow: flame < 25 ? '0 0 14px rgba(255,40,30,1)' : '0 0 12px rgba(255,170,40,0.85)',
            }}>
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                backgroundSize: '60% 100%',
                animation: 'fi-shimmer 1.8s linear infinite',
              }} />
            </div>
          </div>
          <span className="font-black" style={{
            color: flame < 25 ? '#ff9090' : '#fff0c0',
            fontSize: '20px',
            textShadow: '0 0 12px rgba(255,170,40,0.9)',
            fontFamily: "'Cinzel', serif",
          }}>
            {Math.round(flame)}<span className="text-sm">%</span>
          </span>
        </div>
        <div className="text-[11px] font-mono tracking-[0.4em] mt-3 uppercase font-black" style={{
          color: '#ffdd80',
          textShadow: '0 0 8px rgba(255,200,80,0.6)',
          animation: flame < 35 ? 'f5-warning-pulse 1s ease-in-out infinite' : 'none',
        }}>
          [SPACE] Fan the flame
        </div>
        {/* Ignition warning */}
        {igniting > 25 && (
          <div className="mt-3.5 pt-3.5 border-t" style={{ borderColor: 'rgba(57,255,20,0.6)' }}>
            <span className="text-[12px] font-mono tracking-[0.4em] uppercase block font-black" style={{
              color: '#bbff77',
              animation: 'f5-warning-pulse 0.45s ease-in-out infinite',
              textShadow: '0 0 14px rgba(57,255,20,1)',
            }}>
              ⚠ GAS · IGNITION {Math.round(igniting)}%
            </span>
            <div className="w-52 h-2.5 mt-2 rounded-sm overflow-hidden" style={{ background: 'rgba(0,0,0,0.85)', border: '1px solid rgba(57,255,20,0.7)' }}>
              <div className="h-full rounded-sm" style={{
                width: `${igniting}%`,
                background: 'linear-gradient(90deg, #aaff66, #39ff14, #88ff44)',
                boxShadow: '0 0 14px rgba(57,255,20,1)',
              }} />
            </div>
          </div>
        )}
      </div>

      {/* HUD — bottom-center instructions */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-40 pointer-events-none text-center px-7 py-3 rounded-sm" style={{
        background: 'linear-gradient(180deg, rgba(3,18,30,0.92), rgba(3,10,20,0.92))',
        border: '1px solid rgba(255,180,50,0.5)',
        boxShadow: '0 0 22px rgba(0,150,255,0.12) inset, 0 0 22px rgba(255,170,40,0.12) inset, 0 4px 14px rgba(0,0,0,0.5)',
      }}>
        <span className="text-[12px] tracking-[0.4em] font-mono uppercase block font-black" style={{
          color: '#ffdd80',
          textShadow: '0 0 8px rgba(255,200,80,0.6)',
        }}>
          ◆ click + drag along cracks to seal ◆
        </span>
        <span className="text-[10px] tracking-[0.32em] font-mono uppercase block mt-1.5 font-bold" style={{
          color: '#aaddee',
          textShadow: '0 0 6px rgba(80,200,255,0.4)',
        }}>
          avoid green gas with lit lantern · keep the flame alive
        </span>
      </div>

      {/* Danger vignette */}
      <div className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          opacity: danger ? 0.6 : 0,
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(120,0,0,0.25) 100%)',
        }} />

      {/* Screen shake when crack bursts (lightweight: outline pulse) */}
      {cracks.some(c => c.burst && elapsedRef.current - c.burstAt < 0.4) && (
        <div className="absolute inset-0 pointer-events-none" style={{
          boxShadow: 'inset 0 0 80px rgba(255,40,0,0.35)',
          animation: 'f5-warning-pulse 0.4s ease-out',
        }} />
      )}

      {/* Lantern reflection on water (only visible when water is rising past lantern level) */}
      {water > 25 && (
        <div className="absolute pointer-events-none" style={{
          left: `${lan.x}%`,
          bottom: `${Math.max(0, water - 8)}%`,
          transform: 'translate(-50%, 0) scaleY(-1)',
          opacity: 0.35 * Math.min(1, (water - 25) / 30),
          zIndex: 25,
          mixBlendMode: 'screen',
          filter: 'blur(1.5px)',
        }}>
          <div style={{
            transformOrigin: '50% -120%',
            animation: danger ? 'f5-pendulum-fast 1.3s ease-in-out infinite' : 'f5-pendulum 2.4s ease-in-out infinite',
          }}>
            <img src={floor5Lantern} alt="" draggable="false" style={{
              width: '8vmin', height: 'auto', opacity: 0.6,
              filter: igniting > 40
                ? 'hue-rotate(60deg) brightness(1.2) saturate(1.3)'
                : 'brightness(1.1) saturate(1.4)',
            }} />
          </div>
        </div>
      )}

      {/* Film grain — subtle */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.45 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>")`,
        backgroundSize: '200px 200px',
        opacity: 0.07,
        mixBlendMode: 'overlay',
        animation: 'f5-grain 0.18s steps(2) infinite',
        zIndex: 50,
      }} />

      {/* Edge chromatic vignette — cinematic */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, transparent 50%, rgba(180,30,10,0.06) 80%, rgba(80,10,5,0.15) 100%)',
        mixBlendMode: 'multiply',
        zIndex: 49,
      }} />
    </div>
  );
}
// Floor 6: Blackout Tunnels — total darkness, tiny flashlight, moving targets, decoys, blackout pulses
// Floor 6: Blackout Tunnels — AAA darkness, flashlight cone, moving targets + decoys
function Floor6({ onComplete, onFail }) {
  const [found, setFound] = useState(0);
  const [timeLeft, setTimeLeft] = useState(22);
  const [symbols, setSymbols] = useState([]);
  const [decoys, setDecoys] = useState([]);
  const [lightRadius, setLightRadius] = useState(55);
  const [flash, setFlash] = useState(null);
  const [blackout, setBlackout] = useState(false);
  const [collectFx, setCollectFx] = useState(null);
  const containerRef = useRef(null);
  const mousePosRef = useRef({ x: 50, y: 50 });
  const lightRef = useRef(null);
  const failedRef = useRef(false);
  const foundRef = useRef(0);
  const needed = 8;

  useEffect(() => {
    const syms = Array.from({ length: needed }, (_, i) => ({
      id: `s${i}`, x: 8 + Math.random() * 80, y: 10 + Math.random() * 75, found: false,
      dx: (Math.random() - 0.5) * (0.2 + i * 0.06), dy: (Math.random() - 0.5) * (0.2 + i * 0.06),
    }));
    setSymbols(syms);
    const decs = Array.from({ length: 8 }, (_, i) => ({
      id: `d${i}`, x: 5 + Math.random() * 85, y: 8 + Math.random() * 78,
      dx: (Math.random() - 0.5) * 0.5, dy: (Math.random() - 0.5) * 0.5,
    }));
    setDecoys(decs);

    const timer = setInterval(() => {
      setTimeLeft(t => { if (t <= 1) { clearInterval(timer); return 0; } return t - 1; });
    }, 1000);
    const shrink = setInterval(() => setLightRadius(r => Math.max(25, r - 1.5)), 2200);
    const moveInterval = setInterval(() => {
      setSymbols(prev => prev.map(s => {
        if (s.found) return s;
        let nx = s.x + s.dx, ny = s.y + s.dy, ndx = s.dx, ndy = s.dy;
        if (nx < 3 || nx > 93) ndx = -ndx;
        if (ny < 5 || ny > 88) ndy = -ndy;
        return { ...s, x: nx, y: ny, dx: ndx, dy: ndy };
      }));
      setDecoys(prev => prev.map(d => {
        let nx = d.x + d.dx, ny = d.y + d.dy, ndx = d.dx, ndy = d.dy;
        if (nx < 2 || nx > 94) ndx = -ndx;
        if (ny < 3 || ny > 90) ndy = -ndy;
        return { ...d, x: nx, y: ny, dx: ndx, dy: ndy };
      }));
    }, 80);
    const blackoutInterval = setInterval(() => {
      setBlackout(true);
      setTimeout(() => setBlackout(false), 800);
    }, 7000);

    return () => { clearInterval(timer); clearInterval(shrink); clearInterval(moveInterval); clearInterval(blackoutInterval); };
  }, []);

  useEffect(() => {
    if (foundRef.current >= needed && !failedRef.current) { failedRef.current = true; onComplete(); }
    if (timeLeft === 0 && foundRef.current < needed && !failedRef.current) { failedRef.current = true; onFail(); }
  }, [found, timeLeft, onComplete, onFail]);

  const handleMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    if (cx == null) return;
    const x = (cx - rect.left) / rect.width * 100;
    const y = (cy - rect.top) / rect.height * 100;
    mousePosRef.current = { x, y };
    if (lightRef.current && !blackout) {
      lightRef.current.style.background = `radial-gradient(circle ${lightRadius}px at ${x}% ${y}%, transparent 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.98) 100%)`;
    }
  }, [lightRadius, blackout]);

  useEffect(() => {
    if (lightRef.current) {
      if (blackout) {
        lightRef.current.style.background = 'rgba(0,0,0,0.99)';
      } else {
        const { x, y } = mousePosRef.current;
        lightRef.current.style.background = `radial-gradient(circle ${lightRadius}px at ${x}% ${y}%, transparent 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.98) 100%)`;
      }
    }
  }, [blackout, lightRadius]);

  const clickSymbol = (sym) => {
    if (sym.found || blackout) return;
    foundRef.current++;
    setFound(foundRef.current);
    setFlash('good');
    setCollectFx({ x: sym.x, y: sym.y, id: Date.now() });
    setTimeout(() => { setFlash(null); setCollectFx(null); }, 400);
    setSymbols(prev => prev.map(s => s.id === sym.id ? { ...s, found: true } : s));
  };

  const clickDecoy = () => {
    if (blackout) return;
    setFlash('bad');
    setTimeout(() => setFlash(null), 300);
    setTimeLeft(t => Math.max(1, t - 3));
  };

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden"
      data-testid="floor-6"
      style={{ background: '#000', touchAction: 'none' }}
      onMouseMove={handleMove} onTouchMove={handleMove}>
      <style>{`
        @keyframes f6-pulse { 0%, 100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.3); opacity: 1; } }
        @keyframes f6-decoy-pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes f6-collect { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(3); opacity: 0; } }
        @keyframes f6-static { 0% { opacity: 0.06; } 50% { opacity: 0.12; } 100% { opacity: 0.06; } }
        @keyframes f6-flame-flicker { 0%, 100% { opacity: 0.3; transform: translateY(0) scale(1); } 35% { opacity: 0.5; transform: translateY(-4px) scale(1.04); } 65% { opacity: 0.25; transform: translateY(-2px) scale(0.98); } }
      `}</style>

      {/* Iron Anvil prop removed per user request */}

      {/* Flame spark ambients — small smoldering flames around floor edges */}
      {[
        { left: '6%', bottom: '5%', size: 64, delay: 0, dur: 2.4 },
        { left: '14%', bottom: '8%', size: 44, delay: -0.7, dur: 2.8 },
        { right: '7%', bottom: '6%', size: 60, delay: -1.2, dur: 2.6 },
        { right: '16%', bottom: '10%', size: 40, delay: -0.4, dur: 3.1 },
        { left: '38%', bottom: '3%', size: 36, delay: -1.5, dur: 2.2 },
        { right: '40%', bottom: '4%', size: 38, delay: -0.9, dur: 2.7 },
      ].map((f, i) => (
        <img key={`f6flame-${i}`} src={floor6Flame} alt=""
          className="absolute pointer-events-none z-[5] select-none"
          style={{
            left: f.left, right: f.right, bottom: f.bottom,
            width: `${f.size}px`,
            opacity: 0.35,
            mixBlendMode: 'screen',
            filter: 'hue-rotate(-8deg) saturate(1.2)',
            animation: `f6-flame-flicker ${f.dur}s ease-in-out ${f.delay}s infinite`,
          }} />
      ))}

      {/* Very faint ambient particles — visible only in light cone */}
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={`dust-${i}`} className="absolute w-[1px] h-[1px] rounded-full z-10 pointer-events-none"
          style={{ left: `${5 + i * 8}%`, top: `${10 + (i * 7) % 80}%`,
            background: 'rgba(200,200,200,0.15)',
            boxShadow: '0 0 2px rgba(200,200,200,0.1)' }} />
      ))}

      {/* Darkness overlay — flashlight cone */}
      <div ref={lightRef} className="absolute inset-0 z-30 pointer-events-none"
        style={{ background: 'rgba(0,0,0,0.98)', transition: blackout ? 'background 0.3s' : 'none' }} />

      {/* Screen flash */}
      {flash && (
        <div className="absolute inset-0 z-50 pointer-events-none"
          style={{ background: flash === 'good' ? 'rgba(57,255,20,0.06)' : 'rgba(255,0,0,0.1)' }} />
      )}

      {/* Collect burst effect */}
      {collectFx && (
        <div className="absolute z-45 pointer-events-none"
          style={{ left: `calc(${collectFx.x}% - 20px)`, top: `calc(${collectFx.y}% - 20px)`, width: '40px', height: '40px' }}>
          <div className="absolute inset-0 rounded-full" style={{
            border: '2px solid rgba(57,255,20,0.5)', animation: 'f6-collect 0.4s ease-out forwards',
          }} />
        </div>
      )}

      {/* Blackout pulse warning */}
      {blackout && (
        <div className="absolute inset-0 z-45 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <span className="text-[11px] tracking-[0.5em] font-mono font-bold" style={{
              color: 'rgba(255,0,40,0.45)', textShadow: '0 0 8px rgba(255,0,0,0.2)',
              animation: 'f6-static 0.5s ease-in-out infinite',
            }}>BLACKOUT</span>
          </div>
        </div>
      )}

      {/* HUD */}
      <div className="absolute top-0 left-0 right-0 z-40 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[9px] tracking-[0.4em] font-mono uppercase" style={{ color: 'rgba(120,120,120,0.35)' }}>Blackout Tunnels</span>
            <div className="flex items-center gap-3 mt-1">
              <span className="font-mono text-base font-black" style={{ color: '#39ff14', textShadow: '0 0 8px rgba(57,255,20,0.3)' }}>
                {found}<span className="text-white/15 font-normal">/{needed}</span>
              </span>
              {/* Progress diamonds */}
              <div className="flex gap-1">
                {Array.from({ length: needed }).map((_, i) => (
                  <div key={i} className="w-1.5 h-1.5 rotate-45" style={{
                    background: i < found ? '#39ff14' : 'rgba(100,100,100,0.08)',
                    boxShadow: i < found ? '0 0 4px rgba(57,255,20,0.5)' : 'none',
                  }} />
                ))}
              </div>
            </div>
          </div>
          <span className="font-mono text-lg font-black" style={{
            color: timeLeft <= 5 ? '#ff4040' : 'rgba(180,180,180,0.4)',
            textShadow: timeLeft <= 5 ? '0 0 10px rgba(255,0,0,0.4)' : 'none',
          }}>{timeLeft}</span>
        </div>
      </div>

      {/* Symbols — green glowing runes */}
      {symbols.map(s => (
        <button key={s.id} onClick={() => clickSymbol(s)}
          className="absolute z-20 flex items-center justify-center"
          style={{ left: `calc(${s.x}% - 18px)`, top: `calc(${s.y}% - 18px)`, width: '36px', height: '36px',
            opacity: s.found ? 0.03 : 1, cursor: s.found ? 'default' : 'pointer' }}>
          {!s.found && (
            <div className="relative flex items-center justify-center">
              {/* Outer pulse */}
              <div className="absolute w-7 h-7 rounded-full" style={{
                border: '1px solid rgba(57,255,20,0.15)',
                animation: 'f6-pulse 2s ease-in-out infinite',
              }} />
              {/* Core glow */}
              <div className="w-3.5 h-3.5 rounded-full" style={{
                background: 'radial-gradient(circle, #39ff14 0%, rgba(57,255,20,0.4) 60%, transparent 100%)',
                boxShadow: '0 0 10px #39ff14, 0 0 20px rgba(57,255,20,0.3), 0 0 30px rgba(57,255,20,0.1)',
              }} />
            </div>
          )}
        </button>
      ))}

      {/* Decoys — red menacing dots */}
      {decoys.map(d => (
        <button key={d.id} onClick={clickDecoy}
          className="absolute z-20 flex items-center justify-center"
          style={{ left: `calc(${d.x}% - 18px)`, top: `calc(${d.y}% - 18px)`, width: '36px', height: '36px', cursor: 'pointer' }}>
          <div className="relative flex items-center justify-center">
            <div className="w-3 h-3 rounded-full" style={{
              background: 'radial-gradient(circle, #ff3355 0%, rgba(255,51,85,0.4) 60%, transparent 100%)',
              boxShadow: '0 0 8px rgba(255,51,85,0.4), 0 0 16px rgba(255,51,85,0.15)',
              animation: 'f6-decoy-pulse 1.5s ease-in-out infinite',
            }} />
          </div>
        </button>
      ))}

      {/* Bottom instruction */}
      <div className="absolute bottom-3 left-0 right-0 z-40 text-center">
        <span className="text-[7px] tracking-[0.2em] font-mono uppercase" style={{ color: 'rgba(100,100,100,0.15)' }}>
          green = collect // red = decoy (-3s) // all targets move // blackout pulses
        </span>
      </div>
    </div>
  );
}
// Floor 7: THE FORGE — Strike the Soul-Coin
// A spatial-rhythm ritual smithy. A heavy iron hammer hangs on a horizontal rail above an anvil
// bearing a white-hot ingot. The hammer falls on its own metronome — the player ONLY controls
// its X position via mouse. The ingot displays a glowing "strike zone" that repositions each
// cycle; when the hammer descends, X-alignment matters. Between strikes, embers telegraph from
// below, threatening certain X-corridors. Position the hammer to BOTH align with the strike
// zone AND avoid the rising ember columns.
function Floor7({ onComplete, onFail }) {
  const HEAT_TARGET = 120;
  const PRESSURE_CAP = 100;
  const SWING_INITIAL_MS = 1450;
  const SWING_MIN_MS = 720;

  const containerRef = useRef(null);
  const hammerRef = useRef(null);
  const hammerHeadRef = useRef(null);
  const ringRef = useRef(null);
  const sparksLayerRef = useRef(null);
  const embersLayerRef = useRef(null);
  const strikeZoneEl = useRef(null);
  const dropLineRef = useRef(null);
  const ingotRef = useRef(null);
  const ingotBoundsRef = useRef({ left: 25, right: 75, width: 50, top: 60, bottom: 65 }); // screen % bounds

  // Refs for high-frequency state (avoid re-renders)
  const hammerXRef = useRef(50);            // % of width
  const strikeZoneRef = useRef({ x: 50, w: 18 });
  const heatRef = useRef(0);
  const pressureRef = useRef(0);
  const swingMsRef = useRef(SWING_INITIAL_MS);
  const lastStrikeRef = useRef(performance.now());
  const fallStateRef = useRef({ falling: false, t: 0, fromY: 0 });
  const failedRef = useRef(false);
  const wonRef = useRef(false);
  const embersRef = useRef([]);             // active embers
  const lastEmberSpawnRef = useRef(performance.now());
  const emberShakeRef = useRef(0);
  const rafRef = useRef(null);
  const idCtrRef = useRef(0);

  // Low-frequency state for HUD updates (re-renders ~10/s)
  const [heatUI, setHeatUI] = useState(0);
  const [pressureUI, setPressureUI] = useState(0);
  const [strikeCount, setStrikeCount] = useState(0);
  const [combo, setCombo] = useState(0);
  const [verdict, setVerdict] = useState(null); // { type: 'PERFECT'|'GOOD'|'MISS'|'BURN', x, ts }
  const [chamberFlash, setChamberFlash] = useState(null); // 'good' | 'bad'
  const [showTutorial, setShowTutorial] = useState(true);
  const showTutorialRef = useRef(true);

  useEffect(() => {
    showTutorialRef.current = showTutorial;
  }, [showTutorial]);

  useEffect(() => {
    const t = setTimeout(() => setShowTutorial(false), 5500);
    return () => clearTimeout(t);
  }, []);

  // === Mouse / touch handler — only X matters ===
  const handleMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    if (cx == null) return;
    const x = ((cx - rect.left) / rect.width) * 100;
    hammerXRef.current = Math.max(4, Math.min(96, x));
  }, []);

  // Recompute the ingot's screen-% bounds by measuring its actual bounding rect
  function measureIngotBounds() {
    const ing = ingotRef.current;
    const cont = containerRef.current;
    if (!ing || !cont) return;
    const ir = ing.getBoundingClientRect();
    const cr = cont.getBoundingClientRect();
    if (cr.width === 0) return;
    const left = ((ir.left - cr.left) / cr.width) * 100;
    const right = ((ir.right - cr.left) / cr.width) * 100;
    ingotBoundsRef.current = {
      left, right,
      width: right - left,
      top: ((ir.top - cr.top) / cr.height) * 100,
      bottom: ((ir.bottom - cr.top) / cr.height) * 100,
    };
  }

  // === Reposition the strike zone (called after each strike) ===
  // strikeZoneRef.current.x is the CENTER of the zone in SCREEN-% (matches hammerXRef coordinate space).
  // strikeZoneRef.current.w is the zone width in SCREEN-% as well.
  function repositionStrikeZone() {
    measureIngotBounds();
    const b = ingotBoundsRef.current;
    const strikes = strikeCount;
    // Zone width relative to ingot width (shrinks slowly as you progress)
    const zoneFracW = Math.max(0.13, 0.22 - strikes * 0.008);
    const w = b.width * zoneFracW; // screen-% width
    // Pickable X range — keep strike zone fully inside ingot
    const minX = b.left + w / 2 + b.width * 0.04;
    const maxX = b.right - w / 2 - b.width * 0.04;
    const cur = strikeZoneRef.current.x;
    let nx;
    let tries = 0;
    do {
      nx = minX + Math.random() * (maxX - minX);
      tries++;
    } while (Math.abs(nx - cur) < b.width * 0.22 && tries < 8);
    strikeZoneRef.current = { x: nx, w };
    // Push to DOM imperatively (screen-coords)
    if (strikeZoneEl.current) {
      strikeZoneEl.current.style.left = nx + '%';
      strikeZoneEl.current.style.width = w + '%';
    }
  }

  // === Spawn an ember telegraph (rising column threat) ===
  function spawnEmber() {
    if (failedRef.current || wonRef.current) return;
    const id = idCtrRef.current++;
    // Ember telegraphs at a random X. Avoid spawning right on top of strike zone (too unfair)
    let ex;
    let tries = 0;
    do {
      ex = 8 + Math.random() * 84;
      tries++;
    } while (Math.abs(ex - strikeZoneRef.current.x) < 14 && tries < 6);
    embersRef.current.push({
      id,
      x: ex,
      // Phases: 'warn' (1100ms), 'erupt' (300ms), then despawn
      bornAt: performance.now(),
      warnMs: 1100,
      eruptMs: 320,
      hitChecked: false,
    });
  }

  // === Sparks burst (DOM particles) ===
  function spawnSparks(xPct, type) {
    const layer = sparksLayerRef.current;
    if (!layer) return;
    const count = type === 'PERFECT' ? 22 : type === 'GOOD' ? 14 : 8;
    const baseColor = type === 'MISS' || type === 'BURN' ? '#ff2030' : type === 'PERFECT' ? '#fff8d0' : '#ffaa30';
    for (let i = 0; i < count; i++) {
      const s = document.createElement('div');
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.85; // mostly upward
      const speed = 60 + Math.random() * 90;
      const life = 600 + Math.random() * 400;
      const size = 1.5 + Math.random() * 2.5;
      s.style.cssText = `
        position:absolute; left:${xPct}%; bottom:22%; width:${size}px; height:${size}px;
        background:${baseColor}; border-radius:50%;
        box-shadow:0 0 ${4 + size}px ${baseColor};
        pointer-events:none; will-change:transform,opacity;
        transform:translate(-50%,-50%); opacity:1;
        transition:transform ${life}ms cubic-bezier(.2,.6,.4,1), opacity ${life}ms ease-out;
      `;
      layer.appendChild(s);
      // Animate next frame for transition to fire
      requestAnimationFrame(() => {
        const dx = Math.cos(angle) * speed;
        const dy = Math.sin(angle) * speed * 1.2;
        s.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        s.style.opacity = '0';
      });
      setTimeout(() => s.remove(), life + 50);
    }
  }

  // === Resolve a strike when hammer hits the ingot ===
  function resolveStrike() {
    const hammerX = hammerXRef.current;
    const { x: zoneX, w: zoneW } = strikeZoneRef.current;
    const dist = Math.abs(hammerX - zoneX);

    let type = 'MISS';
    if (dist <= zoneW / 2 * 0.5) type = 'PERFECT';
    else if (dist <= zoneW / 2) type = 'GOOD';

    if (type === 'PERFECT') {
      heatRef.current = Math.min(HEAT_TARGET, heatRef.current + 14);
      setCombo(c => c + 1);
      setChamberFlash('good');
      emberShakeRef.current = 8;
    } else if (type === 'GOOD') {
      heatRef.current = Math.min(HEAT_TARGET, heatRef.current + 8);
      setCombo(c => c + 1);
      setChamberFlash('good');
      emberShakeRef.current = 5;
    } else {
      // MISS
      pressureRef.current = Math.min(PRESSURE_CAP + 1, pressureRef.current + 24);
      heatRef.current = Math.max(0, heatRef.current - 4);
      setCombo(0);
      setChamberFlash('bad');
      emberShakeRef.current = 14;
    }

    setVerdict({ type, x: zoneX, ts: performance.now() });
    spawnSparks(zoneX, type);
    setStrikeCount(s => s + 1);
    repositionStrikeZone();

    // Difficulty curve
    swingMsRef.current = Math.max(SWING_MIN_MS, swingMsRef.current - 35);

    setTimeout(() => setChamberFlash(null), 220);
    setTimeout(() => setVerdict(null), 700);

    // Win/fail checks
    if (heatRef.current >= HEAT_TARGET && !wonRef.current) {
      wonRef.current = true;
      setTimeout(() => onComplete(), 600);
    }
    if (pressureRef.current >= PRESSURE_CAP && !failedRef.current) {
      failedRef.current = true;
      setTimeout(() => onFail(), 500);
    }
  }

  // === Apply ember impact damage ===
  function emberHit() {
    pressureRef.current = Math.min(PRESSURE_CAP + 1, pressureRef.current + 18);
    setCombo(0);
    setChamberFlash('bad');
    emberShakeRef.current = 12;
    setVerdict({ type: 'BURN', x: hammerXRef.current, ts: performance.now() });
    setTimeout(() => setChamberFlash(null), 220);
    setTimeout(() => setVerdict(null), 700);
    if (pressureRef.current >= PRESSURE_CAP && !failedRef.current) {
      failedRef.current = true;
      setTimeout(() => onFail(), 500);
    }
  }

  // === Main game loop ===
  useEffect(() => {
    const SWING_RAIL_Y = 18;        // % from top — hammer rest position
    const STRIKE_Y = 70;            // % from top — anvil/ingot Y

    // Measure ingot bounds and initialise strike zone inside it
    const initIngot = () => {
      measureIngotBounds();
      const b = ingotBoundsRef.current;
      const w = b.width * 0.22;
      strikeZoneRef.current = { x: b.left + b.width / 2, w };
      if (strikeZoneEl.current) {
        strikeZoneEl.current.style.left = strikeZoneRef.current.x + '%';
        strikeZoneEl.current.style.width = strikeZoneRef.current.w + '%';
        strikeZoneEl.current.style.top = b.top + '%';
        strikeZoneEl.current.style.height = (b.bottom - b.top) + '%';
      }
    };
    // Defer until DOM laid out
    requestAnimationFrame(() => { initIngot(); requestAnimationFrame(initIngot); });

    const onResize = () => {
      measureIngotBounds();
      const b = ingotBoundsRef.current;
      // Clamp current zone into new bounds
      let nx = strikeZoneRef.current.x;
      const w = strikeZoneRef.current.w;
      nx = Math.max(b.left + w / 2, Math.min(b.right - w / 2, nx));
      strikeZoneRef.current = { x: nx, w };
      if (strikeZoneEl.current) {
        strikeZoneEl.current.style.left = nx + '%';
        strikeZoneEl.current.style.width = w + '%';
        strikeZoneEl.current.style.top = b.top + '%';
        strikeZoneEl.current.style.height = (b.bottom - b.top) + '%';
      }
    };
    window.addEventListener('resize', onResize);

    const tick = (now) => {
      if (failedRef.current || wonRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Pause game logic while tutorial is up — keep visuals running
      if (showTutorialRef.current) {
        lastStrikeRef.current = now;
        lastEmberSpawnRef.current = now;
        // Still update hammer X for preview movement
        const hxPreview = hammerXRef.current;
        if (hammerRef.current) {
          hammerRef.current.style.left = hxPreview + '%';
          hammerRef.current.style.top = '18%';
          hammerRef.current.style.transform = 'translate(-50%, -50%)';
        }
        if (dropLineRef.current) {
          dropLineRef.current.style.left = hxPreview + '%';
          dropLineRef.current.style.background = 'linear-gradient(180deg, rgba(120,240,255,0.0) 0%, rgba(120,240,255,0.4) 60%, rgba(80,220,255,0.7) 100%)';
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // === HAMMER METRONOME ===
      const elapsed = now - lastStrikeRef.current;
      const cycle = swingMsRef.current;
      const fallStart = cycle - 280; // last 280ms = falling animation
      const fs = fallStateRef.current;

      let hammerY;
      let ringPct;
      if (elapsed < fallStart) {
        // Rising / hover phase
        hammerY = SWING_RAIL_Y;
        ringPct = elapsed / fallStart;
        fs.falling = false;
      } else if (elapsed < cycle) {
        // Falling phase — accelerating ease-in
        const ft = (elapsed - fallStart) / 280;
        const eased = ft * ft;
        hammerY = SWING_RAIL_Y + (STRIKE_Y - SWING_RAIL_Y) * eased;
        ringPct = 1;
        fs.falling = true;
      } else {
        // Strike! Resolve and reset
        hammerY = STRIKE_Y;
        resolveStrike();
        lastStrikeRef.current = now;
      }

      // === Apply hammer DOM update ===
      const hx = hammerXRef.current;
      const shake = emberShakeRef.current > 0
        ? `translateX(${(Math.random() - 0.5) * emberShakeRef.current}px)`
        : '';
      if (hammerRef.current) {
        hammerRef.current.style.left = hx + '%';
        hammerRef.current.style.top = hammerY + '%';
        hammerRef.current.style.transform = `translate(-50%, -50%) ${shake}`;
      }
      // Drop guideline — vertical beam from hammer to anvil showing where it'll land
      if (dropLineRef.current) {
        dropLineRef.current.style.left = hx + '%';
        // Compare hammer X to strike zone center → recolor
        const dist = Math.abs(hx - strikeZoneRef.current.x);
        const half = strikeZoneRef.current.w / 2;
        const aligned = dist <= half;
        dropLineRef.current.style.background = aligned
          ? 'linear-gradient(180deg, rgba(180,255,255,0.0) 0%, rgba(120,240,255,0.55) 60%, rgba(80,220,255,0.85) 100%)'
          : 'linear-gradient(180deg, rgba(255,80,40,0.0) 0%, rgba(255,80,40,0.4) 60%, rgba(255,40,40,0.65) 100%)';
        dropLineRef.current.style.boxShadow = aligned
          ? '0 0 10px rgba(120,240,255,0.6)'
          : '0 0 10px rgba(255,60,40,0.45)';
      }
      if (ringRef.current) {
        const dashOffset = 188 * (1 - ringPct);
        ringRef.current.style.strokeDashoffset = dashOffset;
      }
      if (emberShakeRef.current > 0) emberShakeRef.current = Math.max(0, emberShakeRef.current - 0.6);

      // === EMBER SPAWNING (warn → erupt → impact check) ===
      const sinceSpawn = now - lastEmberSpawnRef.current;
      const targetSpawnInterval = Math.max(800, 1600 - strikeCount * 50);
      if (sinceSpawn > targetSpawnInterval) {
        spawnEmber();
        lastEmberSpawnRef.current = now;
      }

      // Update embers
      const emberLayer = embersLayerRef.current;
      embersRef.current = embersRef.current.filter(e => {
        const age = now - e.bornAt;
        const totalLife = e.warnMs + e.eruptMs + 250;
        if (age > totalLife) {
          if (e.dom) e.dom.remove();
          return false;
        }
        // Lazily create DOM node for ember
        if (!e.dom && emberLayer) {
          const el = document.createElement('div');
          el.style.cssText = `
            position:absolute; left:${e.x}%; bottom:0; width:48px; height:0;
            transform:translateX(-50%); pointer-events:none; will-change:height,opacity;
            background:linear-gradient(180deg, transparent 0%, rgba(255,80,0,0.55) 40%, rgba(255,180,40,0.95) 100%);
            box-shadow:0 0 24px rgba(255,100,20,0.6), 0 0 48px rgba(255,60,0,0.35);
            border-radius:24px 24px 4px 4px; opacity:0;
            mix-blend-mode:screen;
          `;
          emberLayer.appendChild(el);
          e.dom = el;
        }
        if (e.dom) {
          if (age < e.warnMs) {
            // Warn phase: thin pulsing column
            const p = age / e.warnMs;
            const pulse = 0.5 + 0.5 * Math.sin(age * 0.025);
            e.dom.style.height = (12 + p * 18) + '%';
            e.dom.style.opacity = (0.18 + 0.32 * pulse).toFixed(3);
            e.dom.style.width = '14px';
          } else if (age < e.warnMs + e.eruptMs) {
            // Erupt phase: massive geyser
            const p = (age - e.warnMs) / e.eruptMs;
            e.dom.style.height = (35 + p * 45) + '%';
            e.dom.style.opacity = (0.95 - p * 0.2).toFixed(3);
            e.dom.style.width = (40 + p * 24) + 'px';
            // Hit check at peak
            if (!e.hitChecked && p > 0.3) {
              e.hitChecked = true;
              if (Math.abs(hammerXRef.current - e.x) < 5.5) {
                emberHit();
              }
            }
          } else {
            // Decay
            const p = (age - e.warnMs - e.eruptMs) / 250;
            e.dom.style.opacity = (0.8 - p).toFixed(3);
          }
        }
        return true;
      });

      // === HUD throttled UI updates (~12/s) ===
      if (Math.random() < 0.18) {
        setHeatUI(heatRef.current);
        setPressureUI(pressureRef.current);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    lastStrikeRef.current = performance.now();
    lastEmberSpawnRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      embersRef.current.forEach(e => e.dom?.remove());
      embersRef.current = [];
    };
    // eslint-disable-next-line
  }, []);

  // === Sync HUD state on key changes ===
  useEffect(() => {
    setHeatUI(heatRef.current);
    setPressureUI(pressureRef.current);
  }, [strikeCount]);

  return (
    <div ref={containerRef}
      className="relative h-full w-full overflow-hidden select-none"
      data-testid="floor-7"
      style={{ background: '#040000', touchAction: 'none' }}
      onMouseMove={handleMove} onTouchMove={handleMove}>

      <style>{`
        @keyframes f7-anvil-glow { 0%, 100% { filter: drop-shadow(0 -8px 24px rgba(255,80,0,0.4)); } 50% { filter: drop-shadow(0 -12px 36px rgba(255,120,30,0.65)); } }
        @keyframes f7-ingot-pulse { 0%, 100% { box-shadow: 0 0 24px rgba(255,180,80,0.7), 0 0 60px rgba(255,80,20,0.45), 0 0 120px rgba(255,40,0,0.25), inset 0 0 18px rgba(255,255,200,0.85); } 50% { box-shadow: 0 0 38px rgba(255,210,100,0.95), 0 0 90px rgba(255,120,40,0.6), 0 0 180px rgba(255,60,0,0.35), inset 0 0 26px rgba(255,255,220,1); } }
        @keyframes f7-strike-zone-blink { 0%, 100% { opacity: 0.85; } 50% { opacity: 1; } }
        @keyframes f7-rail-shimmer { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.55; } }
        @keyframes f7-verdict-rise {
          0%   { transform: translate(-50%, 0) scale(0.7); opacity: 0; }
          25%  { transform: translate(-50%, -30px) scale(1.15); opacity: 1; }
          70%  { transform: translate(-50%, -60px) scale(1); opacity: 0.85; }
          100% { transform: translate(-50%, -100px) scale(0.95); opacity: 0; }
        }
        @keyframes f7-furnace-throb {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 0.85; }
        }
        @keyframes f7-tutorial-fade {
          0%, 80%  { opacity: 1; }
          100%     { opacity: 0; }
        }
        @keyframes f7-tutorial-pop {
          0%   { opacity: 0; transform: translateY(20px) scale(0.95); filter: blur(6px); }
          100% { opacity: 1; transform: translateY(0) scale(1);    filter: blur(0); }
        }
      `}</style>

      {/* === FURNACE BACKGROUND ATMOSPHERE === */}
      <div className="absolute inset-0" style={{
        backgroundImage: `url(${floor7Bg})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        filter: 'brightness(0.32) saturate(1.3) contrast(1.1) hue-rotate(-3deg)',
      }} />

      {/* Heat haze rising from anvil zone */}
      <div className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 100%, rgba(255,80,0,0.22) 0%, rgba(180,40,0,0.12) 35%, transparent 70%)',
        animation: 'f7-furnace-throb 3.5s ease-in-out infinite',
      }} />

      {/* Crushing edge vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.7) 78%, rgba(0,0,0,0.97) 100%)',
      }} />

      {/* Subtle grid floor */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 32px, rgba(255,80,20,0.4) 32px, rgba(255,80,20,0.4) 33px), repeating-linear-gradient(90deg, transparent, transparent 32px, rgba(255,80,20,0.4) 32px, rgba(255,80,20,0.4) 33px)',
      }} />

      {/* Chamber flash */}
      {chamberFlash === 'good' && (
        <div className="absolute inset-0 z-50 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 60%, rgba(255,180,40,0.18) 0%, transparent 70%)',
        }} />
      )}
      {chamberFlash === 'bad' && (
        <div className="absolute inset-0 z-50 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(255,30,40,0.32) 0%, rgba(120,0,15,0.18) 40%, transparent 80%)',
        }} />
      )}

      {/* === HUD (top header) === */}
      <div className="absolute top-0 left-0 right-0 z-40 px-5 pt-3 pb-2"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.88) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between gap-3">
          {/* HEAT (left) */}
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="text-[8px] tracking-[0.4em] font-mono uppercase" style={{ color: 'rgba(255,180,40,0.5)' }}>Forge Heat</span>
              <span className="font-mono text-sm font-black" style={{ color: '#ffaa40', textShadow: '0 0 8px rgba(255,140,40,0.6)' }}>
                {Math.round(heatUI)}<span className="text-white/15 font-normal">/{HEAT_TARGET}</span>
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-sm overflow-hidden" style={{ background: 'rgba(40,10,0,0.7)', border: '1px solid rgba(255,140,40,0.18)' }}>
              <div className="h-full rounded-sm transition-all duration-150" style={{
                width: `${(heatUI / HEAT_TARGET) * 100}%`,
                background: 'linear-gradient(90deg, #cc4400 0%, #ff8800 50%, #ffcc40 100%)',
                boxShadow: '0 0 10px rgba(255,140,40,0.7)',
              }} />
            </div>
          </div>

          {/* CENTER — Title + combo */}
          <div className="flex flex-col items-center px-4">
            <span className="text-[8px] tracking-[0.5em] font-mono uppercase" style={{ color: 'rgba(255,200,80,0.45)' }}>The Forge</span>
            {combo > 1 && (
              <span className="font-mono text-xs font-black" style={{ color: '#ffe080', textShadow: '0 0 8px rgba(255,200,60,0.7)' }}>
                ×{combo}
              </span>
            )}
          </div>

          {/* PRESSURE (right) */}
          <div className="flex-1">
            <div className="flex items-baseline gap-2 justify-end">
              <span className="font-mono text-sm font-black" style={{ color: pressureUI > 70 ? '#ff5050' : '#cc3030', textShadow: pressureUI > 70 ? '0 0 12px rgba(255,40,60,0.85)' : '0 0 6px rgba(180,20,30,0.5)' }}>
                {Math.round(pressureUI)}<span className="text-white/15 font-normal">/{PRESSURE_CAP}</span>
              </span>
              <span className="text-[8px] tracking-[0.4em] font-mono uppercase" style={{ color: 'rgba(255,40,60,0.55)' }}>Pressure</span>
            </div>
            <div className="mt-1 h-1.5 rounded-sm overflow-hidden" style={{ background: 'rgba(40,0,5,0.7)', border: '1px solid rgba(255,40,60,0.18)' }}>
              <div className="h-full rounded-sm transition-all duration-150" style={{
                width: `${(pressureUI / PRESSURE_CAP) * 100}%`,
                background: pressureUI > 70
                  ? 'linear-gradient(90deg, #ff0040 0%, #ff4060 50%, #ffaa40 100%)'
                  : 'linear-gradient(90deg, #800020 0%, #cc0040 100%)',
                boxShadow: pressureUI > 70 ? '0 0 14px rgba(255,40,60,0.85)' : '0 0 8px rgba(180,20,30,0.5)',
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* === HORIZONTAL HAMMER RAIL (top guideline) === */}
      <div className="absolute left-[3%] right-[3%] z-[8] pointer-events-none"
        style={{
          top: '14%', height: '4px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(120,80,40,0.55) 8%, rgba(160,110,60,0.7) 50%, rgba(120,80,40,0.55) 92%, transparent 100%)',
          borderTop: '1px solid rgba(80,40,15,0.6)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,180,80,0.18)',
          animation: 'f7-rail-shimmer 4s ease-in-out infinite',
        }} />
      {/* Rail bracket bolts */}
      {[8, 26, 50, 74, 92].map((x) => (
        <div key={`bolt-${x}`} className="absolute z-[9] pointer-events-none rounded-full"
          style={{
            left: `${x}%`, top: '14%', width: '6px', height: '6px',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, #806030 0%, #402010 80%)',
            boxShadow: '0 0 4px rgba(0,0,0,0.8)',
          }} />
      ))}

      {/* === EMBER LAYER (rising threats) === */}
      <div ref={embersLayerRef} className="absolute inset-0 z-[10] pointer-events-none" />

      {/* === STRIKE ZONE — screen-space overlay positioned ON the ingot via measured bounds === */}
      <div ref={strikeZoneEl} className="absolute z-[16] pointer-events-none"
        style={{
          left: '50%', top: '60%',
          width: '20%', height: '5%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(180deg, rgba(120,255,255,0.55), rgba(60,200,255,0.35))',
          border: '1.5px solid rgba(180,240,255,0.95)',
          boxShadow: '0 0 14px rgba(120,220,255,0.85), 0 0 30px rgba(80,200,255,0.5), inset 0 0 8px rgba(255,255,255,0.6)',
          borderRadius: '3px',
          animation: 'f7-strike-zone-blink 0.6s ease-in-out infinite',
          transition: 'left 0.18s cubic-bezier(.5,1.6,.4,1), width 0.18s ease',
        }}>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-0.5 h-full" style={{
          background: 'rgba(255,255,255,0.95)',
          boxShadow: '0 0 6px rgba(180,240,255,1)',
        }} />
      </div>

      {/* === ANVIL + INGOT (bottom) === */}
      <div className="absolute left-1/2 z-[15] pointer-events-none"
        style={{ top: '70%', transform: 'translate(-50%, -10%)', width: 'min(60%, 540px)', animation: 'f7-anvil-glow 3.6s ease-in-out infinite' }}>
        {/* Ingot — wide white-hot bar resting on anvil */}
        <div ref={ingotRef} className="relative mx-auto" style={{
          width: '88%', height: '34px',
          margin: '0 auto 0 auto',
          background: 'linear-gradient(180deg, #fff8d0 0%, #ffd060 30%, #ff8020 60%, #cc3000 100%)',
          borderRadius: '4px',
          animation: 'f7-ingot-pulse 1.8s ease-in-out infinite',
        }} />

        {/* Anvil body — drawn with stacked CSS */}
        <div className="relative mx-auto" style={{ width: '100%', marginTop: '-2px' }}>
          {/* Anvil top surface */}
          <div className="mx-auto" style={{
            width: '92%', height: '14px',
            background: 'linear-gradient(180deg, #555 0%, #333 50%, #1a1a1a 100%)',
            borderTop: '1px solid #777',
            borderRadius: '2px',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 2px 6px rgba(0,0,0,0.7)',
          }} />
          {/* Anvil neck */}
          <div className="mx-auto" style={{
            width: '36%', height: '38px',
            marginTop: '-2px',
            background: 'linear-gradient(180deg, #333 0%, #222 100%)',
            clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0 100%)',
          }} />
          {/* Anvil base */}
          <div className="mx-auto" style={{
            width: '70%', height: '20px',
            background: 'linear-gradient(180deg, #2a2a2a 0%, #111 100%)',
            borderRadius: '2px',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 12px rgba(0,0,0,0.85)',
          }} />
        </div>

        {/* Floor scorch glow under anvil */}
        <div className="absolute left-1/2 -bottom-4 -translate-x-1/2 pointer-events-none" style={{
          width: '120%', height: '40px',
          background: 'radial-gradient(ellipse at 50% 0%, rgba(255,80,0,0.4) 0%, rgba(120,0,0,0.2) 35%, transparent 70%)',
          filter: 'blur(6px)',
        }} />
      </div>

      {/* === SPARKS LAYER === */}
      <div ref={sparksLayerRef} className="absolute inset-0 z-[18] pointer-events-none" />

      {/* === DROP GUIDELINE — vertical beam showing where hammer will land === */}
      <div ref={dropLineRef} className="absolute z-[12] pointer-events-none"
        style={{
          left: '50%', top: '14%', bottom: '28%',
          width: '2px',
          transform: 'translateX(-50%)',
          willChange: 'left, background',
          mixBlendMode: 'screen',
        }} />

      {/* === HAMMER (player-controlled X, auto-falling Y) === */}
      <div ref={hammerRef} className="absolute z-[20] pointer-events-none"
        style={{
          left: '50%', top: '18%',
          transform: 'translate(-50%, -50%)',
          willChange: 'left, top, transform',
        }}>
        {/* Swing-timer ring around hammer */}
        <svg width="80" height="80" viewBox="0 0 80 80" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
          <circle cx="40" cy="40" r="30" fill="none" stroke="rgba(255,140,40,0.18)" strokeWidth="2" />
          <circle ref={ringRef} cx="40" cy="40" r="30" fill="none"
            stroke="#ffaa40" strokeWidth="2.5" strokeLinecap="round"
            strokeDasharray="188" strokeDashoffset="188"
            transform="rotate(-90 40 40)"
            style={{ filter: 'drop-shadow(0 0 6px rgba(255,140,40,0.9))' }} />
        </svg>
        {/* Hammer head */}
        <div ref={hammerHeadRef} className="relative" style={{ width: '56px', height: '64px' }}>
          {/* Handle — wooden shaft going up */}
          <div className="absolute left-1/2 -translate-x-1/2" style={{
            top: '-44px', width: '8px', height: '52px',
            background: 'linear-gradient(180deg, #6a3818 0%, #3a1808 50%, #2a1206 100%)',
            borderRadius: '2px',
            border: '1px solid rgba(0,0,0,0.5)',
            boxShadow: '0 0 6px rgba(0,0,0,0.5), inset 0 0 4px rgba(180,120,60,0.3)',
          }} />
          {/* Hammer head — heavy iron block */}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(180deg, #999 0%, #555 30%, #2a2a2a 70%, #111 100%)',
            borderRadius: '4px',
            border: '1.5px solid #444',
            boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.8), 0 0 18px rgba(255,80,0,0.25)',
          }}>
            {/* Brand sigil on hammer face */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-serif font-bold"
              style={{ color: 'rgba(255,140,40,0.7)', fontSize: '24px', textShadow: '0 0 4px rgba(255,80,0,0.8)' }}>†</div>
          </div>
          {/* Hammer Y-axis falling indicator (vertical ghost streak when falling) */}
        </div>
      </div>

      {/* === Live verdict floater (PERFECT/GOOD/MISS) === */}
      {verdict && (
        <div className="absolute z-[30] pointer-events-none font-bold tracking-[0.25em]"
          key={verdict.ts}
          style={{
            left: `${verdict.x}%`, top: '60%',
            fontFamily: "'Cinzel','Cinzel Decorative',serif",
            fontSize: '18px',
            color: verdict.type === 'PERFECT' ? '#fff8d0' : verdict.type === 'GOOD' ? '#ffd060' : '#ff4060',
            textShadow: verdict.type === 'PERFECT' ? '0 0 16px rgba(255,255,200,0.95), 0 0 30px rgba(255,200,60,0.6)' :
                        verdict.type === 'GOOD' ? '0 0 12px rgba(255,180,60,0.85)' :
                        '0 0 14px rgba(255,40,60,0.9)',
            animation: 'f7-verdict-rise 0.9s cubic-bezier(.2,.6,.4,1) forwards',
          }}>
          {verdict.type}
        </div>
      )}

      {/* === TUTORIAL OVERLAY (auto-fades after 5.5s) === */}
      {showTutorial && (
        <div className="absolute inset-0 z-[60] pointer-events-none flex items-center justify-center"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 70%)',
            animation: 'f7-tutorial-fade 5.5s ease-out forwards',
          }}>
          <div className="text-center max-w-md px-6" style={{ animation: 'f7-tutorial-pop 0.7s ease-out' }}>
            <div className="text-[9px] tracking-[0.5em] font-mono uppercase mb-2" style={{ color: 'rgba(255,180,40,0.7)' }}>
              The Forge
            </div>
            <div className="font-bold tracking-[0.15em] mb-5" style={{
              fontFamily: "'Cinzel','Cinzel Decorative',serif",
              fontSize: 'clamp(22px, 4vw, 32px)',
              color: '#ffaa40',
              textShadow: '0 0 18px rgba(255,140,40,0.7), 0 0 40px rgba(255,80,20,0.35)',
            }}>
              FORGE THE SOUL-COIN
            </div>
            <div className="space-y-3 text-left" style={{ color: 'rgba(255,220,180,0.85)', fontSize: '13px', lineHeight: 1.6 }}>
              <div className="flex items-start gap-3">
                <span className="font-mono font-bold mt-0.5" style={{ color: '#80e8ff', minWidth: '18px' }}>1</span>
                <span><b style={{ color: '#80e8ff' }}>Move your mouse</b> — the hammer follows your X position. The cyan vertical line shows where it'll land.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-mono font-bold mt-0.5" style={{ color: '#ffd060', minWidth: '18px' }}>2</span>
                <span>The hammer falls on its own. <b style={{ color: '#ffd060' }}>Align the drop line with the cyan zone</b> on the ingot before it falls. Line stays cyan = aligned.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-mono font-bold mt-0.5" style={{ color: '#ff6040', minWidth: '18px' }}>3</span>
                <span><b style={{ color: '#ff6040' }}>Avoid red ember columns</b> rising from below. They warn before erupting — move the hammer away.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-mono font-bold mt-0.5" style={{ color: '#ffaa40', minWidth: '18px' }}>4</span>
                <span>Fill the <b style={{ color: '#ffaa40' }}>FORGE HEAT bar</b> to 100. If <b style={{ color: '#ff4060' }}>PRESSURE</b> caps first → the forge explodes.</span>
              </div>
            </div>
            <div className="mt-5 text-[8px] tracking-[0.4em] font-mono uppercase" style={{ color: 'rgba(255,200,80,0.45)' }}>
              starting in moments...
            </div>
          </div>
        </div>
      )}

      {/* Bottom hint */}
      <div className="absolute bottom-3 left-0 right-0 z-40 text-center">
        <span className="text-[7px] tracking-[0.35em] font-mono uppercase" style={{ color: 'rgba(255,140,40,0.22)' }}>
          align with cyan zone · avoid the ember columns · forge the soul-coin
        </span>
      </div>
    </div>
  );
}
// Floor 8: Toxic Core — THE MIRROR SIGIL.
// Lore: "Reality inverts at the core. Your instincts will betray you."
// An ornate ring of 8 sigils slowly rotates around a mirror altar.
// One sigil glows as the True Mark — strike it before the rotation carries it away.
// Every ~4 seconds the ring REVERSES and the sigils mirror left-right,
// telegraphed by a Chancellor whisper ("the mirror turns…") and a screen tint.
// Get 10 True Marks. 3 lives. 30 seconds. Lore-driven, single-screen, AAA.
function Floor8({ onComplete, onFail }) {
  const SIGILS = [
    { glyph: '✦', name: 'PENITENT' },
    { glyph: '☩', name: 'CONVICTED' },
    { glyph: '⚜', name: 'BOUND' },
    { glyph: '☉', name: 'WITNESSED' },
    { glyph: '⚝', name: 'DAMNED' },
    { glyph: '⚕', name: 'BURDENED' },
    { glyph: '☥', name: 'ETERNAL' },
    { glyph: '✶', name: 'FORGOTTEN' },
  ];
  const NEEDED = 10;
  const MAX_LIVES = 3;
  const FLOOR_DURATION = 30;
  const RING_RADIUS_PCT = 33; // % of min dimension

  const containerRef = useRef(null);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(MAX_LIVES);
  const [timeLeft, setTimeLeft] = useState(FLOOR_DURATION);
  const [ringAngle, setRingAngle] = useState(0);
  const [direction, setDirection] = useState(1);
  const [mirrored, setMirrored] = useState(false);
  const [trueMarkIdx, setTrueMarkIdx] = useState(0);
  const [whisperText, setWhisperText] = useState('');
  const [whisperVisible, setWhisperVisible] = useState(false);
  const [inversionFlash, setInversionFlash] = useState(false);
  const [tapBurst, setTapBurst] = useState(null);
  const [crackVisible, setCrackVisible] = useState(false);

  // Refs mirror state for tight loops
  const ringAngleRef = useRef(0);
  const directionRef = useRef(1);
  const mirroredRef = useRef(false);
  const scoreRef = useRef(0);
  const livesRef = useRef(MAX_LIVES);
  const failedRef = useRef(false);
  const completedRef = useRef(false);
  const trueMarkIdxRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const frameRef = useRef(null);
  const markTimerRef = useRef(null);
  const inversionTimerRef = useRef(null);

  const pickNewTrueMark = useCallback(() => {
    let next = Math.floor(Math.random() * SIGILS.length);
    // avoid picking same sigil twice in a row
    if (next === trueMarkIdxRef.current) {
      next = (next + 1 + Math.floor(Math.random() * (SIGILS.length - 1))) % SIGILS.length;
    }
    trueMarkIdxRef.current = next;
    setTrueMarkIdx(next);
  }, [SIGILS.length]);

  const triggerInversion = useCallback(() => {
    if (failedRef.current || completedRef.current) return;
    directionRef.current *= -1;
    mirroredRef.current = !mirroredRef.current;
    setDirection(directionRef.current);
    setMirrored(mirroredRef.current);
    const whispers = ['the mirror turns…', 'reality bends…', 'the chancellor watches…', 'your reflection moves…'];
    setWhisperText(whispers[Math.floor(Math.random() * whispers.length)]);
    setWhisperVisible(true);
    setInversionFlash(true);
    setTimeout(() => setInversionFlash(false), 550);
    setTimeout(() => setWhisperVisible(false), 1800);
  }, []);

  // Rotation loop
  useEffect(() => {
    const ROT_SPEED = 0.00065; // rad/ms — slow enough to read sigils
    const loop = (now) => {
      const dt = now - lastTimeRef.current;
      lastTimeRef.current = now;
      ringAngleRef.current += ROT_SPEED * directionRef.current * dt;
      setRingAngle(ringAngleRef.current);
      if (!failedRef.current && !completedRef.current) {
        frameRef.current = requestAnimationFrame(loop);
      }
    };
    lastTimeRef.current = performance.now();
    frameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  // True Mark auto-cycle (player too slow → mark moves on)
  useEffect(() => {
    pickNewTrueMark();
    const cycle = () => {
      if (failedRef.current || completedRef.current) return;
      pickNewTrueMark();
      const next = Math.max(1500, 2500 - scoreRef.current * 90);
      markTimerRef.current = setTimeout(cycle, next);
    };
    markTimerRef.current = setTimeout(cycle, 2500);
    return () => clearTimeout(markTimerRef.current);
  }, [pickNewTrueMark]);

  // Inversion scheduler — every ~4s w/ jitter
  useEffect(() => {
    const schedule = () => {
      if (failedRef.current || completedRef.current) return;
      const delay = 3600 + Math.random() * 1400;
      inversionTimerRef.current = setTimeout(() => {
        triggerInversion();
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(inversionTimerRef.current);
  }, [triggerInversion]);

  // Countdown
  useEffect(() => {
    const t = setInterval(() => {
      setTimeLeft(s => {
        if (s <= 1) {
          clearInterval(t);
          if (!completedRef.current && !failedRef.current && scoreRef.current < NEEDED) {
            failedRef.current = true;
            setTimeout(onFail, 200);
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [onFail]);

  const handleSigilTap = (idx) => {
    if (failedRef.current || completedRef.current) return;
    const correct = idx === trueMarkIdxRef.current;
    if (correct) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
      setTapBurst({ idx, success: true, ts: Date.now() });
      setTimeout(() => setTapBurst(null), 650);
      if (scoreRef.current >= NEEDED && !completedRef.current) {
        completedRef.current = true;
        setTimeout(onComplete, 500);
      } else {
        clearTimeout(markTimerRef.current);
        pickNewTrueMark();
        const next = Math.max(1500, 2500 - scoreRef.current * 90);
        markTimerRef.current = setTimeout(function nextCycle() {
          if (failedRef.current || completedRef.current) return;
          pickNewTrueMark();
          markTimerRef.current = setTimeout(nextCycle, Math.max(1500, 2500 - scoreRef.current * 90));
        }, next);
      }
    } else {
      livesRef.current -= 1;
      setLives(livesRef.current);
      setCrackVisible(true);
      setTimeout(() => setCrackVisible(false), 550);
      setTapBurst({ idx, success: false, ts: Date.now() });
      setTimeout(() => setTapBurst(null), 650);
      if (livesRef.current <= 0 && !failedRef.current) {
        failedRef.current = true;
        setTimeout(onFail, 600);
      }
    }
  };

  // Compute sigil render positions
  const sigilPositions = SIGILS.map((s, i) => {
    const baseAngle = (i / SIGILS.length) * Math.PI * 2 - Math.PI / 2; // start at top
    let renderAngle = baseAngle + ringAngle;
    if (mirrored) renderAngle = Math.PI - renderAngle; // horizontal flip
    const x = 50 + Math.cos(renderAngle) * RING_RADIUS_PCT;
    const y = 50 + Math.sin(renderAngle) * RING_RADIUS_PCT;
    return { ...s, idx: i, x, y, isTrue: i === trueMarkIdx };
  });

  const danger = lives <= 1;

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden select-none"
      data-testid="floor-8"
      style={{
        background: '#020005', touchAction: 'none',
        animation: danger ? 'f8-chamber-tremor 0.22s steps(2) infinite' : 'none',
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Cinzel:wght@400;700&display=swap');
        @keyframes f8-altar-breathe { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.92; } 50% { transform: translate(-50%, -50%) scale(1.04); opacity: 1; } }
        @keyframes f8-altar-rotate { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(360deg); } }
        @keyframes f8-altar-rotate-rev { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(-360deg); } }
        @keyframes f8-aura-pulse { 0%, 100% { opacity: 0.18; transform: translate(-50%, -50%) scale(1); } 50% { opacity: 0.32; transform: translate(-50%, -50%) scale(1.06); } }
        @keyframes f8-orbit-stroke { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -200; } }
        @keyframes f8-true-pulse {
          0%, 100% { filter: drop-shadow(0 0 14px #ffd07a) drop-shadow(0 0 28px rgba(255,200,100,0.7)) drop-shadow(0 0 60px rgba(255,170,60,0.35)); transform: translate(-50%, -50%) scale(1.05); }
          50%      { filter: drop-shadow(0 0 22px #fff0c0) drop-shadow(0 0 44px rgba(255,220,140,0.9)) drop-shadow(0 0 90px rgba(255,180,80,0.55)); transform: translate(-50%, -50%) scale(1.18); }
        }
        @keyframes f8-sigil-idle { 0%, 100% { opacity: 0.78; } 50% { opacity: 0.95; } }
        @keyframes f8-whisper-in {
          0%   { opacity: 0; transform: translateX(-50%) translateY(8px); letter-spacing: 0.8em; filter: blur(6px); }
          25%  { opacity: 1; transform: translateX(-50%) translateY(0); letter-spacing: 0.18em; filter: blur(0); }
          75%  { opacity: 1; transform: translateX(-50%) translateY(0); letter-spacing: 0.18em; filter: blur(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-6px); letter-spacing: 0.32em; filter: blur(3px); }
        }
        @keyframes f8-inversion-tint {
          0%   { opacity: 0; }
          15%  { opacity: 0.9; }
          100% { opacity: 0; }
        }
        @keyframes f8-tap-burst-good {
          0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(3.6); opacity: 0; }
        }
        @keyframes f8-tap-burst-bad {
          0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(3); opacity: 0; }
        }
        @keyframes f8-crack-flash { 0%, 100% { opacity: 0; } 25% { opacity: 0.85; } 60% { opacity: 0.4; } }
        @keyframes f8-chamber-tremor {
          0%, 100% { transform: translate(0, 0); }
          25%      { transform: translate(-2px, 1px); }
          50%      { transform: translate(2px, -1px); }
          75%      { transform: translate(-1px, 2px); }
        }
        @keyframes f8-star-twinkle { 0%, 100% { opacity: 0.15; } 50% { opacity: 0.55; } }
        @keyframes f8-mirror-shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* === COSMIC VOID BACKGROUND === */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, rgba(70,15,140,0.35) 0%, rgba(30,5,70,0.55) 25%, rgba(12,0,28,0.85) 60%, #020005 100%)',
      }} />

      {/* Procedural starfield */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 60 }).map((_, i) => {
          const seed = i * 137.5;
          const x = (seed % 100);
          const y = ((seed * 1.7) % 100);
          const size = 1 + ((i * 7) % 3) * 0.5;
          const dur = 3 + ((i * 3) % 5);
          const delay = -((i * 0.41) % 4);
          return (
            <div key={`star-${i}`} className="absolute rounded-full" style={{
              left: `${x}%`, top: `${y}%`,
              width: `${size}px`, height: `${size}px`,
              background: i % 7 === 0 ? '#ffd070' : '#e0c0ff',
              boxShadow: i % 11 === 0 ? '0 0 6px currentColor' : 'none',
              animation: `f8-star-twinkle ${dur}s ease-in-out ${delay}s infinite`,
            }} />
          );
        })}
      </div>

      {/* Outer aurora glow — drifts behind the ring */}
      <div className="absolute left-1/2 top-1/2 pointer-events-none" style={{
        width: '90%', height: '90%',
        background: 'radial-gradient(circle at 50% 50%, rgba(180,80,255,0.18) 0%, rgba(120,40,200,0.08) 35%, transparent 65%)',
        animation: 'f8-aura-pulse 5s ease-in-out infinite',
        filter: 'blur(40px)',
      }} />

      {/* === CENTRAL MIRROR ALTAR === */}
      <div className="absolute left-1/2 top-1/2 z-10 pointer-events-none"
        style={{
          width: '14%', maxWidth: 130, minWidth: 80, aspectRatio: '1/1',
          animation: 'f8-altar-breathe 3.5s ease-in-out infinite',
        }}>
        {/* Outer ring */}
        <div className="absolute inset-0 rounded-full" style={{
          background: 'conic-gradient(from 0deg, #b915cc, #6b00b8, #e060ff, #4a0080, #b915cc)',
          padding: 2,
          mask: 'radial-gradient(circle, transparent 64%, black 65%)',
          WebkitMask: 'radial-gradient(circle, transparent 64%, black 65%)',
          filter: 'drop-shadow(0 0 12px rgba(185,21,204,0.6))',
        }} />
        {/* Inner mirror disc */}
        <div className="absolute inset-[18%] rounded-full" style={{
          background: 'radial-gradient(circle at 38% 32%, rgba(255,220,255,0.55) 0%, rgba(200,100,255,0.4) 25%, rgba(80,0,140,0.7) 60%, rgba(20,0,40,0.92) 100%)',
          boxShadow: 'inset 0 0 24px rgba(255,200,255,0.25), 0 0 40px rgba(185,21,204,0.35)',
          backgroundSize: '200% 100%',
          animation: 'f8-mirror-shimmer 6s linear infinite',
        }} />
        {/* Center jewel */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
          width: '22%', height: '22%',
          background: 'radial-gradient(circle, #fff0ff 0%, #e090ff 35%, #6b00b8 70%, #2a0050 100%)',
          boxShadow: '0 0 14px rgba(255,200,255,0.8), 0 0 28px rgba(220,120,255,0.5)',
        }} />
      </div>

      {/* === ORBIT PATH RING === */}
      <svg className="absolute inset-0 z-15 pointer-events-none w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="f8-ring-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(220,140,255,0)" />
            <stop offset="92%" stopColor="rgba(220,140,255,0.25)" />
            <stop offset="100%" stopColor="rgba(220,140,255,0)" />
          </radialGradient>
        </defs>
        {/* soft glow ring */}
        <circle cx="50" cy="50" r={RING_RADIUS_PCT}
          fill="none" stroke="url(#f8-ring-glow)" strokeWidth="3" />
        {/* etched orbit */}
        <circle cx="50" cy="50" r={RING_RADIUS_PCT}
          fill="none" stroke="rgba(185,21,204,0.35)" strokeWidth="0.35"
          strokeDasharray="0.6 1.4"
          style={{ animation: `f8-orbit-stroke ${mirrored ? '24s' : '20s'} linear infinite ${direction === -1 ? 'reverse' : 'normal'}` }} />
        {/* secondary thin orbit */}
        <circle cx="50" cy="50" r={RING_RADIUS_PCT + 3}
          fill="none" stroke="rgba(160,80,220,0.08)" strokeWidth="0.2"
          strokeDasharray="0.2 2" />
        <circle cx="50" cy="50" r={RING_RADIUS_PCT - 3}
          fill="none" stroke="rgba(160,80,220,0.06)" strokeWidth="0.2"
          strokeDasharray="0.2 2" />
      </svg>

      {/* === SIGILS === */}
      {sigilPositions.map(s => (
        <button key={`sigil-${s.idx}`}
          onClick={() => handleSigilTap(s.idx)}
          data-testid={`f8-sigil-${s.idx}`}
          className="absolute z-20 select-none cursor-pointer"
          style={{
            left: `${s.x}%`, top: `${s.y}%`,
            transform: 'translate(-50%, -50%)',
            width: 64, height: 64,
            background: s.isTrue
              ? 'radial-gradient(circle, rgba(255,220,140,0.35) 0%, rgba(255,160,60,0.15) 45%, transparent 75%)'
              : 'radial-gradient(circle, rgba(40,10,70,0.7) 0%, rgba(20,5,40,0.5) 55%, transparent 80%)',
            border: s.isTrue ? '1.5px solid rgba(255,210,120,0.8)' : '1px solid rgba(185,21,204,0.35)',
            borderRadius: '50%',
            padding: 0,
            fontFamily: "'Cinzel Decorative', serif",
            fontSize: 30,
            color: s.isTrue ? '#fff4d0' : '#d8b8ff',
            transition: 'background 0.2s, border-color 0.2s, color 0.2s',
            animation: s.isTrue ? 'f8-true-pulse 0.9s ease-in-out infinite' : 'f8-sigil-idle 2.4s ease-in-out infinite',
            textShadow: s.isTrue
              ? '0 0 8px #ffd070, 0 0 16px rgba(255,180,60,0.6)'
              : '0 0 6px rgba(220,180,255,0.4)',
          }}>
          {s.glyph}
        </button>
      ))}

      {/* === TAP BURST EFFECT === */}
      {tapBurst && (() => {
        const s = sigilPositions[tapBurst.idx];
        return (
          <div className="absolute z-30 pointer-events-none rounded-full"
            style={{
              left: `${s.x}%`, top: `${s.y}%`,
              width: 80, height: 80,
              border: tapBurst.success ? '2px solid #ffd070' : '2px solid #ff4060',
              background: tapBurst.success
                ? 'radial-gradient(circle, rgba(255,220,140,0.5) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(255,60,80,0.5) 0%, transparent 70%)',
              animation: tapBurst.success
                ? 'f8-tap-burst-good 0.6s ease-out forwards'
                : 'f8-tap-burst-bad 0.6s ease-out forwards',
              boxShadow: tapBurst.success
                ? '0 0 24px #ffd070, 0 0 48px rgba(255,180,60,0.6)'
                : '0 0 24px #ff4060, 0 0 48px rgba(255,0,40,0.5)',
            }} />
        );
      })()}

      {/* === INVERSION TINT FLASH === */}
      {inversionFlash && (
        <div className="absolute inset-0 z-40 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(220,120,255,0.55) 0%, rgba(140,40,200,0.2) 40%, transparent 75%)',
          animation: 'f8-inversion-tint 0.55s ease-out forwards',
          mixBlendMode: 'screen',
        }} />
      )}

      {/* === CHANCELLOR WHISPER === */}
      {whisperVisible && (
        <div className="absolute left-1/2 z-50 pointer-events-none"
          style={{
            top: '36%',
            transform: 'translateX(-50%)',
            color: '#f0d8ff',
            fontFamily: "'Cinzel', serif",
            fontStyle: 'italic',
            fontSize: 15,
            letterSpacing: '0.18em',
            textShadow: '0 0 12px rgba(220,140,255,0.9), 0 0 28px rgba(180,80,255,0.5)',
            animation: 'f8-whisper-in 1.8s ease-in-out forwards',
            whiteSpace: 'nowrap',
          }}>
          — {whisperText} —
        </div>
      )}

      {/* === CRACK FLASH (wrong sigil) === */}
      {crackVisible && (
        <div className="absolute inset-0 z-35 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 35%, rgba(255,0,60,0.18) 80%, rgba(255,0,40,0.32) 100%)',
          animation: 'f8-crack-flash 0.5s ease-out forwards',
        }} />
      )}

      {/* === HUD === */}
      <div className="absolute top-0 left-0 right-0 z-40 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.78) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          {/* Score */}
          <div>
            <span className="text-[9px] tracking-[0.4em] font-mono uppercase" style={{ color: 'rgba(220,140,255,0.5)' }}>
              The Mirror Sigil
            </span>
            <div className="flex items-center gap-3 mt-1">
              <span className="font-mono text-base font-black" style={{ color: '#e090ff', textShadow: '0 0 8px rgba(220,140,255,0.35)' }}>
                {score}<span className="text-white/15 font-normal">/{NEEDED}</span>
              </span>
              <span className="text-[8px] font-mono tracking-[0.2em]" style={{ color: 'rgba(220,140,255,0.3)' }}>MARKS</span>
            </div>
          </div>

          {/* Lives */}
          <div className="flex items-center gap-1.5">
            {Array.from({ length: MAX_LIVES }).map((_, i) => (
              <div key={`life-${i}`} className="w-2.5 h-2.5 rounded-full transition-all duration-300" style={{
                background: i < lives ? '#e090ff' : 'transparent',
                border: `1px solid ${i < lives ? 'rgba(220,140,255,0.8)' : 'rgba(220,140,255,0.2)'}`,
                boxShadow: i < lives ? '0 0 6px rgba(220,140,255,0.7)' : 'none',
              }} />
            ))}
          </div>

          {/* Timer */}
          <div className="text-right">
            <span className="text-[8px] font-mono tracking-[0.2em]" style={{ color: timeLeft <= 8 ? '#ff5060' : 'rgba(220,140,255,0.4)' }}>
              {timeLeft <= 8 ? 'RECKONING' : 'TIME'}
            </span>
            <div className="font-mono text-base font-black mt-0.5" style={{
              color: timeLeft <= 8 ? '#ff5060' : '#e090ff',
              textShadow: `0 0 8px ${timeLeft <= 8 ? 'rgba(255,80,96,0.55)' : 'rgba(220,140,255,0.35)'}`,
            }}>{timeLeft}</div>
          </div>
        </div>
      </div>

      {/* === BOTTOM LEGEND === */}
      <div className="absolute bottom-3 left-0 right-0 z-40 text-center">
        <span className="text-[8px] font-mono tracking-[0.25em]" style={{ color: 'rgba(220,140,255,0.32)' }}>
          STRIKE THE GLOWING MARK · BEWARE THE MIRROR
        </span>
      </div>

      {/* Danger vignette */}
      {danger && (
        <div className="absolute inset-0 z-5 pointer-events-none transition-opacity duration-500"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(255,0,40,0.12) 80%, rgba(255,0,40,0.28) 100%)',
            opacity: 0.6,
          }} />
      )}
    </div>
  );
}


// Floor 9: The Warden — THE WATCHER'S BLINK.
// Lore: "The Warden never sleeps. It only blinks."
// A monumental iron mask hangs above a cathedral corridor. Its crimson iris is
// the only law. HOLD to advance the soul-wisp upward toward the altar; RELEASE
// the instant the eye opens. Movement under the gaze = caught. One life.
// Single input, single focal point, procedural visuals — no images.
function Floor9({ onComplete, onFail }) {
  const FLOOR_DURATION = 25;            // seconds — tight, no slack
  const TRAVEL_SPEED = 22;              // % per second when holding
  const START_Y = 88;                   // bottom of corridor
  const END_Y = 9;                      // altar threshold
  const POST_OPEN_GRACE_MS = 105;       // razor-thin release window after iris opens

  const containerRef = useRef(null);

  // ─── State ────────────────────────────────────────────────────────────────
  const [eyeState, setEyeState] = useState('blink'); // 'blink' | 'preopen' | 'watch'
  const [playerY, setPlayerY] = useState(START_Y);
  const [timeLeft, setTimeLeft] = useState(FLOOR_DURATION);
  const [isHolding, setIsHolding] = useState(false);
  const [caught, setCaught] = useState(false);
  const [watchCount, setWatchCount] = useState(0);

  // ─── Refs (drive the tight loops) ─────────────────────────────────────────
  const eyeStateRef = useRef('blink');
  const playerYRef = useRef(START_Y);
  const holdingRef = useRef(false);
  const failedRef = useRef(false);
  const completedRef = useRef(false);
  const lastFrameRef = useRef(performance.now());
  const watchEnterRef = useRef(0);
  const rafRef = useRef(null);
  const stateTimerRef = useRef(null);
  const countdownRef = useRef(null);

  // ─── Difficulty curve based on progress (0 → 1 as wisp climbs) ───────────
  // Brutal: late floor cuts move windows in half, watches grow, telegraph shrinks.
  const computeTiming = useCallback(() => {
    const progress = Math.min(1, Math.max(0, (START_Y - playerYRef.current) / (START_Y - END_Y)));
    const blinkMs    = Math.round(1800 - progress * 1100); // 1800 → 700
    const watchMs    = Math.round(1900 + progress * 1000); // 1900 → 2900
    const preOpenMs  = Math.round(340  - progress * 200);  // 340  → 140
    return { blinkMs, watchMs, preOpenMs };
  }, []);

  // ─── State scheduler: blink → preopen → watch → blink ────────────────────
  useEffect(() => {
    const advance = (next) => {
      if (failedRef.current || completedRef.current) return;
      eyeStateRef.current = next;
      setEyeState(next);
      const { blinkMs, watchMs, preOpenMs } = computeTiming();
      if (next === 'blink') {
        stateTimerRef.current = setTimeout(() => advance('preopen'), blinkMs);
      } else if (next === 'preopen') {
        stateTimerRef.current = setTimeout(() => advance('watch'), preOpenMs);
      } else if (next === 'watch') {
        watchEnterRef.current = performance.now();
        setWatchCount(c => c + 1);
        stateTimerRef.current = setTimeout(() => advance('blink'), watchMs);
      }
    };
    // Brief orient window before the first eye opens
    stateTimerRef.current = setTimeout(() => advance('preopen'), 950);
    return () => clearTimeout(stateTimerRef.current);
  }, [computeTiming]);

  // ─── Animation loop ──────────────────────────────────────────────────────
  useEffect(() => {
    const loop = (now) => {
      const dt = Math.min(0.05, (now - lastFrameRef.current) / 1000);
      lastFrameRef.current = now;

      // Advance wisp
      if (holdingRef.current && !failedRef.current && !completedRef.current) {
        playerYRef.current = Math.max(END_Y - 2, playerYRef.current - TRAVEL_SPEED * dt);
        setPlayerY(playerYRef.current);

        // Caught: holding during 'watch', past grace window.
        if (eyeStateRef.current === 'watch') {
          const inWatchMs = now - watchEnterRef.current;
          if (inWatchMs > POST_OPEN_GRACE_MS) {
            failedRef.current = true;
            setCaught(true);
            holdingRef.current = false;
            setIsHolding(false);
            setTimeout(onFail, 900);
            return;
          }
        }

        // Completion
        if (playerYRef.current <= END_Y && !completedRef.current) {
          completedRef.current = true;
          setTimeout(onComplete, 700);
          return;
        }
      }

      if (!failedRef.current && !completedRef.current) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    lastFrameRef.current = performance.now();
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [onComplete, onFail]);

  // ─── Countdown ───────────────────────────────────────────────────────────
  useEffect(() => {
    countdownRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(countdownRef.current);
          if (!completedRef.current && !failedRef.current) {
            failedRef.current = true;
            setTimeout(onFail, 200);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [onFail]);

  // ─── Input ───────────────────────────────────────────────────────────────
  const startHold = useCallback(() => {
    if (failedRef.current || completedRef.current) return;
    holdingRef.current = true;
    setIsHolding(true);
  }, []);
  const endHold = useCallback(() => {
    holdingRef.current = false;
    setIsHolding(false);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' || e.key === ' ') { e.preventDefault(); startHold(); }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space' || e.key === ' ') { e.preventDefault(); endHold(); }
    };
    const onBlur = () => endHold();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [startHold, endHold]);

  // ─── Derived render values ───────────────────────────────────────────────
  const danger = eyeState === 'watch';
  const preopen = eyeState === 'preopen';
  const progress = Math.min(1, (START_Y - playerY) / (START_Y - END_Y));

  return (
    <div ref={containerRef}
      className="relative h-full w-full overflow-hidden select-none"
      data-testid="floor-9"
      onPointerDown={startHold}
      onPointerUp={endHold}
      onPointerLeave={endHold}
      onPointerCancel={endHold}
      style={{
        background: '#020001', touchAction: 'none', cursor: 'pointer',
        animation: caught ? 'f9-shock 0.18s steps(2) 3' : 'none',
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Cinzel+Decorative:wght@700;900&display=swap');
        @keyframes f9-iris-open  {
          0%   { transform: translate(-50%, -50%) scaleY(0.04); opacity: 0.55; filter: blur(0.4px); }
          100% { transform: translate(-50%, -50%) scaleY(1);    opacity: 1;    filter: blur(0); }
        }
        @keyframes f9-iris-close {
          0%   { transform: translate(-50%, -50%) scaleY(1);    opacity: 1; }
          100% { transform: translate(-50%, -50%) scaleY(0.04); opacity: 0.35; }
        }
        @keyframes f9-iris-charge {
          0%   { transform: translate(-50%, -50%) scaleY(0.04); opacity: 0.4; box-shadow: 0 0 0 #ff2030; }
          100% { transform: translate(-50%, -50%) scaleY(1);    opacity: 1;   box-shadow: 0 0 22px #ff2030, 0 0 50px rgba(255,0,40,0.45); }
        }
        @keyframes f9-glare-pulse {
          0%, 100% { opacity: 0.32; transform: translateX(-50%) scaleY(1); }
          50%      { opacity: 0.55; transform: translateX(-50%) scaleY(1.04); }
        }
        @keyframes f9-wisp-pulse {
          0%, 100% { box-shadow: 0 0 8px rgba(255,210,140,0.95), 0 0 22px rgba(255,170,80,0.55), 0 0 50px rgba(255,140,40,0.25); }
          50%      { box-shadow: 0 0 12px rgba(255,230,170,1),   0 0 32px rgba(255,180,90,0.75), 0 0 70px rgba(255,140,40,0.4); }
        }
        @keyframes f9-fog-drift {
          0%   { transform: translateX(-12%); opacity: 0.45; }
          100% { transform: translateX(12%);  opacity: 0.6; }
        }
        @keyframes f9-mote-drift {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          15%  { opacity: 0.5; }
          85%  { opacity: 0.3; }
          100% { transform: translateY(-130%) translateX(8px); opacity: 0; }
        }
        @keyframes f9-shock {
          0%, 100% { transform: translate(0,0); }
          25%      { transform: translate(-3px, 1px); }
          50%      { transform: translate(3px, -1px); }
          75%      { transform: translate(-2px, 2px); }
        }
        @keyframes f9-runes-glow {
          0%, 100% { opacity: 0.32; }
          50%      { opacity: 0.55; }
        }
        @keyframes f9-mask-breathe {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50%      { transform: translateX(-50%) translateY(2px); }
        }
        @keyframes f9-altar-shimmer {
          0%, 100% { opacity: 0.4; }
          50%      { opacity: 0.7; }
        }
        @keyframes f9-shatter-particle {
          0%   { transform: translate(0,0) scale(1); opacity: 1; }
          100% { transform: var(--dx) var(--dy); opacity: 0; }
        }
        @keyframes f9-victory-bloom {
          0%   { opacity: 0; transform: scale(0.4); }
          100% { opacity: 1; transform: scale(2.4); }
        }
      `}</style>

      {/* === CATHEDRAL VOID BACKGROUND === */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 20%, rgba(50,0,8,0.5) 0%, rgba(18,0,4,0.85) 32%, rgba(5,0,2,0.96) 65%, #010000 100%)',
      }} />

      {/* Cathedral pillars — subtle vertical CSS bars */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'repeating-linear-gradient(90deg, transparent 0%, transparent 17%, rgba(60,0,12,0.18) 17.5%, transparent 18%, transparent 33%)',
      }} />

      {/* Perspective floor grid */}
      <div className="absolute bottom-0 left-0 right-0 h-[42%] pointer-events-none" style={{
        background: 'linear-gradient(180deg, transparent 0%, rgba(40,0,8,0.35) 65%, rgba(80,0,16,0.5) 100%)',
        maskImage: 'linear-gradient(180deg, transparent 0%, black 30%)',
        WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, black 30%)',
      }} />
      <svg className="absolute bottom-0 left-0 right-0 pointer-events-none" viewBox="0 0 100 50" preserveAspectRatio="none" style={{ height: '38%', width: '100%' }}>
        {/* Receding floor lines */}
        {[6, 12, 20, 30, 42].map((y, i) => (
          <line key={`fl-${i}`} x1="0" y1={50 - y} x2="100" y2={50 - y}
            stroke="rgba(180,40,60,0.06)" strokeWidth="0.15" />
        ))}
        {/* Vanishing-point converging lines */}
        {[10, 30, 50, 70, 90].map((x, i) => (
          <line key={`vl-${i}`} x1={x} y1="50" x2="50" y2="6"
            stroke="rgba(180,40,60,0.04)" strokeWidth="0.12" />
        ))}
      </svg>

      {/* Rising fog */}
      <div className="absolute left-0 right-0 bottom-0 pointer-events-none" style={{
        height: '32%',
        background: 'radial-gradient(ellipse at 50% 90%, rgba(100,10,20,0.42) 0%, rgba(40,0,10,0.18) 45%, transparent 80%)',
        filter: 'blur(14px)',
        animation: 'f9-fog-drift 9s ease-in-out infinite alternate',
      }} />

      {/* Dust motes */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 26 }).map((_, i) => {
          const seed = i * 73.31;
          const x = (seed % 100);
          const y = 30 + ((seed * 1.31) % 60);
          const dur = 7 + ((i * 3) % 6);
          const delay = -((i * 0.41) % dur);
          return (
            <div key={`mote-${i}`} className="absolute rounded-full" style={{
              left: `${x}%`, top: `${y}%`,
              width: 2, height: 2,
              background: 'rgba(255,240,210,0.6)',
              animation: `f9-mote-drift ${dur}s linear ${delay}s infinite`,
            }} />
          );
        })}
      </div>

      {/* === GLARE CONE (when watching) === */}
      <div className="absolute pointer-events-none transition-opacity duration-300" style={{
        left: '50%',
        top: '18%',
        width: '70%',
        height: '85%',
        transform: 'translateX(-50%)',
        background: 'linear-gradient(180deg, rgba(255,30,60,0.32) 0%, rgba(220,10,40,0.16) 30%, rgba(160,0,28,0.08) 60%, transparent 95%)',
        clipPath: 'polygon(46% 0%, 54% 0%, 100% 100%, 0% 100%)',
        opacity: danger ? 1 : 0,
        filter: 'blur(2px)',
        mixBlendMode: 'screen',
        animation: danger ? 'f9-glare-pulse 1.2s ease-in-out infinite' : 'none',
      }} />

      {/* Pre-open warning glow on corridor */}
      {preopen && (
        <div className="absolute pointer-events-none" style={{
          left: '50%', top: '18%',
          width: '60%', height: '80%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(180deg, rgba(255,30,60,0.12) 0%, transparent 70%)',
          clipPath: 'polygon(46% 0%, 54% 0%, 100% 100%, 0% 100%)',
          mixBlendMode: 'screen',
        }} />
      )}

      {/* === THE WARDEN MASK — pure SVG, monumental === */}
      <div className="absolute left-1/2 z-30 pointer-events-none" style={{
        top: '5%',
        transform: 'translateX(-50%)',
        width: 'min(36vmin, 320px)',
        animation: 'f9-mask-breathe 6s ease-in-out infinite',
      }}>
        <svg viewBox="0 0 100 130" style={{ width: '100%', height: 'auto', display: 'block' }}>
          <defs>
            <linearGradient id="f9-iron" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#3a2a26" />
              <stop offset="40%"  stopColor="#1f1612" />
              <stop offset="70%"  stopColor="#16100c" />
              <stop offset="100%" stopColor="#0c0808" />
            </linearGradient>
            <linearGradient id="f9-iron-rim" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#5a3a30" />
              <stop offset="100%" stopColor="#1a1008" />
            </linearGradient>
            <radialGradient id="f9-iris-grad" cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#ffeae0" />
              <stop offset="20%"  stopColor="#ff5060" />
              <stop offset="55%"  stopColor="#cc0028" />
              <stop offset="100%" stopColor="#3a0008" />
            </radialGradient>
            <filter id="f9-mask-shadow"><feDropShadow dx="0" dy="2" stdDeviation="2.5" floodOpacity="0.7" /></filter>
            <filter id="f9-iris-glow"><feGaussianBlur stdDeviation="0.8" /></filter>
          </defs>

          {/* === CROWN — 5 iron spires === */}
          {[
            { x: 50, h: 22, w: 4 },
            { x: 38, h: 16, w: 3 },
            { x: 62, h: 16, w: 3 },
            { x: 28, h: 11, w: 2.5 },
            { x: 72, h: 11, w: 2.5 },
          ].map((s, i) => (
            <path key={`spire-${i}`}
              d={`M ${s.x - s.w} 22 L ${s.x} ${22 - s.h} L ${s.x + s.w} 22 Z`}
              fill="url(#f9-iron-rim)" />
          ))}

          {/* === DOME / HEAD === */}
          <path d="M 18 60 Q 18 22 50 22 Q 82 22 82 60 L 82 92 Q 82 110 50 116 Q 18 110 18 92 Z"
            fill="url(#f9-iron)" filter="url(#f9-mask-shadow)" />

          {/* Outer rim highlight */}
          <path d="M 18 60 Q 18 22 50 22 Q 82 22 82 60 L 82 92 Q 82 110 50 116 Q 18 110 18 92 Z"
            fill="none" stroke="#5a3a30" strokeWidth="0.6" opacity="0.55" />

          {/* === FOREHEAD BAND with engraved runes === */}
          <rect x="20" y="34" width="60" height="6" fill="#0a0604" />
          <path d="M 20 34 L 80 34 M 20 40 L 80 40" stroke="#3a2018" strokeWidth="0.35" />
          <g style={{ animation: 'f9-runes-glow 5s ease-in-out infinite' }}
             fill="#c08050" fontFamily="'Cinzel Decorative', serif" fontSize="4.5" textAnchor="middle">
            <text x="30" y="38.5">✦</text>
            <text x="42" y="38.5">☩</text>
            <text x="50" y="38.5">⚜</text>
            <text x="58" y="38.5">☩</text>
            <text x="70" y="38.5">✦</text>
          </g>

          {/* === EYE SLIT (horizontal channel) === */}
          <rect x="22" y="56" width="56" height="10" rx="2"
            fill="#020000" stroke="#3a2018" strokeWidth="0.4" />
          {/* Subtle inner shadow */}
          <rect x="23" y="57" width="54" height="8" rx="1.5" fill="#000" opacity="0.65" />

          {/* === IRIS (eye state-driven) === */}
          {eyeState === 'blink' && (
            <g style={{ transformOrigin: '50px 61px', animation: 'f9-iris-close 0.22s ease-in forwards' }}>
              <ellipse cx="50" cy="61" rx="13" ry="3.2" fill="url(#f9-iris-grad)" opacity="0.35" />
              <line x1="38" y1="61" x2="62" y2="61" stroke="#3a0008" strokeWidth="0.6" />
            </g>
          )}
          {eyeState === 'preopen' && (
            <g style={{ transformOrigin: '50px 61px', animation: 'f9-iris-charge 0.4s ease-out forwards' }}>
              <ellipse cx="50" cy="61" rx="13" ry="3.5" fill="url(#f9-iris-grad)"
                filter="url(#f9-iris-glow)" />
              <circle cx="50" cy="61" r="2.5" fill="#ffeed0" opacity="0.75" />
            </g>
          )}
          {eyeState === 'watch' && (
            <g style={{ transformOrigin: '50px 61px', animation: 'f9-iris-open 0.18s ease-out forwards' }}>
              <ellipse cx="50" cy="61" rx="13" ry="3.6" fill="url(#f9-iris-grad)"
                filter="url(#f9-iris-glow)"
                style={{ filter: 'drop-shadow(0 0 4px #ff2040) drop-shadow(0 0 10px rgba(255,0,40,0.55))' }} />
              <circle cx="50" cy="61" r="2" fill="#ffffff" />
            </g>
          )}

          {/* === NOSE BRIDGE === */}
          <path d="M 49 64 L 49 88 L 51 88 L 51 64 Z" fill="#0a0604" />
          <path d="M 49 64 L 49 88" stroke="#3a2018" strokeWidth="0.25" />
          <path d="M 51 64 L 51 88" stroke="#3a2018" strokeWidth="0.25" />

          {/* === CHEEK PLATES + RIVETS === */}
          <path d="M 22 70 Q 22 95 35 105 L 35 70 Z" fill="#1a100c" opacity="0.7" />
          <path d="M 78 70 Q 78 95 65 105 L 65 70 Z" fill="#1a100c" opacity="0.7" />
          {[26, 30, 70, 74].map((x, i) => (
            <circle key={`riv-${i}`} cx={x} cy={i % 2 === 0 ? 76 : 84} r="0.9"
              fill="#3a2418" stroke="#5a3a28" strokeWidth="0.2" />
          ))}

          {/* === LOWER GRILL (mouth) === */}
          {[0, 1, 2, 3, 4, 5].map(i => (
            <rect key={`grill-${i}`} x={42 + i * 2.6} y="98" width="1.5" height="10"
              fill="#000" opacity="0.85" />
          ))}
          <rect x="40" y="96" width="20" height="2" fill="#1a100c" />
        </svg>
      </div>

      {/* === ALTAR (top destination) === */}
      <div className="absolute left-1/2 pointer-events-none z-20" style={{
        top: `${END_Y - 1}%`,
        transform: 'translate(-50%, -100%)',
        width: '38%', maxWidth: 220, minWidth: 140,
        height: 3,
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,210,140,0.7) 50%, transparent 100%)',
        boxShadow: '0 0 20px rgba(255,180,80,0.6), 0 0 40px rgba(255,160,60,0.3)',
        animation: 'f9-altar-shimmer 2.4s ease-in-out infinite',
      }} />

      {/* === SOUL WISP (player) === */}
      {!caught && (
        <div className="absolute z-25 pointer-events-none" style={{
          left: '50%',
          top: `${playerY}%`,
          transform: 'translate(-50%, -50%)',
          width: 22, height: 30,
          background: 'radial-gradient(ellipse at 50% 50%, rgba(255,240,180,0.95) 0%, rgba(255,180,80,0.7) 35%, rgba(255,120,40,0.3) 70%, transparent 100%)',
          borderRadius: '50%',
          animation: 'f9-wisp-pulse 1.4s ease-in-out infinite',
          transition: 'top 0.08s linear',
          filter: isHolding ? 'brightness(1.15)' : 'brightness(1)',
        }} />
      )}

      {/* Wisp trail (subtle vertical streak below wisp) */}
      {!caught && (
        <div className="absolute z-24 pointer-events-none" style={{
          left: '50%',
          top: `${playerY + 1.2}%`,
          transform: 'translateX(-50%)',
          width: 8,
          height: `${Math.max(0, START_Y - playerY) * 0.35}%`,
          background: 'linear-gradient(180deg, rgba(255,200,120,0.6) 0%, rgba(255,170,80,0.18) 50%, transparent 100%)',
          filter: 'blur(2px)',
          opacity: 0.65,
        }} />
      )}

      {/* === SHATTER on caught === */}
      {caught && (
        <div className="absolute z-30 pointer-events-none" style={{
          left: '50%', top: `${playerY}%`, transform: 'translate(-50%, -50%)',
        }}>
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            const dx = Math.cos(a) * 45;
            const dy = Math.sin(a) * 45;
            return (
              <div key={`shard-${i}`} className="absolute rounded-full" style={{
                left: 0, top: 0,
                width: 4, height: 4,
                background: '#ff5060',
                boxShadow: '0 0 8px #ff5060',
                '--dx': `${dx}px`,
                '--dy': `${dy}px`,
                animation: 'f9-shatter-particle 0.65s ease-out forwards',
              }} />
            );
          })}
          <div className="absolute rounded-full" style={{
            left: '-30px', top: '-30px', width: 60, height: 60,
            background: 'radial-gradient(circle, rgba(255,0,40,0.6) 0%, transparent 70%)',
            animation: 'f9-victory-bloom 0.5s ease-out forwards',
          }} />
        </div>
      )}

      {/* === HUD === */}
      <div className="absolute top-0 left-0 right-0 z-40 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.78) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          {/* Title + state */}
          <div>
            <span className="text-[9px] tracking-[0.4em] font-mono uppercase" style={{ color: 'rgba(255,160,120,0.4)' }}>
              The Warden
            </span>
            <p className="text-[8px] tracking-[0.2em] font-mono mt-1 uppercase" style={{
              color: danger ? '#ff5060' : preopen ? '#ffaa40' : 'rgba(255,255,255,0.32)',
              transition: 'color 0.18s',
            }}>
              {caught ? 'CAUGHT' : danger ? '◉ The eye watches — be still' : preopen ? '◐ It opens…' : '◯ Advance'}
            </p>
          </div>

          {/* Progress bar — vertical climb */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <span className="text-[7px] font-mono tracking-[0.25em] mb-1" style={{ color: 'rgba(255,160,120,0.4)' }}>ASCENT</span>
              <div className="w-1.5 h-12 rounded-sm overflow-hidden" style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,160,120,0.18)',
              }}>
                <div className="w-full transition-all duration-200" style={{
                  height: `${progress * 100}%`,
                  marginTop: `${(1 - progress) * 100}%`,
                  background: 'linear-gradient(180deg, #ff5060 0%, #ffa040 100%)',
                  boxShadow: '0 0 6px rgba(255,160,80,0.6)',
                }} />
              </div>
            </div>

            {/* Timer */}
            <div className="text-right">
              <span className="text-[7px] font-mono tracking-[0.25em]" style={{ color: timeLeft <= 8 ? '#ff5060' : 'rgba(255,160,120,0.4)' }}>
                {timeLeft <= 8 ? 'JUDGEMENT' : 'TIME'}
              </span>
              <div className="font-mono text-base font-black mt-0.5" style={{
                color: timeLeft <= 8 ? '#ff5060' : '#ffaa70',
                textShadow: `0 0 8px ${timeLeft <= 8 ? 'rgba(255,80,96,0.55)' : 'rgba(255,160,80,0.35)'}`,
              }}>{timeLeft}</div>
            </div>
          </div>
        </div>
      </div>

      {/* === BOTTOM PROMPT === */}
      <div className="absolute bottom-3 left-0 right-0 z-40 text-center">
        <span className="text-[8px] font-mono tracking-[0.32em]" style={{ color: 'rgba(255,160,120,0.32)' }}>
          HOLD TO ASCEND · RELEASE WHEN THE EYE OPENS
        </span>
        <p className="text-[7px] font-mono mt-1 italic" style={{ color: 'rgba(255,180,140,0.18)' }}>
          watch {watchCount}
        </p>
      </div>

      {/* Danger vignette */}
      {danger && (
        <div className="absolute inset-0 z-5 pointer-events-none transition-opacity duration-300" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(255,0,40,0.08) 70%, rgba(255,0,40,0.18) 100%)',
          opacity: 1,
        }} />
      )}
    </div>
  );
}


// Floor 10: VORAGO ULTIMA — THE PULSE CONVERGENCE.
// Lore: "No more floors below. Only the maze. Only the clock.
//        The Chancellor's gaze turns elsewhere — this last trial is between you
//        and the dark itself."
//
// The Abyss closes in. The player is the last light at center. Concentric rings
// of red death pulse inward from the void — each broken by a single narrow gap.
// Rotate the soul-wisp's orbit angle (cursor / touch / arrow keys) so the gap
// passes through it. Miss the gap = caught. One life. 75 seconds. Vorago tier.
function Floor10({ onComplete, onFail }) {
  // ─── Constants ──────────────────────────────────────────────────────────
  const FLOOR_DURATION = 75;          // seconds
  const ORBIT_RADIUS   = 22;          // % of viewbox (100)
  const SPAWN_RADIUS   = 58;          // outside the visible 100x100 viewBox
  const WISP_HALF_DEG  = 1.5;         // wisp angular half-width — tighter than visual

  // ─── State ──────────────────────────────────────────────────────────────
  const [timeLeft, setTimeLeft]     = useState(FLOOR_DURATION);
  const [ringsSurvived, setRingsSurvived] = useState(0);
  const [phase, setPhase]           = useState('opening'); // 'opening' | 'play' | 'death' | 'win'
  const [angle, setAngle]           = useState(-Math.PI / 2); // wisp orbit angle (rad)
  const [inversionActive, setInversionActive] = useState(false);
  const [whisper, setWhisper]       = useState('');

  // ─── Refs (tight loops) ─────────────────────────────────────────────────
  const containerRef    = useRef(null);
  const ringsRef        = useRef([]);
  const ringIdRef       = useRef(0);
  const lastSpawnRef    = useRef(0);
  const startedAtRef    = useRef(0);
  const angleRef        = useRef(-Math.PI / 2);
  const trailRef        = useRef([]);
  const inversionRef    = useRef(false);
  const inversionEndRef = useRef(0);
  const lastInversionRef = useRef(0);
  const failedRef       = useRef(false);
  const wonRef          = useRef(false);
  const rafRef          = useRef(null);
  const [, force]       = useState(0); // throttled re-render tick

  // Cleanup all timers on unmount
  useEffect(() => () => {
    cancelAnimationFrame(rafRef.current);
  }, []);

  // ─── Cinematic opening (1.4s) then start ────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      if (failedRef.current || wonRef.current) return;
      setPhase('play');
      startedAtRef.current = performance.now();
      lastSpawnRef.current = performance.now();
      lastInversionRef.current = performance.now() + 32000; // first inversion ~32s in
    }, 1400);
    return () => clearTimeout(t);
  }, []);

  // ─── Pointer / touch → wisp angle ───────────────────────────────────────
  useEffect(() => {
    const updateFromPoint = (clientX, clientY) => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top  + r.height / 2;
      let a = Math.atan2(clientY - cy, clientX - cx);
      if (inversionRef.current) {
        // Horizontal flip — left becomes right
        a = Math.PI - a;
      }
      angleRef.current = a;
      setAngle(a);
    };
    const onMove = (e) => updateFromPoint(e.clientX, e.clientY);
    const onTouch = (e) => {
      if (e.touches && e.touches[0]) {
        updateFromPoint(e.touches[0].clientX, e.touches[0].clientY);
      }
    };
    const onKey = (e) => {
      // Optional keyboard control — fine-tune angle
      const STEP = 0.18; // ~10°
      if (e.key === 'ArrowLeft')  angleRef.current -= STEP;
      if (e.key === 'ArrowRight') angleRef.current += STEP;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        setAngle(angleRef.current);
        e.preventDefault();
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // ─── Difficulty curve ───────────────────────────────────────────────────
  const computeDifficulty = useCallback((elapsedSec) => {
    const t = Math.min(1, elapsedSec / FLOOR_DURATION);
    // Spawn cadence: 2.0s → 0.7s
    const spawnIntervalMs = Math.round(2000 - t * 1300);
    // Ring lifetime (edge → center): 4.2s → 2.4s
    const lifetimeMs = Math.round(4200 - t * 1800);
    // Gap arc: 18° → 9°
    const gapDeg = 18 - t * 9;
    // Stroke width: thicker → thinner
    const stroke = 1.1 - t * 0.35;
    return { spawnIntervalMs, lifetimeMs, gapDeg, stroke };
  }, []);

  // ─── Spawn ring helper ──────────────────────────────────────────────────
  const spawnRing = useCallback((now, elapsedSec) => {
    const { lifetimeMs, gapDeg } = computeDifficulty(elapsedSec);
    // Pick gap center — biased away from current wisp angle so player must move
    const wispDeg = (angleRef.current * 180 / Math.PI + 360) % 360;
    const oppDeg = (wispDeg + 180 + (Math.random() - 0.5) * 90) % 360;
    const id = ringIdRef.current++;
    ringsRef.current.push({
      id,
      spawnedAt: now,
      lifetime: lifetimeMs,
      gapCenterDeg: oppDeg,
      gapDeg,
      // Monochrome palette: 0% saturation rings (pure silver/white).
      // Every 5th ring gets a faint cyan-blue tint for variety.
      hue: id % 5 === 4 ? 210 : 0,
      sat: id % 5 === 4 ? 30 : 0,
      passed: false,
      checked: false,
      warned: false,
    });
  }, [computeDifficulty]);

  // ─── Main game loop ─────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'play') return;
    let mounted = true;
    // Track total milliseconds spent in inversion freezes so all timing
    // refs (ring ages, spawn cadence, countdown) shift forward instead of
    // letting the player die during the dramatic effect.
    let inversionEnteredAt = 0;
    const loop = () => {
      if (!mounted) return;
      const now = performance.now();

      // ─── Inversion freeze handling ──────────────────────────────────
      // When inversion is active, freeze the world: don't age rings, don't
      // spawn, don't tick the gameplay clock — just play the effect.
      if (inversionRef.current) {
        if (!inversionEnteredAt) inversionEnteredAt = now;
        if (now > inversionEndRef.current) {
          // Effect just ended → roll all timing refs forward by the freeze
          // duration so the player resumes exactly where they were.
          const freezeMs = now - inversionEnteredAt;
          inversionRef.current = false;
          setInversionActive(false);
          startedAtRef.current += freezeMs;
          lastSpawnRef.current  += freezeMs;
          lastInversionRef.current += freezeMs;
          ringsRef.current.forEach(r => { r.spawnedAt += freezeMs; });
          inversionEnteredAt = 0;
        } else {
          // Mid-freeze: just keep the trail samples decaying and repaint.
          trailRef.current.push({ a: angleRef.current, t: now });
          if (trailRef.current.length > 8) trailRef.current.shift();
          force(v => (v + 1) & 0xffff);
          if (!failedRef.current && !wonRef.current) {
            rafRef.current = requestAnimationFrame(loop);
          }
          return;
        }
      }

      const elapsedSec = (now - startedAtRef.current) / 1000;

      // Schedule next inversion every ~13s after 32s mark
      if (!inversionRef.current && now >= lastInversionRef.current && elapsedSec > 30) {
        inversionRef.current = true;
        setInversionActive(true);
        inversionEndRef.current = now + 1500;
        lastInversionRef.current = now + 13000 + Math.random() * 4000;
        const w = ['the dark remembers…', 'your reflection turns…', 'the abyss answers…'];
        setWhisper(w[Math.floor(Math.random() * w.length)]);
        setTimeout(() => setWhisper(''), 1700);
      }

      // Spawn rings
      const diff = computeDifficulty(elapsedSec);
      if (now - lastSpawnRef.current >= diff.spawnIntervalMs) {
        spawnRing(now, elapsedSec);
        lastSpawnRef.current = now;
      }

      // Wisp trail (sample every frame, keep last 8)
      trailRef.current.push({ a: angleRef.current, t: now });
      if (trailRef.current.length > 8) trailRef.current.shift();

      // Update rings & check collisions
      const wispDeg = (((angleRef.current * 180 / Math.PI) % 360) + 360) % 360;
      ringsRef.current = ringsRef.current.filter(ring => {
        const age = now - ring.spawnedAt;
        const progress = Math.min(1, age / ring.lifetime);
        // radius: SPAWN_RADIUS → 0 (center)
        ring.radius = SPAWN_RADIUS * (1 - progress);

        // Warning when close to wisp orbit
        if (!ring.warned && ring.radius <= ORBIT_RADIUS + 6 && ring.radius >= ORBIT_RADIUS - 2) {
          ring.warned = true;
        }

        // Collision check: when ring radius crosses wisp orbit
        if (!ring.checked && ring.radius <= ORBIT_RADIUS + 0.6) {
          ring.checked = true;
          // Compute gap range
          const half = ring.gapDeg / 2;
          const gapLo = (ring.gapCenterDeg - half + 360) % 360;
          const gapHi = (ring.gapCenterDeg + half + 360) % 360;
          // Wisp must be within [gapLo, gapHi] (handle wrap)
          let inGap;
          if (gapLo < gapHi) {
            inGap = wispDeg >= gapLo - WISP_HALF_DEG && wispDeg <= gapHi + WISP_HALF_DEG;
          } else {
            inGap = wispDeg >= gapLo - WISP_HALF_DEG || wispDeg <= gapHi + WISP_HALF_DEG;
          }
          if (inGap) {
            ring.passed = true;
            setRingsSurvived(s => s + 1);
          } else if (!failedRef.current) {
            failedRef.current = true;
            ring.killed = true;
            setPhase('death');
            setTimeout(onFail, 1400);
          }
        }
        // Remove rings that have collapsed to nothing
        return ring.radius > -2 && age < ring.lifetime + 200;
      });

      // Throttled rerender — every frame; rings array is mutable but
      // we need React to repaint. Force tick.
      force(v => (v + 1) & 0xffff);

      if (!failedRef.current && !wonRef.current) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { mounted = false; cancelAnimationFrame(rafRef.current); };
  }, [phase, computeDifficulty, spawnRing, onFail]);

  // ─── Countdown ──────────────────────────────────────────────────────────
  // Ticks once per second, but skips while inversion is active so the
  // dramatic effect doesn't eat the player's clock.
  useEffect(() => {
    if (phase !== 'play') return;
    const t = setInterval(() => {
      if (inversionRef.current) return; // freeze the clock during inversion
      setTimeLeft(s => {
        if (s <= 1) {
          clearInterval(t);
          if (!failedRef.current && !wonRef.current) {
            wonRef.current = true;
            setPhase('win');
            setTimeout(onComplete, 1600);
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [phase, onComplete]);

  // ─── Render helpers ─────────────────────────────────────────────────────
  const wispX = 50 + ORBIT_RADIUS * Math.cos(angle);
  const wispY = 50 + ORBIT_RADIUS * Math.sin(angle);

  // Convert ring → SVG path for arc with gap
  const ringPath = (radius, gapCenterDeg, gapDeg) => {
    const half = gapDeg / 2;
    const startDeg = gapCenterDeg + half;          // arc starts after the gap
    const endDeg   = gapCenterDeg - half + 360;    // arc ends before the gap (one full loop)
    const startRad = startDeg * Math.PI / 180;
    const endRad   = endDeg   * Math.PI / 180;
    const x1 = 50 + radius * Math.cos(startRad);
    const y1 = 50 + radius * Math.sin(startRad);
    const x2 = 50 + radius * Math.cos(endRad);
    const y2 = 50 + radius * Math.sin(endRad);
    const sweep = (endDeg - startDeg + 360) % 360;
    const large = sweep > 180 ? 1 : 0;
    return `M ${x1.toFixed(3)} ${y1.toFixed(3)} A ${radius} ${radius} 0 ${large} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`;
  };

  return (
    <div ref={containerRef}
      className="relative h-full w-full overflow-hidden select-none"
      data-testid="floor-10"
      style={{
        background: '#000', cursor: 'none', touchAction: 'none',
        animation: phase === 'death'
          ? 'f10-death-shake 0.12s steps(3) 4'
          : inversionActive
            ? 'f10-inversion-shake 0.45s ease-in-out 3'
            : 'none',
      }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Cinzel+Decorative:wght@700;900&display=swap');
        @keyframes f10-center-rotate { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }
        @keyframes f10-center-pulse { 0%,100% { opacity: 0.55; } 50% { opacity: 0.85; } }
        @keyframes f10-wisp-pulse {
          0%,100% { filter: drop-shadow(0 0 6px #ffffff) drop-shadow(0 0 14px rgba(220,235,255,0.75)) drop-shadow(0 0 30px rgba(160,200,240,0.4)); }
          50%     { filter: drop-shadow(0 0 10px #ffffff) drop-shadow(0 0 22px rgba(240,250,255,0.95)) drop-shadow(0 0 52px rgba(180,210,240,0.6)); }
        }
        @keyframes f10-opening-bloom {
          0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.2); }
          50%  { opacity: 1; }
          100% { opacity: 0.0; transform: translate(-50%,-50%) scale(7); }
        }
        @keyframes f10-orbit-glow { 0%,100% { opacity: 0.22; } 50% { opacity: 0.42; } }
        @keyframes f10-star-twinkle { 0%,100% { opacity: 0.12; } 50% { opacity: 0.55; } }
        @keyframes f10-inversion-tint {
          0%   { opacity: 0; }
          12%  { opacity: 0.95; }
          40%  { opacity: 0.55; }
          80%  { opacity: 0.35; }
          100% { opacity: 0; }
        }
        @keyframes f10-inversion-shockwave {
          0%   { opacity: 0.95; transform: translate(-50%,-50%) scale(0.1); }
          80%  { opacity: 0.55; }
          100% { opacity: 0; transform: translate(-50%,-50%) scale(8); }
        }
        @keyframes f10-inversion-border {
          0%   { opacity: 0; box-shadow: inset 0 0 0px rgba(255,255,255,0); }
          15%  { opacity: 1; box-shadow: inset 0 0 80px 4px rgba(240,250,255,0.92), inset 0 0 200px rgba(190,220,250,0.5); }
          60%  { opacity: 1; box-shadow: inset 0 0 100px 6px rgba(220,235,250,0.78), inset 0 0 250px rgba(180,210,240,0.4); }
          100% { opacity: 0; box-shadow: inset 0 0 0px rgba(255,255,255,0); }
        }
        @keyframes f10-inversion-shake {
          0%, 100% { transform: translate(0,0); }
          15%      { transform: translate(-6px, 3px); }
          30%      { transform: translate(7px, -3px); }
          45%      { transform: translate(-4px, -3px); }
          60%      { transform: translate(5px, 4px); }
          75%      { transform: translate(-3px, 2px); }
        }
        @keyframes f10-inversion-banner {
          0%   { opacity: 0; transform: translate(-50%,-50%) scaleY(0.05) scaleX(1.8); letter-spacing: 0.95em; filter: blur(8px); }
          18%  { opacity: 1; transform: translate(-50%,-50%) scaleY(1) scaleX(1);    letter-spacing: 0.32em; filter: blur(0); }
          72%  { opacity: 1; transform: translate(-50%,-50%) scaleY(1) scaleX(1);    letter-spacing: 0.32em; filter: blur(0); }
          100% { opacity: 0; transform: translate(-50%,-50%) scaleY(0.05) scaleX(1.5); letter-spacing: 0.7em;  filter: blur(6px); }
        }
        @keyframes f10-inversion-chromar {
          0%, 100% { transform: translateX(0); opacity: 0; }
          15%      { transform: translateX(-6px); opacity: 0.7; }
          30%      { transform: translateX(5px);  opacity: 0.6; }
          50%      { transform: translateX(-3px); opacity: 0.45; }
          80%      { transform: translateX(2px);  opacity: 0.25; }
        }
        @keyframes f10-inversion-chromab {
          0%, 100% { transform: translateX(0); opacity: 0; }
          15%      { transform: translateX(6px); opacity: 0.7; }
          30%      { transform: translateX(-5px); opacity: 0.6; }
          50%      { transform: translateX(3px);  opacity: 0.45; }
          80%      { transform: translateX(-2px); opacity: 0.25; }
        }
        @keyframes f10-scanline-sweep {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        @keyframes f10-cursor-flip {
          0%, 100% { opacity: 0; transform: translate(-50%,-50%) scale(0.6); }
          25%      { opacity: 1; transform: translate(-50%,-50%) scale(1.1); }
          75%      { opacity: 1; transform: translate(-50%,-50%) scale(1); }
        }
        @keyframes f10-whisper-in {
          0%   { opacity: 0; transform: translateX(-50%) translateY(8px); letter-spacing: 0.8em; filter: blur(6px); }
          25%  { opacity: 1; transform: translateX(-50%) translateY(0);    letter-spacing: 0.22em; filter: blur(0); }
          80%  { opacity: 1; transform: translateX(-50%) translateY(0);    letter-spacing: 0.22em; filter: blur(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-6px); letter-spacing: 0.36em; filter: blur(3px); }
        }
        @keyframes f10-death-shake {
          0%,100% { transform: translate(0,0); }
          25%     { transform: translate(-4px,2px); }
          50%     { transform: translate(4px,-2px); }
          75%     { transform: translate(-2px,4px); }
        }
        @keyframes f10-victory-radiance {
          0%   { opacity: 0; transform: scale(0.3); }
          100% { opacity: 1; transform: scale(3.5); }
        }
        @keyframes f10-impact-burst {
          0%   { opacity: 1; transform: scale(0.5); }
          100% { opacity: 0; transform: scale(2.4); }
        }
      `}</style>

      {/* === DEEP MONOCHROME VOID === */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at 50% 50%, rgba(40,48,60,0.45) 0%, rgba(15,18,24,0.85) 30%, rgba(4,5,8,0.97) 65%, #000 100%)',
      }} />

      {/* Procedural starfield — pure white / pale silver */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 80 }).map((_, i) => {
          const seed = i * 137.508;
          const x = (seed % 100);
          const y = ((seed * 1.61) % 100);
          const sz = 0.8 + ((i * 7) % 3) * 0.4;
          const dur = 3.5 + ((i * 3) % 5);
          const delay = -((i * 0.41) % 4);
          return (
            <div key={`star-${i}`} className="absolute rounded-full" style={{
              left: `${x}%`, top: `${y}%`,
              width: sz, height: sz,
              background: i % 9 === 0 ? '#ffffff' : '#e8eff7',
              boxShadow: i % 13 === 0 ? '0 0 5px currentColor' : 'none',
              animation: `f10-star-twinkle ${dur}s ease-in-out ${delay}s infinite`,
            }} />
          );
        })}
      </div>

      {/* === ARENA SVG (square, centered, viewBox 0..100) ===
          During inversion the entire arena mirrors horizontally — visualises
          the cursor-flip the player just experienced. */}
      <svg className="absolute left-1/2 top-1/2 pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: 'min(100vmin, 100%)',
          height: 'min(100vmin, 100%)',
          transform: `translate(-50%,-50%) scaleX(${inversionActive ? -1 : 1})`,
          overflow: 'visible',
          transition: 'transform 0.35s cubic-bezier(0.6, -0.2, 0.4, 1.2)',
          filter: inversionActive ? 'brightness(1.6) contrast(1.15) drop-shadow(0 0 8px rgba(240,250,255,0.6))' : 'none',
        }}>
        <defs>
          <radialGradient id="f10-center-sigil" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#ffffff" />
            <stop offset="35%" stopColor="#d8dee8" />
            <stop offset="70%" stopColor="#3a3e48" />
            <stop offset="100%" stopColor="#06080c" />
          </radialGradient>
          <radialGradient id="f10-wisp" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#ffffff" />
            <stop offset="30%" stopColor="#f0f4fa" />
            <stop offset="65%" stopColor="#90a0b8" />
            <stop offset="100%" stopColor="rgba(180,200,220,0)" />
          </radialGradient>
        </defs>

        {/* === ORBIT PATH (etched ring at wisp's radius) === */}
        <circle cx="50" cy="50" r={ORBIT_RADIUS}
          fill="none" stroke="rgba(220,230,245,0.22)" strokeWidth="0.18"
          strokeDasharray="0.4 1.2"
          style={{ animation: 'f10-orbit-glow 4s ease-in-out infinite' }} />
        <circle cx="50" cy="50" r={ORBIT_RADIUS - 1.5}
          fill="none" stroke="rgba(220,230,245,0.10)" strokeWidth="0.12" />
        <circle cx="50" cy="50" r={ORBIT_RADIUS + 1.5}
          fill="none" stroke="rgba(220,230,245,0.10)" strokeWidth="0.12" />

        {/* === CENTER ABYSS SIGIL === */}
        <g style={{ transformOrigin: '50px 50px', animation: 'f10-center-pulse 3s ease-in-out infinite' }}>
          {/* outer rim */}
          <circle cx="50" cy="50" r="8" fill="none" stroke="rgba(220,230,245,0.55)" strokeWidth="0.25" strokeDasharray="0.6 1.8" />
          {/* rotating sigil ring */}
          <g style={{ transformOrigin: '50px 50px', animation: 'f10-center-rotate 20s linear infinite' }}>
            <g fill="rgba(240,246,255,0.78)" fontFamily="'Cinzel Decorative', serif" fontSize="2.2" textAnchor="middle">
              {[0, 60, 120, 180, 240, 300].map(deg => {
                const r = 6.5;
                const rad = deg * Math.PI / 180;
                const x = 50 + r * Math.cos(rad);
                const y = 50 + r * Math.sin(rad) + 0.7;
                return <text key={deg} x={x} y={y} transform={`rotate(${deg + 90} ${x} ${y})`}>✦</text>;
              })}
            </g>
          </g>
          {/* core */}
          <circle cx="50" cy="50" r="3.2" fill="url(#f10-center-sigil)" />
          <circle cx="50" cy="50" r="1" fill="#ffffff" />
        </g>

        {/* === RINGS === */}
        {ringsRef.current.map(ring => {
          if (!ring.radius || ring.radius < 0.6) return null;
          const diff = computeDifficulty((performance.now() - startedAtRef.current) / 1000);
          const isCritical = ring.radius <= ORBIT_RADIUS + 3 && ring.radius >= ORBIT_RADIUS - 1;
          const isAfterCross = ring.passed && ring.radius < ORBIT_RADIUS - 0.5;
          const opacity = isAfterCross ? 0.2 : 1;
          const stroke = isCritical ? diff.stroke + 0.4 : diff.stroke;
          const sat = ring.sat ?? 0;
          const color = ring.killed
            ? '#ffffff'
            : ring.passed
              ? `hsla(${ring.hue}, ${sat}%, 78%, 0.45)`
              : `hsl(${ring.hue}, ${sat}%, 92%)`;
          const glowColor = `hsla(${ring.hue}, ${sat}%, 88%,`;
          return (
            <g key={ring.id} opacity={opacity}>
              {/* Outer glow ring */}
              <path d={ringPath(ring.radius, ring.gapCenterDeg, ring.gapDeg)}
                fill="none"
                stroke={color}
                strokeWidth={stroke + 1.4}
                strokeLinecap="round"
                opacity="0.22"
                style={{ filter: 'blur(0.6px)' }} />
              {/* Main ring */}
              <path d={ringPath(ring.radius, ring.gapCenterDeg, ring.gapDeg)}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeLinecap="round"
                style={{
                  filter: isCritical
                    ? `drop-shadow(0 0 1.6px ${glowColor}1)) drop-shadow(0 0 4px ${glowColor}0.75))`
                    : `drop-shadow(0 0 0.8px ${glowColor}0.9))`,
                }} />
            </g>
          );
        })}

        {/* === WISP TRAIL === */}
        {trailRef.current.map((t, i) => {
          const x = 50 + ORBIT_RADIUS * Math.cos(t.a);
          const y = 50 + ORBIT_RADIUS * Math.sin(t.a);
          const opacity = (i / trailRef.current.length) * 0.45;
          return (
            <circle key={`trail-${t.t}`} cx={x} cy={y} r={1.1 - i * 0.08}
              fill="#e8eff7" opacity={opacity}
              style={{ filter: 'blur(0.3px)' }} />
          );
        })}

        {/* === WISP === */}
        {phase !== 'death' && (
          <g style={{ animation: 'f10-wisp-pulse 1.1s ease-in-out infinite' }}>
            <circle cx={wispX} cy={wispY} r="3.5" fill="url(#f10-wisp)" opacity="0.65" />
            <circle cx={wispX} cy={wispY} r="1.6" fill="#ffffff" />
            <circle cx={wispX} cy={wispY} r="0.7" fill="#ffffff" />
          </g>
        )}

        {/* === DEATH SHATTER === */}
        {phase === 'death' && (
          <g style={{ transformOrigin: `${wispX}px ${wispY}px` }}>
            {Array.from({ length: 18 }).map((_, i) => {
              const a = (i / 18) * Math.PI * 2;
              const dx = Math.cos(a) * 14;
              const dy = Math.sin(a) * 14;
              return (
                <circle key={`shard-${i}`}
                  cx={wispX + dx} cy={wispY + dy} r="0.6"
                  fill="#ff5060"
                  opacity="0.85"
                  style={{
                    filter: 'drop-shadow(0 0 1px #ff3040)',
                  }} />
              );
            })}
            <circle cx={wispX} cy={wispY} r="4" fill="rgba(255,0,40,0.5)"
              style={{ filter: 'blur(2px)' }} />
          </g>
        )}

        {/* === VICTORY RADIANCE === */}
        {phase === 'win' && (
          <g style={{ transformOrigin: '50px 50px', animation: 'f10-victory-radiance 1.5s ease-out forwards' }}>
            <circle cx="50" cy="50" r="6" fill="rgba(255,255,255,0.85)" />
            <circle cx="50" cy="50" r="3" fill="rgba(255,255,255,1)" />
          </g>
        )}
      </svg>

      {/* === OPENING BLOOM === */}
      {phase === 'opening' && (
        <div className="absolute left-1/2 top-1/2 pointer-events-none rounded-full" style={{
          width: '8vmin', height: '8vmin',
          background: 'radial-gradient(circle, rgba(255,255,255,0.7) 0%, rgba(220,232,248,0.32) 35%, transparent 70%)',
          animation: 'f10-opening-bloom 1.4s ease-out forwards',
        }} />
      )}

      {/* === INVERSION EFFECT STACK ===
          Layered effects fire together to make the inversion *unmissable*:
            1. Full-screen radial purple tint
            2. Expanding shockwave from center
            3. Inner purple border-glow pulsing inward
            4. Vertical scanline sweep (CRT mirror glitch)
            5. Chromatic-aberration twin offset on the title
            6. Massive "INVERSION" banner text at center
            7. Cursor-flip glyph below the banner
            8. Arena scaleX(-1) + hue-rotate (applied on SVG above)
            9. Screen shake (applied on container) */}
      {inversionActive && (
        <>
          {/* 1. Radial tint — icy white-blue */}
          <div className="absolute inset-0 pointer-events-none z-30" style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(240,250,255,0.8) 0%, rgba(200,220,245,0.4) 30%, rgba(140,170,210,0.18) 60%, transparent 85%)',
            animation: 'f10-inversion-tint 1.5s ease-out forwards',
            mixBlendMode: 'screen',
          }} />

          {/* 2. Shockwave (expanding ring from center) */}
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-31 rounded-full" style={{
            width: '14vmin', height: '14vmin',
            border: '3px solid rgba(255,255,255,0.98)',
            boxShadow: '0 0 26px rgba(240,250,255,0.95), inset 0 0 22px rgba(220,235,250,0.7)',
            animation: 'f10-inversion-shockwave 1.5s ease-out forwards',
          }} />

          {/* 3. Inner border-glow on full container */}
          <div className="absolute inset-0 pointer-events-none z-32" style={{
            animation: 'f10-inversion-border 1.5s ease-out forwards',
          }} />

          {/* 4. CRT scanline sweep */}
          <div className="absolute inset-0 pointer-events-none z-33 overflow-hidden">
            <div style={{
              position: 'absolute', left: 0, right: 0,
              height: '14%',
              background: 'linear-gradient(180deg, transparent 0%, rgba(240,250,255,0.22) 45%, rgba(255,255,255,0.6) 50%, rgba(240,250,255,0.22) 55%, transparent 100%)',
              filter: 'blur(2px)',
              animation: 'f10-scanline-sweep 0.75s ease-in-out forwards',
              mixBlendMode: 'screen',
            }} />
          </div>

          {/* 6. Massive "INVERSION" banner */}
          <div className="absolute left-1/2 top-1/2 pointer-events-none z-50" style={{
            transform: 'translate(-50%,-50%)',
            animation: 'f10-inversion-banner 1.5s ease-in-out forwards',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          }}>
            <div style={{ position: 'relative' }}>
              {/* 5. Chromatic aberration twins — kept red+cyan, the OG signature, looks great on monochrome */}
              <div style={{
                position: 'absolute', inset: 0,
                fontFamily: "'Cinzel Decorative', serif",
                fontSize: 'clamp(34px, 7.5vmin, 78px)',
                fontWeight: 900,
                color: '#ff3060',
                letterSpacing: '0.32em',
                whiteSpace: 'nowrap',
                animation: 'f10-inversion-chromar 0.75s ease-in-out forwards',
                mixBlendMode: 'screen',
                pointerEvents: 'none',
              }}>INVERSION</div>
              <div style={{
                position: 'absolute', inset: 0,
                fontFamily: "'Cinzel Decorative', serif",
                fontSize: 'clamp(34px, 7.5vmin, 78px)',
                fontWeight: 900,
                color: '#30c0ff',
                letterSpacing: '0.32em',
                whiteSpace: 'nowrap',
                animation: 'f10-inversion-chromab 0.75s ease-in-out forwards',
                mixBlendMode: 'screen',
                pointerEvents: 'none',
              }}>INVERSION</div>
              {/* The real text on top — bright white shiny */}
              <div style={{
                position: 'relative',
                fontFamily: "'Cinzel Decorative', serif",
                fontSize: 'clamp(34px, 7.5vmin, 78px)',
                fontWeight: 900,
                color: '#ffffff',
                letterSpacing: '0.32em',
                whiteSpace: 'nowrap',
                textShadow: '0 0 24px rgba(240,250,255,0.98), 0 0 50px rgba(200,220,245,0.75), 0 0 90px rgba(160,200,240,0.5)',
              }}>INVERSION</div>
            </div>

            {/* 7. Cursor-flip glyph */}
            <div style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 'clamp(11px, 1.6vmin, 16px)',
              fontStyle: 'italic',
              color: 'rgba(240,250,255,0.85)',
              letterSpacing: '0.4em',
              marginTop: 8,
              textShadow: '0 0 10px rgba(220,235,250,0.8)',
            }}>← cursor reversed →</div>
          </div>
        </>
      )}

      {/* === CHANCELLOR WHISPER === */}
      {whisper && (
        <div className="absolute left-1/2 z-40 pointer-events-none" style={{
          top: '32%',
          color: '#ffffff',
          fontFamily: "'Cinzel', serif",
          fontStyle: 'italic',
          fontSize: 16,
          letterSpacing: '0.22em',
          textShadow: '0 0 14px rgba(240,250,255,0.98), 0 0 30px rgba(200,220,245,0.6)',
          animation: 'f10-whisper-in 1.7s ease-in-out forwards',
          whiteSpace: 'nowrap',
        }}>— {whisper} —</div>
      )}

      {/* === HUD === */}
      <div className="absolute top-0 left-0 right-0 z-50 px-5 pt-4 pb-3"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, transparent 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[9px] tracking-[0.45em] font-mono uppercase" style={{ color: 'rgba(220,230,245,0.55)' }}>
              Vorago Ultima
            </span>
            <p className="font-mono mt-1 text-[14px] font-black" style={{
              color: '#f2f5fa',
              textShadow: '0 0 10px rgba(220,232,248,0.55), 0 0 22px rgba(180,200,220,0.3)',
            }}>{ringsSurvived}<span className="text-white/15 font-normal text-[11px]"> rings deflected</span></p>
          </div>

          <div className="text-right">
            <span className="text-[8px] font-mono tracking-[0.3em] uppercase" style={{ color: timeLeft <= 12 ? '#ff5060' : 'rgba(220,230,245,0.5)' }}>
              {timeLeft <= 12 ? 'final breath' : 'the clock'}
            </span>
            <div className="font-mono text-2xl font-black mt-0.5" style={{
              color: timeLeft <= 12 ? '#ff5060' : '#f2f5fa',
              textShadow: `0 0 14px ${timeLeft <= 12 ? 'rgba(255,80,96,0.7)' : 'rgba(220,232,248,0.55)'}`,
            }}>{timeLeft}</div>
          </div>
        </div>
      </div>

      {/* === BOTTOM PROMPT === */}
      <div className="absolute bottom-4 left-0 right-0 z-50 text-center">
        <span className="text-[8px] font-mono tracking-[0.4em] uppercase" style={{ color: 'rgba(220,230,245,0.45)' }}>
          Cursor sets your orbit · find the gap · the dark forgets nothing
        </span>
      </div>

      {/* === LATE-GAME DANGER VIGNETTE === */}
      {timeLeft <= 20 && phase === 'play' && (
        <div className="absolute inset-0 z-5 pointer-events-none transition-opacity duration-500" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(255,0,40,0.08) 75%, rgba(255,0,40,0.22) 100%)',
          opacity: 0.7,
        }} />
      )}
    </div>
  );
}


const FloorComponents = [Floor1, Floor2, Floor3, Floor4, Floor5, Floor6, Floor7, Floor8, Floor9, Floor10];

export default function TheDescent({ walletAddress, connected }) {
  const { isLive } = useDemo();
  const [gameState, setGameState] = useState('menu');
  const [currentFloor, setCurrentFloor] = useState(0);
  const [sessionToken, setSessionToken] = useState(null);
  const [balance, setBalance] = useState(0);
  const [floorStartTime, setFloorStartTime] = useState(null);
  const [result, setResult] = useState(null);
  const [screenShake, setScreenShake] = useState(0);
  const [entering, setEntering] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminFloor, setAdminFloor] = useState(0); // 0 = play all, 1-10 = skip to floor
  const [floorIntroDone, setFloorIntroDone] = useState(false);
  const [tutorialDone, setTutorialDone] = useState(false);
  const [skipTutorials, setSkipTutorials] = useState(
    () => { try { return localStorage.getItem('gfg_descent_skip_tutorials') === 'true'; } catch { return false; } }
  );
  const [demoFreePlays, setDemoFreePlays] = useState({ remaining: 0, used: 0, total: 3, isDemo: false });

  const adminMode = walletAddress === ADMIN_WALLET;
  const isDemoWallet = walletAddress?.startsWith('Demo');
  const hasDemoFreePlays = isDemoWallet && demoFreePlays.remaining > 0;

  // Fetch demo free plays remaining on mount and after each game
  useEffect(() => {
    if (!walletAddress || !isDemoWallet) return;
    fetch(`${API_URL}/api/descent/demo-plays/${walletAddress}`)
      .then(r => r.json())
      .then(d => setDemoFreePlays({
        isDemo: !!d.is_demo,
        remaining: d.free_plays_remaining || 0,
        used: d.free_plays_used || 0,
        total: d.free_plays_total || 3,
      }))
      .catch(() => {});
  }, [walletAddress, isDemoWallet, gameState]);

  // Reset intro + tutorial state when floor changes
  useEffect(() => {
    setFloorIntroDone(false);
    setTutorialDone(false);
  }, [currentFloor]);

  // Hide global UI (music toggle, cursor trail, mobile menu) during active gameplay
  useEffect(() => {
    const isPlaying = gameState === 'playing';
    if (isPlaying) {
      document.body.classList.add('game-active');
    } else {
      document.body.classList.remove('game-active');
    }
    return () => document.body.classList.remove('game-active');
  }, [gameState]);

  useEffect(() => {
    if (!walletAddress) {
      return;
    }
    fetch(`${API_URL}/api/whack/user/${walletAddress}`)
      .then(r => r.json())
      .then(d => setBalance(d.fee_balance || 0))
      .catch(() => {});
  }, [walletAddress, gameState]);

  const startDescent = async (overrideFloor) => {
    if (!connected || !walletAddress) return;
    const selectedFloor = overrideFloor ?? adminFloor;
    setEntering(true);

    try {
      const res = await fetch(`${API_URL}/api/descent/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: walletAddress,
          start_floor: selectedFloor > 0 ? selectedFloor : 0,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSessionToken(data.session_token);
        setBalance(data.new_balance);
        setIsAdmin(data.admin_mode || false);
        const startAt = data.current_floor; // 0-indexed floors completed
        setCurrentFloor(0);
        setTimeout(() => {
          setGameState('playing');
          setCurrentFloor(startAt + 1); // 1-indexed floor to play
          setFloorStartTime(Date.now());
          setEntering(false);
        }, 1500);
      } else if (data.message && data.message.includes('active descent')) {
        // Auto-abandon stuck session and retry once
        await fetch(`${API_URL}/api/descent/abandon`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wallet_address: walletAddress }),
        });
        setEntering(false);
        setTimeout(() => startDescent(selectedFloor), 300);
      } else {
        setResult({ type: 'error', message: data.message });
        setGameState('result');
        setEntering(false);
      }
    } catch {
      setEntering(false);
    }
  };

  const onFloorComplete = useCallback(async () => {
    const timeTaken = (Date.now() - floorStartTime) / 1000;

    try {
      const res = await fetch(`${API_URL}/api/descent/floor/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: walletAddress,
          session_token: sessionToken,
          floor: currentFloor,
          time_taken: timeTaken,
          live_mode: !!isLive,
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.onchain) fireOnChainToast(toast, data.onchain);
        if (data.auto_cashout) {
          setResult({ type: 'victory', fees: data.fees_won, floor: 10, multiplier: 500, onchain: data.onchain || null });
          setGameState('result');
          // Notarise the Floor 10 clear (SPL Memo when live, Mongo-only in PREVIEW_MODE).
          try {
            fetch(`${API_URL}/api/arcade/descent/milestone`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                wallet_address: walletAddress,
                floor: 10,
                score: Math.max(1, Math.min(10_000_000, Math.floor(data.fees_won || 0))),
                session_token: sessionToken,
                run_id: `${walletAddress}-${floorStartTime}`,
              }),
            }).catch(() => {});
          } catch { /* non-blocking */ }
        } else {
          setResult({
            currentValue: data.current_value,
            multiplier: data.multiplier,
            nextFloor: data.next_floor,
            nextMultiplier: data.next_multiplier,
            onchain: data.onchain || null,
          });
          setGameState('cashout');
        }
      } else {
        setResult({ type: 'error', message: data.message });
        setGameState('result');
      }
    } catch {
      setResult({ type: 'error', message: 'Connection lost in the depths...' });
      setGameState('result');
    }
  }, [walletAddress, sessionToken, currentFloor, floorStartTime, isLive]);

  const onFloorFail = useCallback(async () => {
    setScreenShake(1);
    setTimeout(() => setScreenShake(0), 500);
    try {
      await fetch(`${API_URL}/api/descent/fail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, session_token: sessionToken }),
      });
    } catch {}
    setResult({ type: 'death', floor: currentFloor, feesLost: isAdmin ? 0 : ENTRY_FEE });
    setGameState('failed');
  }, [walletAddress, sessionToken, currentFloor, isAdmin]);

  const cashOut = async () => {
    try {
      const res = await fetch(`${API_URL}/api/descent/cashout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, session_token: sessionToken, live_mode: !!isLive }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.onchain) fireOnChainToast(toast, data.onchain);
        setResult({ type: 'cashout', fees: data.fees_won, floor: data.floor_cashed_out, profit: data.net_profit, onchain: data.onchain || null });
        setGameState('result');
      }
    } catch {}
  };

  const descend = () => {
    const nextFloor = result.nextFloor;
    setCurrentFloor(nextFloor);
    setFloorStartTime(Date.now());
    setGameState('playing');
    setResult(null);
  };

  const resetGame = () => {
    if (walletAddress) {
      fetch(`${API_URL}/api/descent/abandon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress }),
      }).catch(() => {});
    }
    setGameState('menu');
    setCurrentFloor(0);
    setSessionToken(null);
    setResult(null);
    setIsAdmin(false);
    setAdminFloor(0);
  };

  const FloorComponent = FloorComponents[currentFloor - 1];
  const depthIntensity = currentFloor / 10;
  const shakeStyle = screenShake ? { animation: 'shake 0.3s ease-in-out' } : {};
  const ACCENT = '#c026d3';
  const ACCENT_RED = '#dc2626';
  const ACCENT_DIM = 'rgba(192,38,211,';

  // ════════════════════════════════════════
  // CINEMATIC MENU — Hacker/CRT Style
  // ════════════════════════════════════════
  if (gameState === 'menu') {
    const canPlay = connected && (adminMode || hasDemoFreePlays || balance >= ENTRY_FEE);
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="the-descent-menu"
        style={{ background: 'radial-gradient(ellipse at 50% 60%, #120818 0%, #0a0410 30%, #050208 70%, #020004 100%)' }}>
        <style>{CINEMATIC_KEYFRAMES}{`
          @keyframes bloodDrip {
            0% { transform: translateY(-10px) scaleY(0.3); opacity: 0; }
            10% { opacity: 0.6; transform: translateY(0) scaleY(1); }
            50% { transform: translateY(60vh) scaleY(1.5); opacity: 0.3; }
            100% { transform: translateY(100vh) scaleY(2); opacity: 0; }
          }
          @keyframes descend-pulse {
            0%, 100% { box-shadow: 0 0 15px rgba(192,38,211,0.3), 0 0 30px rgba(220,38,38,0.15), inset 0 0 10px rgba(192,38,211,0.05); }
            50% { box-shadow: 0 0 25px rgba(192,38,211,0.5), 0 0 50px rgba(220,38,38,0.25), inset 0 0 20px rgba(192,38,211,0.1); }
          }
          @keyframes abyss-breathe {
            0%, 100% { opacity: 0.03; transform: scale(0.95); }
            50% { opacity: 0.07; transform: scale(1.05); }
          }
        `}</style>

        {/* CRT + Noise */}
        <ScanlineOverlay />
        <NoiseOverlay />

        {/* Purple/red gas particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          {[...Array(14)].map((_, i) => (
            <motion.div key={i} className="absolute rounded-full"
              style={{
                left: `${5 + i * 7}%`, bottom: '-20px',
                width: `${12 + Math.random() * 20}px`, height: `${12 + Math.random() * 20}px`,
                background: i % 3 === 0
                  ? 'radial-gradient(circle, rgba(192,38,211,0.25) 0%, transparent 70%)'
                  : i % 3 === 1
                  ? 'radial-gradient(circle, rgba(220,38,38,0.2) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)',
                filter: 'blur(3px)',
              }}
              animate={{ y: [0, -700], x: [0, Math.sin(i * 0.8) * 30], opacity: [0, 0.5, 0.15, 0] }}
              transition={{ duration: 9 + Math.random() * 4, delay: i * 0.6, repeat: Infinity, ease: 'easeOut' }}
            />
          ))}
        </div>

        {/* Blood drips — red */}
        <div className="absolute inset-x-0 top-0 pointer-events-none z-20 h-full overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute', top: '-10px',
              left: `${12 + (i * 14) + Math.random() * 8}%`,
              width: '2px', height: '25px',
              background: 'linear-gradient(180deg, rgba(220,38,38,0.5) 0%, rgba(192,38,211,0.2) 60%, transparent 100%)',
              borderRadius: '0 0 2px 2px',
              animation: `bloodDrip ${3.5 + Math.random() * 3}s ${Math.random() * 4}s ease-in infinite`,
              filter: 'drop-shadow(0 0 4px rgba(220,38,38,0.3))',
            }} />
          ))}
        </div>

        {/* Corner brackets — purple/red */}
        <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 z-30" style={{ borderColor: 'rgba(192,38,211,0.3)' }} />
        <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 z-30" style={{ borderColor: 'rgba(220,38,38,0.3)' }} />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 z-30" style={{ borderColor: 'rgba(220,38,38,0.3)' }} />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 z-30" style={{ borderColor: 'rgba(192,38,211,0.3)' }} />

        {/* Breathing abyss glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
          <div className="w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(192,38,211,0.06) 0%, rgba(220,38,38,0.03) 40%, transparent 70%)', animation: 'abyss-breathe 6s ease-in-out infinite' }} />
        </div>

        {/* Floating ancient runes */}
        <div className="absolute inset-0 pointer-events-none z-[15] overflow-hidden">
          {['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'].map((rune, i) => (
            <motion.div
              key={`rune-${i}`}
              className="absolute font-bold select-none"
              style={{
                fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                fontSize: `${18 + (i % 3) * 8}px`,
                color: i < 5 ? 'rgba(168,85,247,0.25)' : i < 8 ? 'rgba(192,38,211,0.22)' : 'rgba(220,38,38,0.2)',
                textShadow: i < 5 ? '0 0 15px rgba(168,85,247,0.5)' : i < 8 ? '0 0 15px rgba(192,38,211,0.4)' : '0 0 15px rgba(220,38,38,0.35)',
                left: `${5 + i * 9}%`,
                top: `${15 + (i % 4) * 20}%`,
              }}
              animate={{
                y: [0, -30, 0, 20, 0],
                opacity: [0.15, 0.35, 0.15],
                rotate: [0, 5, -5, 0],
              }}
              transition={{ duration: 7 + i * 0.5, delay: i * 0.4, repeat: Infinity, ease: 'easeInOut' }}
            >
              {rune}
            </motion.div>
          ))}
        </div>

        {/* Slow horizontal fog wisps */}
        <div className="absolute inset-0 pointer-events-none z-[12] overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={`fog-${i}`}
              className="absolute"
              style={{
                width: `${500 + i * 80}px`,
                height: `${100 + i * 25}px`,
                top: `${10 + i * 18}%`,
                left: '-30%',
                background: i % 2 === 0
                  ? 'radial-gradient(ellipse, rgba(168,85,247,0.12) 0%, transparent 70%)'
                  : 'radial-gradient(ellipse, rgba(220,38,38,0.09) 0%, transparent 70%)',
                filter: 'blur(25px)',
              }}
              animate={{ x: ['0vw', '160vw'] }}
              transition={{ duration: 25 + i * 5, delay: i * 3, repeat: Infinity, ease: 'linear' }}
            />
          ))}
        </div>

        {/* Vertical chain lines */}
        <div className="absolute inset-0 pointer-events-none z-[14]">
          {[12, 88].map((x, i) => (
            <motion.div
              key={`chain-${i}`}
              className="absolute"
              style={{
                left: `${x}%`,
                top: '-5%',
                height: '110%',
                width: '2px',
                background: `linear-gradient(180deg, transparent 0%, rgba(192,38,211,0.15) 15%, rgba(192,38,211,0.08) 40%, rgba(220,38,38,0.1) 70%, transparent 100%)`,
              }}
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 5, delay: i * 2.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
          {/* Chain tick marks */}
          {[12, 88].map((x, ci) => (
            [...Array(8)].map((_, i) => (
              <motion.div
                key={`tick-${ci}-${i}`}
                className="absolute"
                style={{
                  left: `${x - 0.3}%`,
                  top: `${10 + i * 11}%`,
                  width: '8px',
                  height: '2px',
                  background: 'rgba(192,38,211,0.15)',
                }}
                animate={{ opacity: [0.2, 0.6, 0.2] }}
                transition={{ duration: 3, delay: ci * 1.5 + i * 0.3, repeat: Infinity }}
              />
            ))
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-20 flex flex-col items-center justify-center h-full px-4">

          {/* Admin badge */}
          {adminMode && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="absolute top-5 right-5 flex items-center gap-2 px-3 py-1.5 z-30"
              style={{ background: 'rgba(192,38,211,0.08)', border: '1px solid rgba(192,38,211,0.3)' }}
              data-testid="admin-badge">
              <Shield className="w-3.5 h-3.5" style={{ color: '#c026d3' }} />
              <span className="text-[10px] tracking-[0.3em] font-mono font-bold" style={{ color: '#c026d3' }}>ADMIN // FREE PLAY</span>
            </motion.div>
          )}

          {/* Title — Cinzel Decorative with purple/red gradient */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center relative">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-[0.12em] uppercase relative"
              style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}
              data-testid="cinematic-title">
              {/* Shadow layer */}
              <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(192,38,211,0.2)', filter: 'blur(4px)' }}>THE DESCENT</span>
              {/* Main text */}
              <span className="relative" style={{
                background: 'linear-gradient(180deg, #e9d5ff 0%, #c084fc 15%, #a855f7 30%, #9333ea 45%, #c026d3 60%, #dc2626 80%, #991b1b 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(192,38,211,0.5)) drop-shadow(0 0 40px rgba(220,38,38,0.2))',
              }}>THE DESCENT</span>
              {/* Shimmer */}
              <motion.span className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(90deg, transparent, rgba(192,132,252,0.3), transparent)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%',
              }} animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }} transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}>THE DESCENT</motion.span>
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="text-sm tracking-[0.3em] uppercase mt-2"
              style={{ fontFamily: "'Cinzel', serif", color: 'rgba(192,132,252,0.65)' }}>
              How deep will you go?
            </motion.p>
          </motion.div>

          {/* Lore box */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="max-w-lg text-center mt-4 px-5 py-3"
            style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(192,38,211,0.08)' }}>
            <p className="text-[11px] leading-relaxed italic" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(192,132,252,0.65)' }}>
              Ten floors descend into the abyss. Each deeper than the last. Risk your fees, survive the trials, or lose everything. Only the worthy claim the treasure at the bottom.
            </p>
          </motion.div>

          {/* Floor Grid — horizontal */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
            className="w-full max-w-lg mt-5">
            <p className="text-[9px] tracking-[0.3em] uppercase text-center mb-3"
              style={{ fontFamily: "'Cinzel', serif", color: 'rgba(192,132,252,0.6)' }}>
              {adminMode ? 'Select Floor to Play' : '10 Floors of Progressive Risk'}
            </p>
            <div className="grid grid-cols-5 gap-2 max-w-md mx-auto">
              {FLOORS.map((f, i) => {
                const floorNum = i + 1;
                const isSelected = adminMode && adminFloor === floorNum;
                const floorColor = i < 5 ? '#a855f7' : i < 8 ? '#c026d3' : '#dc2626';
                return (
                  <motion.button key={i}
                    onClick={adminMode ? () => setAdminFloor(prev => prev === floorNum ? 0 : floorNum) : undefined}
                    initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9 + i * 0.05 }}
                    className="relative text-center py-2.5 px-1"
                    style={{
                      background: isSelected ? `linear-gradient(180deg, ${floorColor}30, ${floorColor}10)` : `linear-gradient(180deg, ${floorColor}12, ${floorColor}04)`,
                      border: `1px solid ${isSelected ? floorColor + '90' : floorColor + '35'}`,
                      cursor: adminMode ? 'pointer' : 'default',
                      boxShadow: isSelected ? `0 0 12px ${floorColor}30` : `inset 0 0 15px ${floorColor}06`,
                    }}
                    whileHover={adminMode ? { scale: 1.05, borderColor: floorColor + '60' } : {}}>
                    {isSelected && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full flex items-center justify-center"
                        style={{ background: floorColor, boxShadow: `0 0 6px ${floorColor}` }}>
                        <span className="text-[6px] text-black font-black">GO</span>
                      </div>
                    )}
                    <div className="text-sm font-bold" style={{ fontFamily: "'Cinzel', serif", color: floorColor, textShadow: `0 0 8px ${floorColor}40` }}>{f.icon}</div>
                    <div className="text-[8px] mt-0.5 truncate font-bold" style={{ fontFamily: "'Courier New', monospace", color: `${floorColor}bb` }}>{f.name}</div>
                    <div className="text-xs font-black mt-0.5" style={{ fontFamily: "'Impact', sans-serif", color: floorColor, textShadow: `0 0 6px ${floorColor}30` }}>{MULTIPLIERS[i]}x</div>
                  </motion.button>
                );
              })}
            </div>
            {adminMode && adminFloor > 0 && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-center mt-2 text-[10px] font-mono tracking-wider" style={{ color: '#c026d3' }}>
                STARTING AT: FLOOR {adminFloor} — {FLOORS[adminFloor - 1].name}
              </motion.p>
            )}
          </motion.div>

          {/* Entry info — fee balance wager, demo free plays banner if applicable */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
            className="mt-5 flex items-center gap-5">
            <div className="text-center px-5 py-3" style={{
              background: hasDemoFreePlays ? 'rgba(57,255,20,0.06)' : 'rgba(192,38,211,0.04)',
              border: hasDemoFreePlays ? '1px solid rgba(57,255,20,0.4)' : '1px solid rgba(192,38,211,0.15)',
              boxShadow: hasDemoFreePlays ? '0 0 20px rgba(57,255,20,0.18) inset' : 'none',
            }}>
              <span className="text-[9px] tracking-[0.2em] uppercase block" style={{
                fontFamily: "'Cinzel', serif",
                color: adminMode ? 'rgba(57,255,20,0.95)' : (hasDemoFreePlays ? 'rgba(170,255,100,0.95)' : 'rgba(192,132,252,0.7)'),
              }}>
                {adminMode ? 'Admin: No Cost' : (hasDemoFreePlays ? 'Demo: Free Trial' : 'Entry Fee')}
              </span>
              <span className="text-xl font-black block mt-0.5" style={{
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                background: (adminMode || hasDemoFreePlays)
                  ? 'linear-gradient(90deg, #aaff66, #39ff14, #22c55e)'
                  : 'linear-gradient(90deg, #c084fc, #c026d3)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {(adminMode || hasDemoFreePlays) ? 'FREE' : ENTRY_FEE.toLocaleString()}
              </span>
              {hasDemoFreePlays ? (
                <span className="text-[9px] mt-0.5 inline-block font-bold tracking-[0.15em]" style={{
                  color: '#aaff66',
                  fontFamily: "'Courier New', monospace",
                  textShadow: '0 0 6px rgba(57,255,20,0.6)',
                }}>
                  {demoFreePlays.remaining} of {demoFreePlays.total} runs left
                </span>
              ) : (!adminMode && (
                <span className="text-[8px]" style={{ color: 'rgba(192,132,252,0.6)', fontFamily: "'Courier New', monospace" }}>from fee balance</span>
              ))}
            </div>
            <div className="text-center px-5 py-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <span className="text-[9px] tracking-[0.2em] uppercase block" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.7)' }}>Your Balance</span>
              <span className="text-xl font-black block mt-0.5" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", color: 'rgba(255,255,255,0.7)' }}>
                {balance.toLocaleString()}
              </span>
              <span className="text-[8px]" style={{ color: 'rgba(192,132,252,0.5)', fontFamily: "'Courier New', monospace" }}>Up to +500 SP</span>
            </div>
          </motion.div>

          {/* Descend Button */}
          <motion.button
            onClick={canPlay ? () => startDescent() : undefined}
            disabled={!canPlay || entering}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: canPlay ? 1 : 0.4, y: 0 }} transition={{ delay: 1.3 }}
            className="mt-5 px-10 py-4 font-bold text-base tracking-[0.12em] uppercase relative overflow-hidden"
            data-testid="start-descent-btn"
            style={{
              background: entering ? 'rgba(192,38,211,0.06)' : 'rgba(0,0,0,0.5)',
              border: `1px solid ${canPlay ? 'rgba(192,38,211,0.4)' : 'rgba(255,255,255,0.06)'}`,
              fontFamily: "'Cinzel', serif",
              animation: canPlay && !entering ? 'descend-pulse 3s ease-in-out infinite' : 'none',
              cursor: canPlay ? 'pointer' : 'not-allowed',
            }}
            whileHover={canPlay ? { scale: 1.02, borderColor: 'rgba(192,38,211,0.7)', boxShadow: '0 0 30px rgba(192,38,211,0.15)' } : {}}
            whileTap={canPlay ? { scale: 0.98 } : {}}>
            <span className="relative z-10 flex items-center gap-3" style={{
              background: canPlay ? 'linear-gradient(90deg, #c084fc, #c026d3, #dc2626)' : 'none',
              WebkitBackgroundClip: canPlay ? 'text' : 'unset', WebkitTextFillColor: canPlay ? 'transparent' : '#444',
            }}>
              {entering ? (
                <span className="animate-pulse" style={{ WebkitTextFillColor: '#c026d3' }}>Descending...</span>
              ) : !connected ? (
                'Connect Wallet'
              ) : !adminMode && !hasDemoFreePlays && balance < ENTRY_FEE ? (
                'Insufficient Fees'
              ) : adminMode && adminFloor > 0 ? (
                <><Skull className="w-5 h-5" style={{ color: '#c026d3' }} /> Play Floor {adminFloor}</>
              ) : hasDemoFreePlays ? (
                <><Skull className="w-5 h-5" style={{ color: '#39ff14' }} /> Free Trial Run · {demoFreePlays.remaining} Left</>
              ) : (
                <><Skull className="w-5 h-5" style={{ color: '#c026d3' }} /> Enter The Abyss</>
              )}
            </span>
            {canPlay && !entering && (
              <motion.div className="absolute inset-0"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(192,38,211,0.08), transparent)' }}
                animate={{ x: ['-100%', '100%'] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }} />
            )}
          </motion.button>

          {/* Status bar */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#c026d3', boxShadow: '0 0 6px #c026d3', animation: 'warningPulse 2s ease-in-out infinite' }} />
            <span className="text-[9px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(192,132,252,0.65)' }}>
              {adminMode
                ? 'Admin Override Engaged // Free Play Active'
                : (hasDemoFreePlays
                  ? `Demo Trial Active // ${demoFreePlays.remaining} of ${demoFreePlays.total} Free Runs Left`
                  : 'Depth Protocol Active // Fees Required to Participate')}
            </span>
          </motion.div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // PLAYING — Floor Tutorial (per-floor how-to-play popup, dismissible)
  // ════════════════════════════════════════
  if (gameState === 'playing' && !tutorialDone && !skipTutorials && FLOOR_TUTORIALS[currentFloor]) {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="floor-tutorial-screen"
        style={{ background: '#000' }}>
        <FloorTutorial
          floorNum={currentFloor}
          onBegin={() => setTutorialDone(true)}
          onToggleSkip={(checked) => {
            setSkipTutorials(checked);
            try { localStorage.setItem('gfg_descent_skip_tutorials', checked ? 'true' : 'false'); } catch {}
          }}
        />
      </div>
    );
  }

  // ════════════════════════════════════════
  // PLAYING — Floor Intro (cinematic transition before gameplay)
  // ════════════════════════════════════════
  if (gameState === 'playing' && !floorIntroDone && FLOOR_INTROS[currentFloor]) {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="floor-intro-screen"
        style={{ background: '#000' }}>
        <FloorIntro floorNum={currentFloor} onReady={() => setFloorIntroDone(true)} />
      </div>
    );
  }

  // ════════════════════════════════════════
  // PLAYING — Floor Challenge
  // ════════════════════════════════════════
  if (gameState === 'playing' && FloorComponent) {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none" data-testid="the-descent-game"
        style={{
          background: `radial-gradient(ellipse at 50% 80%, rgba(${Math.floor(depthIntensity * 30)},0,0,0.3) 0%, #000 70%)`,
          ...shakeStyle,
        }}
      >
        <style>{CINEMATIC_KEYFRAMES}{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-8px) rotate(-1deg); }
            40% { transform: translateX(8px) rotate(1deg); }
            60% { transform: translateX(-4px); }
            80% { transform: translateX(4px); }
          }
        `}</style>
        <ScanlineOverlay />
        <div className="absolute inset-0 z-40 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at center, transparent ${60 - depthIntensity * 30}%, rgba(0,0,0,${0.6 + depthIntensity * 0.3}) 100%)` }}
        />

        <motion.div key={currentFloor} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="relative z-20 h-full flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.9), transparent)' }}>
            <div>
              <span className="text-[9px] font-mono tracking-[0.3em]" style={{ color: FLOORS[currentFloor - 1].color + '60' }}>
                FLOOR {currentFloor}/10
              </span>
              <p className="text-sm font-bold mt-0.5" style={{ color: FLOORS[currentFloor - 1].color, fontFamily: "'Courier New', monospace" }}>
                {FLOORS[currentFloor - 1].name}
              </p>
            </div>
            <div className="text-right">
              <span className="text-[9px] font-mono tracking-[0.3em] text-white/20">MULTIPLIER</span>
              <p className="text-lg font-black" style={{ color: ACCENT, fontFamily: "'Courier New', monospace" }}>{MULTIPLIERS[currentFloor - 1]}x</p>
            </div>
          </div>
          <div className="flex-1">
            <FloorComponent onComplete={onFloorComplete} onFail={onFloorFail} />
          </div>
          <div className="text-center pb-4">
            <span className="text-[9px] font-mono text-white/15">{FLOORS[currentFloor - 1].desc}</span>
          </div>
        </motion.div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // CLAIM REWARD SCREEN
  // ════════════════════════════════════════
  if (gameState === 'cashout' && result) {
    const nextFloorMeta = result.nextFloor ? FLOORS[result.nextFloor - 1] : null;
    const nextAccent = nextFloorMeta?.color || ACCENT;
    return (
      <div className="relative h-screen w-full overflow-hidden select-none"
        style={{ background: 'radial-gradient(ellipse at 50% 60%, #1a0405 0%, #050102 100%)' }}
      >
        <style>{CINEMATIC_KEYFRAMES}</style>
        <style>{`
          @keyframes cs-glow { 0%,100% { text-shadow: 0 0 30px rgba(255,180,50,0.6), 0 0 60px rgba(255,140,30,0.35), 0 0 120px rgba(255,100,20,0.2); } 50% { text-shadow: 0 0 50px rgba(255,210,90,0.85), 0 0 100px rgba(255,150,40,0.5), 0 0 160px rgba(255,100,20,0.3); } }
          @keyframes cs-flame-rise { 0% { transform: translateY(0) scale(1); opacity: 0.6; } 100% { transform: translateY(-100vh) scale(0.4); opacity: 0; } }
          @keyframes cs-rune-pulse { 0%,100% { opacity: 0.45; transform: scale(1); } 50% { opacity: 0.85; transform: scale(1.04); } }
          @keyframes cs-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
          @keyframes cs-divider { 0% { width: 0; opacity: 0; } 100% { width: 320px; opacity: 1; } }
          @keyframes cs-pulse-ring { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(1.4); opacity: 0; } }
        `}</style>
        <ScanlineOverlay />
        <NoiseOverlay />

        {/* Cathedral background — radial crimson + gold from below */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: `radial-gradient(ellipse 60vmax 70vmax at 50% 110%, ${ACCENT}25 0%, ${ACCENT}10 30%, transparent 65%)`,
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 50vmax 60vmax at 50% -10%, rgba(255,200,80,0.08) 0%, transparent 60%)',
        }} />

        {/* Rising embers */}
        {Array.from({ length: 16 }).map((_, i) => {
          const seed = i * 31.7;
          const left = (seed * 1.9) % 100;
          const dur = 6 + (seed % 5);
          const delay = (seed * 0.4) % 6;
          const size = 0.18 + ((seed * 0.05) % 0.22);
          return (
            <div key={`ember_${i}`} className="absolute pointer-events-none rounded-full" style={{
              left: `${left}%`, bottom: '-2vmin',
              width: `${size}vmin`, height: `${size}vmin`,
              background: 'rgba(255,200,80,0.9)',
              boxShadow: '0 0 8px rgba(255,160,60,0.8), 0 0 16px rgba(255,100,30,0.4)',
              animation: `cs-flame-rise ${dur}s linear ${delay}s infinite`,
              filter: 'blur(0.5px)',
            }} />
          );
        })}

        {/* Decorative rune circle behind the multiplier */}
        <div className="absolute pointer-events-none" style={{
          left: '50%', top: '32%', transform: 'translate(-50%, -50%)',
          width: '480px', height: '480px',
          maxWidth: '70vmin', maxHeight: '70vmin',
        }}>
          <svg viewBox="0 0 480 480" className="w-full h-full" style={{ animation: 'cs-rune-pulse 5s ease-in-out infinite' }}>
            {/* Outer ring */}
            <circle cx="240" cy="240" r="220" fill="none" stroke={ACCENT} strokeWidth="1" opacity="0.35" />
            <circle cx="240" cy="240" r="200" fill="none" stroke={ACCENT} strokeWidth="0.5" opacity="0.25" strokeDasharray="6 4" />
            <circle cx="240" cy="240" r="160" fill="none" stroke="#ffaa30" strokeWidth="0.7" opacity="0.4" />
            <circle cx="240" cy="240" r="130" fill="none" stroke="#ffcc66" strokeWidth="0.4" opacity="0.3" strokeDasharray="2 3" />
            {/* Diamond accents at cardinal points on outer ring */}
            {[0, 90, 180, 270].map(deg => (
              <g key={deg} transform={`rotate(${deg} 240 240) translate(0 -220)`}>
                <polygon points="240,234 246,240 240,246 234,240" fill={ACCENT} opacity="0.85" />
              </g>
            ))}
            {/* Tick marks on outer ring */}
            {Array.from({ length: 24 }).map((_, i) => (
              <line key={i} x1="240" y1="20" x2="240" y2="28"
                transform={`rotate(${i * 15} 240 240)`}
                stroke={ACCENT} strokeWidth="0.6" opacity="0.3" />
            ))}
          </svg>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          className="relative z-20 flex flex-col items-center justify-center h-full px-6"
        >
          {/* Top: Floor cleared banner */}
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
            className="text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <span style={{ color: ACCENT, opacity: 0.45 }}>◆</span>
              <span className="text-[10px] tracking-[0.6em] font-mono uppercase font-black" style={{ color: ACCENT, textShadow: `0 0 10px ${ACCENT}90` }}>
                Floor {currentFloor} Cleared
              </span>
              <span style={{ color: ACCENT, opacity: 0.45 }}>◆</span>
            </div>
            <p className="text-[9px] tracking-[0.4em] font-mono uppercase" style={{ color: '#ffcc66aa' }}>
              The court of $GFG records your descent
            </p>
          </motion.div>

          {/* Center: Multiplier — massive, lore-styled */}
          <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="text-center mt-8 relative">
            <span className="text-[10px] tracking-[0.5em] font-mono uppercase block font-bold mb-2" style={{ color: '#ffcc66cc' }}>
              your multiplier
            </span>
            <div className="relative inline-block">
              <p className="font-black leading-none" style={{
                fontFamily: "'Cinzel Decorative', serif",
                fontSize: 'clamp(80px, 14vmin, 168px)',
                background: 'linear-gradient(180deg, #fff5d8 0%, #ffd980 35%, #ff9930 75%, #cc4400 100%)',
                backgroundSize: '100% 200%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                animation: 'cs-glow 3s ease-in-out infinite',
                filter: 'drop-shadow(0 0 30px rgba(255,180,50,0.6))',
              }}>
                {result.multiplier}<span style={{ fontSize: '0.55em' }}>×</span>
              </p>
              {/* Pulse ring */}
              <div className="absolute inset-0 pointer-events-none" style={{
                border: `2px solid ${ACCENT}40`,
                borderRadius: '50%',
                animation: 'cs-pulse-ring 2.5s ease-out infinite',
              }} />
            </div>
            {/* Shimmer divider */}
            <div className="h-[2px] mt-4 mx-auto" style={{
              animation: 'cs-divider 1.2s ease-out 0.6s both',
              background: `linear-gradient(90deg, transparent 0%, ${ACCENT}60 30%, ${ACCENT} 50%, ${ACCENT}60 70%, transparent 100%)`,
              backgroundSize: '200% 100%',
            }} />
          </motion.div>

          {/* Current value tile */}
          <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}
            className="mt-7 px-8 py-5 rounded-sm relative"
            style={{
              background: 'linear-gradient(180deg, rgba(20,8,2,0.95), rgba(10,4,1,0.95))',
              border: '2px solid rgba(255,180,50,0.6)',
              boxShadow: '0 0 30px rgba(255,140,30,0.25) inset, 0 0 50px rgba(255,140,30,0.18), 0 8px 30px rgba(0,0,0,0.6)',
              minWidth: '320px',
            }}>
            {/* Corner ornaments */}
            {[
              ['top-0 left-0', '0deg'],
              ['top-0 right-0', '90deg'],
              ['bottom-0 right-0', '180deg'],
              ['bottom-0 left-0', '270deg'],
            ].map(([pos, rot]) => (
              <div key={pos} className={`absolute ${pos} w-3 h-3 m-1`} style={{
                transform: `rotate(${rot})`,
                borderTop: `1px solid ${ACCENT}`,
                borderLeft: `1px solid ${ACCENT}`,
                opacity: 0.7,
              }} />
            ))}
            <span className="text-[11px] tracking-[0.5em] font-mono uppercase block text-center font-black" style={{ color: '#ffcc66' }}>
              · vault holding ·
            </span>
            <p className="text-center font-black mt-2" style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 'clamp(28px, 4vmin, 44px)',
              background: 'linear-gradient(180deg, #fff5d8, #ffcc66 60%, #ff9933)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              filter: 'drop-shadow(0 0 12px rgba(255,180,50,0.5))',
            }}>
              {result.currentValue?.toLocaleString()}
            </p>
            <p className="text-[11px] mt-1.5 font-mono text-center font-bold tracking-[0.3em]" style={{ color: '#ff7733' }}>
              + {(result.floor || 1) * 50 + 100} SP on claim
            </p>
            {result?.onchain && (
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
                <OnChainReceipt receipt={result.onchain} compact />
              </div>
            )}
          </motion.div>

          {/* Lore quote */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.5, 0.3, 0.55, 0.4] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
            className="mt-6 text-xs italic font-serif text-center max-w-md"
            style={{ color: '#ffd9806e', letterSpacing: '0.05em' }}
          >
            "The deeper you walk, the louder the gold whispers. The sewer remembers either way."
          </motion.p>

          {/* Action buttons */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
            className="flex flex-col sm:flex-row gap-4 mt-8 w-full max-w-2xl px-4">
            <button onClick={cashOut}
              className="group flex-1 relative overflow-hidden px-8 py-5 font-black tracking-[0.25em] uppercase transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'linear-gradient(180deg, rgba(15,30,15,0.95), rgba(8,18,8,0.95))',
                border: '2px solid rgba(57,255,20,0.6)',
                color: '#aaff66',
                fontFamily: "'Cinzel', serif",
                fontSize: '15px',
                boxShadow: '0 0 24px rgba(57,255,20,0.18) inset, 0 0 32px rgba(57,255,20,0.15), 0 6px 18px rgba(0,0,0,0.5)',
                textShadow: '0 0 10px rgba(57,255,20,0.7)',
                borderRadius: '2px',
              }}
              data-testid="cashout-btn"
            >
              {/* Shimmer overlay on hover */}
              <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
                background: 'linear-gradient(90deg, transparent, rgba(170,255,100,0.18), transparent)',
                backgroundSize: '60% 100%',
                animation: 'cs-shimmer 1.5s linear infinite',
              }} />
              <span className="relative flex items-center justify-center gap-2.5">
                <DollarSign className="w-5 h-5" />
                Claim the Vault
              </span>
              <span className="block text-[9px] tracking-[0.3em] font-mono mt-1 opacity-60">walk away with your gold</span>
            </button>

            <button onClick={descend}
              className="group flex-1 relative overflow-hidden px-8 py-5 font-black tracking-[0.25em] uppercase transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: `linear-gradient(180deg, ${nextAccent}25, ${nextAccent}12)`,
                border: `2px solid ${nextAccent}`,
                color: '#fff5d8',
                fontFamily: "'Cinzel', serif",
                fontSize: '15px',
                boxShadow: `0 0 30px ${nextAccent}30 inset, 0 0 44px ${nextAccent}25, 0 6px 18px rgba(0,0,0,0.5)`,
                textShadow: `0 0 12px ${nextAccent}cc`,
                borderRadius: '2px',
                animation: 'descend-pulse 2s ease-in-out infinite',
              }}
              data-testid="descend-btn"
            >
              <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
                background: `linear-gradient(90deg, transparent, ${nextAccent}33, transparent)`,
                backgroundSize: '60% 100%',
                animation: 'cs-shimmer 1.5s linear infinite',
              }} />
              <span className="relative flex items-center justify-center gap-2.5">
                <ChevronDown className="w-5 h-5" />
                Descend Further
              </span>
              <span className="block text-[9px] tracking-[0.3em] font-mono mt-1 opacity-70">risk it for the deeper court</span>
            </button>
          </motion.div>

          {/* Next floor preview */}
          {nextFloorMeta && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
              className="mt-6 text-center px-5 py-2.5 rounded-sm"
              style={{
                background: 'rgba(0,0,0,0.55)',
                border: `1px solid ${nextAccent}55`,
                backdropFilter: 'blur(4px)',
              }}>
              <div className="flex items-center gap-3 justify-center">
                <span className="text-[9px] tracking-[0.4em] font-mono uppercase" style={{ color: '#ffcc66aa' }}>Next Floor</span>
                <span className="text-[10px] font-black" style={{ color: nextAccent, fontFamily: "'Cinzel', serif" }}>
                  {result.nextFloor} · {nextFloorMeta.name}
                </span>
                <span className="text-[10px] font-mono font-bold" style={{ color: nextAccent }}>{result.nextMultiplier}×</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // DEATH SCREEN
  // ════════════════════════════════════════
  if (gameState === 'failed') {
    const floorIdx = (result?.floor || 1) - 1;
    const floorMeta = FLOORS[floorIdx] || {};
    const floorIntro = FLOOR_INTROS[result?.floor] || {};
    const romanNumerals = ['I','II','III','IV','V','VI','VII','VIII','IX','X'];
    const roman = romanNumerals[floorIdx] || 'X';
    return (
      <div className="relative h-screen w-full overflow-hidden select-none"
        style={{ background: '#020002' }}
      >
        <style>{CINEMATIC_KEYFRAMES}{`
          @keyframes ledger-mask-judge {
            0%, 100% { filter: brightness(0.7) drop-shadow(0 0 22px rgba(180,10,20,0.55)) drop-shadow(0 0 60px rgba(120,0,10,0.3)); transform: translateY(0); }
            50%      { filter: brightness(0.95) drop-shadow(0 0 44px rgba(220,20,30,0.8)) drop-shadow(0 0 110px rgba(160,0,20,0.45)); transform: translateY(-3px); }
          }
          @keyframes ledger-eye-die {
            0%   { opacity: 1;    box-shadow: 0 0 18px #ff0020, 0 0 42px rgba(255,0,40,0.7); }
            45%  { opacity: 0.85; box-shadow: 0 0 12px #cc0018, 0 0 28px rgba(200,0,30,0.55); }
            75%  { opacity: 0.4;  box-shadow: 0 0 6px  #800010, 0 0 14px rgba(140,0,20,0.3); }
            100% { opacity: 0.12; box-shadow: 0 0 3px  rgba(80,0,10,0.4); }
          }
          @keyframes ledger-ring-rot { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes ledger-ring-rot-rev { 0% { transform: rotate(360deg); } 100% { transform: rotate(0deg); } }
          @keyframes ledger-ghost-drift {
            0%   { transform: translate(0, 0) scale(0.85); opacity: 0; }
            20%  { opacity: 0.32; }
            55%  { transform: translate(60px, -50px) scale(1); opacity: 0.4; }
            85%  { opacity: 0.18; }
            100% { transform: translate(120px, -100px) scale(0.8); opacity: 0; }
          }
          @keyframes ledger-rune-fall {
            0%   { transform: translateY(-10vh); opacity: 0; }
            15%  { opacity: 0.2; }
            88%  { opacity: 0.15; }
            100% { transform: translateY(110vh); opacity: 0; }
          }
          @keyframes ledger-roman-engrave {
            0%   { letter-spacing: 1.6em; opacity: 0; filter: blur(8px) brightness(0.2); }
            55%  { letter-spacing: 0.4em; opacity: 1; filter: blur(0) brightness(1); }
            100% { letter-spacing: 0.4em; opacity: 1; filter: blur(0) brightness(1); }
          }
          @keyframes ledger-title-emerge {
            0%   { opacity: 0; letter-spacing: 0.9em; filter: blur(6px); }
            100% { opacity: 1; letter-spacing: 0.5em; filter: blur(0); }
          }
          @keyframes ledger-title-pulse {
            0%, 100% { text-shadow: 0 0 12px rgba(160,0,20,0.4), 0 0 30px rgba(120,0,15,0.2), 0 2px 12px rgba(0,0,0,0.95); }
            50%      { text-shadow: 0 0 22px rgba(220,20,40,0.7), 0 0 50px rgba(180,0,20,0.35), 0 2px 12px rgba(0,0,0,0.95); }
          }
          @keyframes ledger-crack-grow {
            0%   { stroke-dashoffset: 200; opacity: 0; }
            30%  { opacity: 0.8; }
            100% { stroke-dashoffset: 0; opacity: 0.75; }
          }
          @keyframes ledger-stone-breathe {
            0%, 100% { box-shadow: inset 0 0 50px rgba(0,0,0,0.92), 0 0 30px rgba(120,0,15,0.22), 0 0 80px rgba(60,0,10,0.3); }
            50%      { box-shadow: inset 0 0 60px rgba(0,0,0,0.96), 0 0 48px rgba(180,10,25,0.32), 0 0 120px rgba(80,0,15,0.4); }
          }
          @keyframes ledger-toll-pulse {
            0%, 100% { box-shadow: 0 0 0 1px rgba(140,10,20,0.35), 0 0 18px rgba(140,10,20,0.15); }
            50%      { box-shadow: 0 0 0 1px rgba(200,20,30,0.6),  0 0 36px rgba(200,20,30,0.32); }
          }
          @keyframes ledger-chamber-pulse {
            0%, 100% { opacity: 0.55; }
            50%      { opacity: 0.85; }
          }
          @keyframes ledger-heartbeat {
            0%, 100% { opacity: 0; transform: scale(1); }
            8%       { opacity: 0.55; transform: scale(1.015); }
            16%      { opacity: 0.15; transform: scale(1); }
            24%      { opacity: 0.45; transform: scale(1.012); }
            32%      { opacity: 0; transform: scale(1); }
          }
          @keyframes ledger-mote-rise {
            0%   { transform: translateY(0) translateX(0); opacity: 0; }
            12%  { opacity: 0.6; }
            50%  { transform: translateY(-50vh) translateX(8px); opacity: 0.5; }
            88%  { opacity: 0.25; }
            100% { transform: translateY(-100vh) translateX(-6px); opacity: 0; }
          }
        `}</style>
        <ScanlineOverlay />
        <NoiseOverlay />
        {/* === MINIMAL VOID ATMOSPHERE === */}
        {/* Pure black base */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{ background: '#040003' }} />

        {/* Vintage spotlight cone — warm amber/sepia centered on content */}
        <motion.div className="absolute inset-0 z-0 pointer-events-none"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 4, ease: 'easeOut' }}
          style={{
            background: 'radial-gradient(ellipse 42% 60% at 50% 50%, rgba(180,90,40,0.32) 0%, rgba(120,40,15,0.18) 32%, rgba(60,15,5,0.08) 58%, transparent 82%)',
            mixBlendMode: 'screen',
          }} />

        {/* Inner brighter spotlight core */}
        <motion.div className="absolute inset-0 z-0 pointer-events-none"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 5, delay: 0.4, ease: 'easeOut' }}
          style={{
            background: 'radial-gradient(ellipse 22% 32% at 50% 50%, rgba(255,170,90,0.18) 0%, rgba(200,90,30,0.08) 50%, transparent 78%)',
            mixBlendMode: 'screen',
          }} />

        {/* Subtle blood undertone — barely there */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(80,8,15,0.22) 0%, rgba(20,2,5,0.1) 45%, transparent 75%)',
        }} />

        {/* Heavy crushing vignette — keeps the void at the edges */}
        <div className="absolute inset-0 z-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 28%, rgba(0,0,0,0.78) 70%, rgba(0,0,0,0.99) 100%)',
        }} />

        {/* Sepia film tone overlay — vintage warmth */}
        <div className="absolute inset-0 z-[1] pointer-events-none" style={{
          background: 'linear-gradient(180deg, rgba(80,40,10,0.04) 0%, transparent 40%, rgba(40,15,5,0.06) 100%)',
          mixBlendMode: 'overlay',
        }} />

        {/* Drifting dust motes inside the spotlight cone — vintage projector vibe */}
        {Array.from({ length: 12 }).map((_, i) => {
          const left = 38 + (i * 2.4) % 24; // clustered in spotlight zone
          const dur = 12 + (i * 1.4) % 8;
          const delay = -(i * 1.7) % 12;
          const size = 1 + (i % 3) * 0.6;
          return (
            <span key={`mote-${i}`} className="absolute pointer-events-none z-[2] rounded-full"
              style={{
                left: `${left}%`,
                top: '95%',
                width: `${size}px`, height: `${size}px`,
                background: 'rgba(255,180,90,0.7)',
                boxShadow: '0 0 4px rgba(255,160,70,0.5)',
                animation: `ledger-mote-rise ${dur}s linear ${delay}s infinite`,
              }} />
          );
        })}

        {/* === MAIN CONTENT — sparse vertical stack === */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.6 }}
          className="relative z-20 flex flex-col items-center justify-center h-full px-6">

          {/* Iron Warden Mask — bathed in the spotlight, sepia-tinted */}
          <motion.div className="relative"
            initial={{ scale: 0.55, y: -16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ width: '130px' }}>
            {/* Spotlight halo on the mask */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
              style={{
                width: '280px', height: '280px',
                background: 'radial-gradient(circle, rgba(200,110,50,0.28) 0%, rgba(120,40,15,0.14) 30%, transparent 70%)',
              }} />
            <img src={floor9IronMask} alt="" className="relative w-full select-none pointer-events-none"
              style={{
                filter: 'sepia(0.35) saturate(0.85) brightness(0.78) drop-shadow(0 0 20px rgba(180,90,40,0.5)) drop-shadow(0 0 50px rgba(120,40,15,0.3))',
                animation: 'ledger-mask-judge 7s ease-in-out infinite',
              }} />
            {/* Dying ember eye-slit — amber instead of red */}
            <div className="absolute top-[28%] left-1/2 -translate-x-1/2 rounded-full"
              style={{
                width: '18px', height: '3px',
                background: 'radial-gradient(ellipse, #ff8a30 0%, #c04010 60%, transparent 100%)',
                animation: 'ledger-eye-die 7s ease-out forwards 1.2s',
              }} />
          </motion.div>

          {/* Vintage Roman numeral — engraved in sepia, no tablet, just text */}
          <motion.div
            initial={{ opacity: 0, letterSpacing: '0.6em', filter: 'blur(8px)' }}
            animate={{ opacity: 1, letterSpacing: '0.08em', filter: 'blur(0px)' }}
            transition={{ delay: 1.4, duration: 1.6, ease: 'easeOut' }}
            className="mt-10 font-bold relative"
            style={{
              fontFamily: "'Cinzel Decorative','Cinzel',serif",
              fontSize: 'clamp(64px, 11vw, 100px)',
              color: 'rgba(220,140,70,0.92)',
              lineHeight: 1,
              textShadow: '0 0 28px rgba(220,120,50,0.45), 0 0 60px rgba(180,80,30,0.25), 0 4px 0 rgba(0,0,0,0.96), 0 1px 1px rgba(255,200,120,0.18)',
            }}>
            {roman}
          </motion.div>

          {/* Hairline divider beneath numeral */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 0.4, scaleX: 1 }}
            transition={{ delay: 2.0, duration: 1.4 }}
            className="mt-4 h-[1px] w-32 origin-center"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(200,110,50,0.7), transparent)',
            }} />

          {/* Single line — minimal toll record */}
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.4, duration: 1.4 }}
            className="mt-5 text-[10px] md:text-[11px] font-mono tracking-[0.5em] uppercase"
            style={{
              color: isAdmin ? 'rgba(180,100,50,0.55)' : 'rgba(220,130,60,0.7)',
              textShadow: '0 0 10px rgba(180,80,30,0.4)',
            }}>
            {isAdmin ? '— admin · no toll —' : `— ${(result?.feesLost || 0).toLocaleString()} $GFG burned —`}
          </motion.p>

          {/* CTA — bare text link */}
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.2, duration: 1.2 }}
            onClick={resetGame}
            className="mt-12 group relative px-2 py-1 font-serif text-[11px] tracking-[0.55em] uppercase font-bold"
            style={{
              color: 'rgba(200,110,50,0.85)',
              background: 'transparent',
              border: 'none',
              textShadow: '0 0 14px rgba(200,90,30,0.5), 0 1px 2px rgba(0,0,0,0.96)',
            }}
            data-testid="try-again-descent-btn"
          >
            <span className="relative z-10 transition-colors duration-500 group-hover:text-[rgba(255,160,80,1)]">
              descend again
            </span>
            <div className="mt-1 mx-auto h-[1px] w-0 group-hover:w-full transition-all duration-500"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(220,130,60,0.7), transparent)' }} />
          </motion.button>
        </motion.div>
      </div>
    );
  }


  // ════════════════════════════════════════
  // RESULT SCREEN (Win/Claimed)
  // ════════════════════════════════════════
  if (gameState === 'result' && result?.type !== 'error') {
    const isVictory = result?.type === 'victory';
    return (
      <div className="relative h-screen w-full overflow-hidden select-none"
        style={{ background: '#000' }}
      >
        <style>{CINEMATIC_KEYFRAMES}{`
          @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&display=swap');
          @keyframes result-particle-rise {
            0% { transform: translateY(0) scale(1); opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 0.5; }
            100% { transform: translateY(-110vh) scale(0.3); opacity: 0; }
          }
          @keyframes title-glow-pulse {
            0%, 100% { text-shadow: 0 0 40px rgba(57,255,20,0.4), 0 0 80px rgba(57,255,20,0.2), 0 0 120px rgba(57,255,20,0.08), 0 4px 30px rgba(0,0,0,0.9); }
            50% { text-shadow: 0 0 60px rgba(57,255,20,0.6), 0 0 100px rgba(57,255,20,0.35), 0 0 160px rgba(57,255,20,0.15), 0 4px 30px rgba(0,0,0,0.9); }
          }
          @keyframes title-glitch {
            0%, 92%, 100% { transform: translate(0,0); clip-path: inset(0 0 0 0); }
            93% { transform: translate(-3px, 1px); clip-path: inset(20% 0 60% 0); }
            95% { transform: translate(2px, -1px); clip-path: inset(50% 0 20% 0); }
            97% { transform: translate(-1px, 2px); clip-path: inset(10% 0 70% 0); }
          }
          @keyframes fees-counter {
            0% { opacity: 0; transform: scale(0.6) translateY(20px); filter: blur(8px); }
            60% { opacity: 1; transform: scale(1.08) translateY(-4px); filter: blur(0); }
            100% { opacity: 1; transform: scale(1) translateY(0); filter: blur(0); }
          }
          @keyframes shimmer-line {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
        `}</style>
        <ScanlineOverlay />
        <NoiseOverlay />

        {isVictory ? (
          <>
            {/* Fullscreen portal background — stays visible */}
            <motion.img src={victoryBgImg} alt=""
              initial={{ scale: 1.15, opacity: 0 }}
              animate={{ scale: 1, opacity: [0, 0.7, 0.7, 0.5] }}
              transition={{ duration: 5, times: [0, 0.2, 0.5, 1], ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 w-full h-full object-cover z-0" />

            {/* Vignette — heavier at edges for text pop */}
            <div className="absolute inset-0 z-5 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 50% 40%, transparent 15%, rgba(0,0,0,0.4) 45%, rgba(0,0,0,0.85) 100%)' }} />

            {/* Rising particles */}
            {Array.from({ length: 35 }).map((_, i) => {
              const size = 1.5 + Math.random() * 3.5;
              const colors = ['#39ff14', '#39ff14', '#b4ff8a', '#ffffff', '#f5d87a', '#00ffaa'];
              const color = colors[i % colors.length];
              return (
                <div key={`rp-${i}`} className="absolute z-8 pointer-events-none rounded-full"
                  style={{
                    left: `${5 + Math.random() * 90}%`,
                    bottom: `${-5 - Math.random() * 15}%`,
                    width: `${size}px`, height: `${size}px`,
                    background: color,
                    boxShadow: `0 0 ${size * 3}px ${color}, 0 0 ${size * 6}px ${color}40`,
                    animation: `result-particle-rise ${4 + Math.random() * 6}s linear infinite`,
                    animationDelay: `${Math.random() * 5}s`,
                    opacity: 0,
                  }} />
              );
            })}
          </>
        ) : (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.25 }}
              transition={{ duration: 1.5 }}
              className="absolute inset-0 z-0 pointer-events-none">
              <img src={abyssBg} alt="" className="w-full h-full object-cover" />
            </motion.div>
            <div className="absolute inset-0 z-5 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.7) 100%)' }} />
          </>
        )}

        {/* Content */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="relative z-20 flex flex-col items-center justify-center h-full px-6"
        >
          {isVictory ? (
            <>
              {/* ═══ TITLE — Cinzel Decorative, lore-green, glitch layers ═══ */}
              <div className="relative">
                <motion.h2
                  initial={{ opacity: 0 }} animate={{ opacity: 0.15 }} transition={{ delay: 1.2 }}
                  className="absolute inset-0 text-4xl md:text-6xl lg:text-7xl font-black text-center select-none"
                  aria-hidden
                  style={{ fontFamily: "'Cinzel Decorative', serif", color: '#39ff14', animation: 'title-glitch 6s steps(1) infinite', filter: 'blur(1.5px)' }}>
                  ABYSS CONQUERED
                </motion.h2>
                <motion.h2
                  initial={{ opacity: 0 }} animate={{ opacity: 0.1 }} transition={{ delay: 1.4 }}
                  className="absolute inset-0 text-4xl md:text-6xl lg:text-7xl font-black text-center select-none"
                  aria-hidden
                  style={{ fontFamily: "'Cinzel Decorative', serif", color: '#006b2b', animation: 'title-glitch 6s steps(1) infinite', animationDelay: '0.15s', filter: 'blur(0.5px)', transform: 'translateX(2px)' }}>
                  ABYSS CONQUERED
                </motion.h2>
                <motion.h2
                  initial={{ opacity: 0, letterSpacing: '0.6em', y: 25, scale: 0.95 }}
                  animate={{ opacity: 1, letterSpacing: '0.15em', y: 0, scale: 1 }}
                  transition={{ delay: 0.5, duration: 2.5, ease: [0.16, 1, 0.3, 1] }}
                  className="text-4xl md:text-6xl lg:text-7xl font-black text-center relative"
                  style={{ fontFamily: "'Cinzel Decorative', serif", color: '#b4ff8a', animation: 'title-glow-pulse 4s ease-in-out infinite' }}>
                  ABYSS CONQUERED
                </motion.h2>
              </div>

              {/* Decorative shimmer line */}
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '260px', opacity: 1 }}
                transition={{ delay: 2, duration: 1.5 }}
                className="h-[2px] mt-5"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(57,255,20,0.1) 20%, rgba(57,255,20,0.6) 50%, rgba(57,255,20,0.1) 80%, transparent 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer-line 3s linear infinite',
                }} />

              {/* Sub-line */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.85 }}
                transition={{ delay: 2.2, duration: 1 }}
                className="text-[10px] md:text-xs font-mono mt-3 tracking-[0.3em] uppercase"
                style={{ color: 'rgba(57,255,20,0.8)' }}>
                Floor {result?.floor} — {result?.multiplier}x multiplier
              </motion.p>
            </>
          ) : (
            <>
              <motion.div initial={{ scale: 0, y: 30 }} animate={{ scale: 1, y: 0 }}
                transition={{ type: 'spring', damping: 15 }}>
                <img src={gremlinSoulImg} alt="" style={{ width: '72px', height: '72px', filter: 'drop-shadow(0 0 24px rgba(57,255,20,0.5))' }} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="text-3xl md:text-4xl font-black mt-4 tracking-[0.2em]"
                style={{ color: '#39ff14', fontFamily: "'Courier New', monospace", textShadow: '0 0 30px rgba(57,255,20,0.3)' }}>
                REWARD CLAIMED
              </motion.h2>
              <motion.div initial={{ width: 0 }} animate={{ width: '120px' }}
                transition={{ delay: 1, duration: 0.8 }}
                className="h-[1px] mt-3"
                style={{ background: 'linear-gradient(90deg, transparent, #39ff14, transparent)' }} />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.85 }}
                transition={{ delay: 1.5 }}
                className="text-sm font-mono mt-3"
                style={{ color: 'rgba(255,255,255,0.75)' }}>
                Floor {result?.floor} — {result?.multiplier}x multiplier
              </motion.p>
            </>
          )}

          {/* ═══ REWARD — Lore-themed reveal ═══ */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: isVictory ? 3 : 2, duration: 1.5 }}
            className="mt-10 flex flex-col items-center"
          >
            {/* Thin separator */}
            <div className="w-16 h-[1px] mb-6" style={{ background: 'rgba(57,255,20,0.1)' }} />

            {/* Lore description */}
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 0.35, y: 0 }}
              transition={{ delay: isVictory ? 3.3 : 2.3, duration: 1 }}
              className="text-[10px] md:text-xs font-mono tracking-[0.25em] uppercase"
              style={{ color: 'rgba(57,255,20,0.85)' }}>
              {isVictory ? 'THE SEWERS YIELD THEIR TRIBUTE' : 'FEES STOLEN FROM THE DEPTHS'}
            </motion.p>

            {/* Fee amount — large, cinematic */}
            <motion.p
              initial={{ opacity: 0, scale: 0.9, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: isVictory ? 3.8 : 2.8, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-5xl md:text-6xl lg:text-7xl font-black mt-3"
              style={{
                fontFamily: "'Cinzel Decorative', serif",
                color: '#39ff14',
                textShadow: '0 0 40px rgba(57,255,20,0.3), 0 0 80px rgba(57,255,20,0.1)',
              }}>
              {result?.fees?.toLocaleString()}
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.2 }}
              transition={{ delay: isVictory ? 4.2 : 3.2, duration: 0.8 }}
              className="text-[9px] font-mono tracking-[0.35em] mt-2 uppercase"
              style={{ color: 'rgba(57,255,20,0.6)' }}>
              fees earned
            </motion.p>

            {/* Season Points earned */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: isVictory ? 4.4 : 3.4, duration: 0.8 }}
              className="text-sm font-mono tracking-[0.15em] mt-2"
              style={{ color: '#ff6b00' }}>
              {isVictory ? '+500 SP' : `+${(result?.floor || 1) * 50} SP`}
            </motion.p>

            {result?.onchain && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: isVictory ? 4.6 : 3.6, duration: 0.6 }}
                style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}
                data-testid="descent-onchain-receipt-wrap"
              >
                <OnChainReceipt receipt={result.onchain} />
              </motion.div>
            )}
          </motion.div>

          {/* Lore line */}
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 0.1 }}
            transition={{ delay: isVictory ? 4.5 : 3.5, duration: 1 }}
            className="text-[10px] font-mono mt-8 italic tracking-wide text-center"
            style={{ color: 'rgba(255,255,255,0.2)' }}>
            {isVictory
              ? '"The sewers bow to you. Your name is etched in the walls."'
              : '"Smart. You live to descend another day."'}
          </motion.p>

          {/* COLLECT FEES button */}
          <motion.button onClick={resetGame}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: isVictory ? 5 : 4, duration: 0.6 }}
            className="mt-6 px-12 py-3.5 font-mono text-sm tracking-[0.2em] uppercase relative overflow-hidden group"
            style={{
              border: '1px solid rgba(57,255,20,0.15)',
              color: '#39ff14',
              borderRadius: '2px',
              background: 'rgba(57,255,20,0.03)',
              backdropFilter: 'blur(4px)',
            }}
            data-testid="play-again-descent-btn"
          >
            <span className="relative z-10">COLLECT FEES</span>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(57,255,20,0.06), transparent)' }} />
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Error result
  if (gameState === 'result' && result?.type === 'error') {
    return (
      <div className="relative h-screen w-full overflow-hidden select-none flex items-center justify-center"
        style={{ background: '#050101' }}
      >
        <div className="text-center px-6">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: ACCENT }} />
          <p className="text-sm font-mono" style={{ color: ACCENT }}>{result.message}</p>
          <button onClick={resetGame} className="mt-6 px-6 py-2 font-mono text-xs text-white/30 border border-white/10 rounded-sm">BACK</button>
        </div>
      </div>
    );
  }

  return null;
}
